var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);

// js/common/actions/confirm-button.js
var ConfirmButton = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      if (!window.confirm(this.getAttribute("data-message") ?? "Once confirmed, this action cannot be undone.")) {
        event.preventDefault();
      }
    });
  }
};
if (!window.customElements.get("confirm-button")) {
  window.customElements.define("confirm-button", ConfirmButton);
}

// js/common/actions/copy-button.js
var _CopyButton_instances, copyToClipboard_fn;
var CopyButton = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _CopyButton_instances);
    this.addEventListener("click", __privateMethod(this, _CopyButton_instances, copyToClipboard_fn));
  }
  get buttonElement() {
    return this.querySelector("button");
  }
};
_CopyButton_instances = new WeakSet();
copyToClipboard_fn = async function() {
  if (!navigator.clipboard) {
    return;
  }
  await navigator.clipboard.writeText(this.getAttribute("data-text") ?? "");
  if (this.hasAttribute("data-success-message")) {
    const originalMessage = this.buttonElement.textContent;
    this.buttonElement.textContent = this.getAttribute("data-success-message");
    setTimeout(() => {
      this.buttonElement.textContent = originalMessage;
    }, 1500);
  }
};
if (!window.customElements.get("copy-button")) {
  window.customElements.define("copy-button", CopyButton);
}

// js/common/actions/share-button.js
var _ShareButton_instances, showSystemShare_fn;
var ShareButton = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _ShareButton_instances);
    if (navigator.share) {
      this.querySelector(".share-buttons--native").hidden = false;
      this.addEventListener("click", __privateMethod(this, _ShareButton_instances, showSystemShare_fn));
    }
  }
};
_ShareButton_instances = new WeakSet();
showSystemShare_fn = async function() {
  try {
    await navigator.share({
      title: this.hasAttribute("share-title") ? this.getAttribute("share-title") : document.title,
      url: this.hasAttribute("share-url") ? this.getAttribute("share-url") : window.location.href
    });
  } catch (error) {
  }
};
if (!window.customElements.get("share-button")) {
  window.customElements.define("share-button", ShareButton);
}

// js/common/animation/marquee-text.js
import { inView, animate } from "vendor";
var _currentAnimation, _MarqueeText_instances, direction_get, scroller_get, initialize_fn;
var MarqueeText = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _MarqueeText_instances);
    __privateAdd(this, _currentAnimation);
    inView(this, __privateMethod(this, _MarqueeText_instances, initialize_fn).bind(this), { margin: "400px" });
    if (this.hasAttribute("pause-on-hover")) {
      this.addEventListener("pointerenter", () => __privateGet(this, _currentAnimation)?.pause());
      this.addEventListener("pointerleave", () => __privateGet(this, _currentAnimation)?.play());
    }
  }
};
_currentAnimation = new WeakMap();
_MarqueeText_instances = new WeakSet();
direction_get = function() {
  return this.getAttribute("direction") === "right" ? 1 : -1;
};
scroller_get = function() {
  return this.shadowRoot.querySelector('[part="scroller"]');
};
initialize_fn = function() {
  if (this.children[0]?.childElementCount === 0) {
    return;
  }
  this.attachShadow({ mode: "open" }).appendChild(
    document.createRange().createContextualFragment(`
      <slot part="scroller"></slot>
    `)
  );
  const fragment = document.createDocumentFragment();
  const duplicateCount = Math.ceil(this.clientWidth / this.firstElementChild.clientWidth);
  for (let i = 1; i <= duplicateCount; ++i) {
    for (let y = 0; y < 2; ++y) {
      const node = this.firstElementChild.cloneNode(true);
      const value = 100 * i * (y % 2 === 0 ? -1 : 1);
      node.setAttribute("aria-hidden", "true");
      node.style.cssText = `position: absolute; inset-inline-start: calc(${value}%);`;
      fragment.appendChild(node);
    }
  }
  this.append(fragment);
  __privateSet(this, _currentAnimation, animate(
    __privateGet(this, _MarqueeText_instances, scroller_get),
    { transform: ["translateX(0)", `translateX(calc(var(--transform-logical-flip) * ${__privateGet(this, _MarqueeText_instances, direction_get) * 100}%))`] },
    {
      duration: 1 / parseFloat(this.getAttribute("speed")) * (__privateGet(this, _MarqueeText_instances, scroller_get).clientWidth / 300),
      easing: "linear",
      repeat: Infinity
    }
  ));
};
if (!window.customElements.get("marquee-text")) {
  window.customElements.define("marquee-text", MarqueeText);
}

// js/common/behavior/gesture-area.js
var _domElement, _thresholdDistance, _thresholdTime, _signal, _firstClientX, _tracking, _start, _GestureArea_instances, touchStart_fn, preventTouch_fn, gestureStart_fn, gestureMove_fn, gestureEnd_fn;
var GestureArea = class {
  constructor(domElement, { thresholdDistance = 80, thresholdTime = 500, signal = null } = {}) {
    __privateAdd(this, _GestureArea_instances);
    __privateAdd(this, _domElement);
    __privateAdd(this, _thresholdDistance);
    __privateAdd(this, _thresholdTime);
    __privateAdd(this, _signal);
    __privateAdd(this, _firstClientX);
    __privateAdd(this, _tracking, false);
    __privateAdd(this, _start, {});
    __privateSet(this, _domElement, domElement);
    __privateSet(this, _thresholdDistance, thresholdDistance);
    __privateSet(this, _thresholdTime, thresholdTime);
    __privateSet(this, _signal, signal ?? new AbortController().signal);
    __privateGet(this, _domElement).addEventListener("touchstart", __privateMethod(this, _GestureArea_instances, touchStart_fn).bind(this), { passive: true, signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("touchmove", __privateMethod(this, _GestureArea_instances, preventTouch_fn).bind(this), { passive: false, signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointerdown", __privateMethod(this, _GestureArea_instances, gestureStart_fn).bind(this), { signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointermove", __privateMethod(this, _GestureArea_instances, gestureMove_fn).bind(this), { passive: false, signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointerup", __privateMethod(this, _GestureArea_instances, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointerleave", __privateMethod(this, _GestureArea_instances, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointercancel", __privateMethod(this, _GestureArea_instances, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
  }
};
_domElement = new WeakMap();
_thresholdDistance = new WeakMap();
_thresholdTime = new WeakMap();
_signal = new WeakMap();
_firstClientX = new WeakMap();
_tracking = new WeakMap();
_start = new WeakMap();
_GestureArea_instances = new WeakSet();
touchStart_fn = function(event) {
  __privateSet(this, _firstClientX, event.touches[0].clientX);
};
preventTouch_fn = function(event) {
  if (Math.abs(event.touches[0].clientX - __privateGet(this, _firstClientX)) > 10) {
    event.preventDefault();
  }
};
gestureStart_fn = function(event) {
  __privateSet(this, _tracking, true);
  __privateSet(this, _start, {
    time: (/* @__PURE__ */ new Date()).getTime(),
    x: event.clientX,
    y: event.clientY
  });
};
gestureMove_fn = function(event) {
  if (__privateGet(this, _tracking)) {
    event.preventDefault();
  }
};
gestureEnd_fn = function(event) {
  if (!__privateGet(this, _tracking)) {
    return;
  }
  __privateSet(this, _tracking, false);
  const now = (/* @__PURE__ */ new Date()).getTime(), deltaTime = now - __privateGet(this, _start).time, deltaX = event.clientX - __privateGet(this, _start).x, deltaY = event.clientY - __privateGet(this, _start).y;
  if (deltaTime > __privateGet(this, _thresholdTime)) {
    return;
  }
  let matchedEvent;
  if (deltaX === 0 && deltaY === 0) {
    matchedEvent = "tap";
  } else if (deltaX > __privateGet(this, _thresholdDistance) && Math.abs(deltaY) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swiperight";
  } else if (-deltaX > __privateGet(this, _thresholdDistance) && Math.abs(deltaY) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swipeleft";
  } else if (deltaY > __privateGet(this, _thresholdDistance) && Math.abs(deltaX) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swipedown";
  } else if (-deltaY > __privateGet(this, _thresholdDistance) && Math.abs(deltaX) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swipeup";
  }
  if (matchedEvent) {
    __privateGet(this, _domElement).dispatchEvent(new CustomEvent(matchedEvent, { bubbles: true, composed: true, detail: { originalEvent: event } }));
  }
};

// js/common/utilities/country-selector.js
var _onCountryChangedListener, _CountrySelector_instances, onCountryChanged_fn;
var CountrySelector = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CountrySelector_instances);
    __privateAdd(this, _onCountryChangedListener, __privateMethod(this, _CountrySelector_instances, onCountryChanged_fn).bind(this));
  }
  connectedCallback() {
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement.addEventListener("change", __privateGet(this, _onCountryChangedListener));
    if (this.hasAttribute("country") && this.getAttribute("country") !== "") {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute("country"))
      );
    }
    this.countryElement.dispatchEvent(new Event("change"));
  }
  disconnectedCallback() {
    this.countryElement.removeEventListener("change", __privateGet(this, _onCountryChangedListener));
  }
};
_onCountryChangedListener = new WeakMap();
_CountrySelector_instances = new WeakSet();
onCountryChanged_fn = function() {
  const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute("data-provinces"));
  this.provinceElement.parentElement.hidden = provinces.length === 0;
  if (provinces.length === 0) {
    return;
  }
  this.provinceElement.innerHTML = "";
  provinces.forEach((data) => {
    const selected = data[1] === this.getAttribute("province") || data[0] === this.getAttribute("province");
    this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
  });
};
if (!window.customElements.get("country-selector")) {
  window.customElements.define("country-selector", CountrySelector);
}

// js/common/utilities/cached-fetch.js
var cachedMap = /* @__PURE__ */ new Map();
function cachedFetch(url, options) {
  const cacheKey = url;
  if (cachedMap.has(cacheKey)) {
    return Promise.resolve(new Response(new Blob([cachedMap.get(cacheKey)])));
  }
  return fetch(url, options).then((response) => {
    if (response.status === 200) {
      const contentType = response.headers.get("Content-Type");
      if (contentType && (contentType.match(/application\/json/i) || contentType.match(/text\//i))) {
        response.clone().text().then((content) => {
          cachedMap.set(cacheKey, content);
        });
      }
    }
    return response;
  });
}

// js/common/utilities/extract-section-id.js
function extractSectionId(element) {
  if (!element) return "";
  element = element.classList && element.classList.contains("shopify-section") ? element : element.closest && element.closest(".shopify-section");
  if (!element || !element.id) return "";
  return element.id.replace("shopify-section-", "");
}

// js/common/utilities/dom.js
function deepQuerySelector(root, selector) {
  let element = root.querySelector(selector);
  if (element) {
    return element;
  }
  for (const template of root.querySelectorAll("template")) {
    element = deepQuerySelector(template.content, selector);
    if (element) {
      return element;
    }
  }
  return null;
}
function throttle(callback) {
  let requestId = null, lastArgs;
  const later = (context) => () => {
    requestId = null;
    callback.apply(context, lastArgs);
  };
  const throttled = (...args) => {
    lastArgs = args;
    if (requestId === null) {
      requestId = requestAnimationFrame(later(this));
    }
  };
  throttled.cancel = () => {
    cancelAnimationFrame(requestId);
    requestId = null;
  };
  return throttled;
}
function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}
function waitForEvent(element, eventName) {
  return new Promise((resolve) => {
    const done = (event) => {
      if (event.target === element) {
        element.removeEventListener(eventName, done);
        resolve(event);
      }
    };
    element.addEventListener(eventName, done);
  });
}

// js/common/utilities/media.js
function videoLoaded(videoOrArray) {
  if (!videoOrArray) {
    return Promise.resolve();
  }
  videoOrArray = videoOrArray instanceof Element ? [videoOrArray] : Array.from(videoOrArray);
  return Promise.all(
    videoOrArray.map((video) => {
      return new Promise((resolve) => {
        if (video.tagName === "VIDEO" && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA || !video.offsetParent || video.parentNode.hasAttribute("suspended")) {
          resolve();
        } else {
          video.oncanplay = () => resolve();
        }
      });
    })
  );
}
function imageLoaded(imageOrArray) {
  if (!imageOrArray) {
    return Promise.resolve();
  }
  imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
  return Promise.all(
    imageOrArray.map((image) => {
      return new Promise((resolve) => {
        if (image.tagName === "IMG" && image.complete || !image.offsetParent) {
          resolve();
        } else {
          image.onload = () => resolve();
        }
      });
    })
  );
}
function generateSrcset(imageObjectOrString, widths = []) {
  let imageUrl, maxWidth;
  if (typeof imageObjectOrString === "string") {
    imageUrl = new URL(imageObjectOrString.startsWith("//") ? `https:${imageObjectOrString}` : imageObjectOrString);
    maxWidth = parseInt(imageUrl.searchParams.get("width"));
  } else {
    imageUrl = new URL(imageObjectOrString["src"].startsWith("//") ? `https:${imageObjectOrString["src"]}` : imageObjectOrString["src"]);
    maxWidth = imageObjectOrString["width"];
  }
  return widths.filter((width) => width <= maxWidth).map((width) => {
    imageUrl.searchParams.set("width", width.toString());
    return `${imageUrl.href} ${width}w`;
  }).join(", ");
}
function createMediaImg(media, widths = [], attributes = {}) {
  const image = new Image(media["preview_image"]["width"], media["preview_image"]["height"]), src = media["preview_image"]["src"], featuredMediaUrl = new URL(src.startsWith("//") ? `https:${src}` : src);
  for (const attributeKey in attributes) {
    image.setAttribute(attributeKey, attributes[attributeKey]);
  }
  image.alt = media["alt"] || "";
  image.src = featuredMediaUrl.href;
  image.srcset = generateSrcset(media["preview_image"], widths);
  return image;
}

// js/common/utilities/media-query.js
function matchesMediaQuery(breakpointName) {
  if (!window.themeVariables.mediaQueries.hasOwnProperty(breakpointName)) {
    throw `Media query ${breakpointName} does not exist`;
  }
  return window.matchMedia(window.themeVariables.mediaQueries[breakpointName]).matches;
}
function mediaQueryListener(breakpointName, func) {
  if (!window.themeVariables.mediaQueries.hasOwnProperty(breakpointName)) {
    throw `Media query ${breakpointName} does not exist`;
  }
  return window.matchMedia(window.themeVariables.mediaQueries[breakpointName]).addEventListener("change", func);
}

// js/common/utilities/player.js
var _callback, _duration, _remainingTime, _startTime, _timer, _state, _onVisibilityChangeListener, _mustResumeOnVisibility, _Player_instances, onVisibilityChange_fn;
var Player = class extends EventTarget {
  constructor(durationInSec, stopOnVisibility = true) {
    super();
    __privateAdd(this, _Player_instances);
    __privateAdd(this, _callback);
    __privateAdd(this, _duration);
    __privateAdd(this, _remainingTime);
    __privateAdd(this, _startTime);
    __privateAdd(this, _timer);
    __privateAdd(this, _state, "paused");
    __privateAdd(this, _onVisibilityChangeListener, __privateMethod(this, _Player_instances, onVisibilityChange_fn).bind(this));
    __privateAdd(this, _mustResumeOnVisibility, true);
    __privateSet(this, _callback, () => this.dispatchEvent(new CustomEvent("player:end")));
    __privateSet(this, _duration, __privateSet(this, _remainingTime, durationInSec * 1e3));
    if (stopOnVisibility) {
      document.addEventListener("visibilitychange", __privateGet(this, _onVisibilityChangeListener));
    }
  }
  pause() {
    if (__privateGet(this, _state) !== "started") {
      return;
    }
    clearTimeout(__privateGet(this, _timer));
    __privateSet(this, _state, "paused");
    __privateSet(this, _remainingTime, __privateGet(this, _remainingTime) - ((/* @__PURE__ */ new Date()).getTime() - __privateGet(this, _startTime)));
    this.dispatchEvent(new CustomEvent("player:pause"));
  }
  resume(restartTimer = false) {
    if (__privateGet(this, _state) !== "stopped") {
      if (restartTimer) {
        this.start();
      } else {
        clearTimeout(__privateGet(this, _timer));
        __privateSet(this, _startTime, (/* @__PURE__ */ new Date()).getTime());
        __privateSet(this, _state, "started");
        __privateSet(this, _timer, setTimeout(__privateGet(this, _callback), __privateGet(this, _remainingTime)));
        this.dispatchEvent(new CustomEvent("player:resume"));
      }
    }
  }
  start() {
    clearTimeout(__privateGet(this, _timer));
    __privateSet(this, _startTime, (/* @__PURE__ */ new Date()).getTime());
    __privateSet(this, _state, "started");
    __privateSet(this, _remainingTime, __privateGet(this, _duration));
    __privateSet(this, _timer, setTimeout(__privateGet(this, _callback), __privateGet(this, _remainingTime)));
    this.dispatchEvent(new CustomEvent("player:start"));
  }
  stop() {
    clearTimeout(__privateGet(this, _timer));
    __privateSet(this, _state, "stopped");
    this.dispatchEvent(new CustomEvent("player:stop"));
  }
};
_callback = new WeakMap();
_duration = new WeakMap();
_remainingTime = new WeakMap();
_startTime = new WeakMap();
_timer = new WeakMap();
_state = new WeakMap();
_onVisibilityChangeListener = new WeakMap();
_mustResumeOnVisibility = new WeakMap();
_Player_instances = new WeakSet();
onVisibilityChange_fn = function() {
  if (document.visibilityState === "hidden") {
    __privateSet(this, _mustResumeOnVisibility, __privateGet(this, _state) === "started");
    this.pause();
    this.dispatchEvent(new CustomEvent("player:visibility-pause"));
  } else if (document.visibilityState === "visible" && __privateGet(this, _mustResumeOnVisibility)) {
    this.resume();
    this.dispatchEvent(new CustomEvent("player:visibility-resume"));
  }
};

// js/common/utilities/qr-code.js
var QrCode = class extends HTMLElement {
  connectedCallback() {
    new window.QRCode(this, {
      text: this.getAttribute("identifier"),
      width: this.hasAttribute("width") ? parseInt(this.getAttribute("width")) : 200,
      height: this.hasAttribute("height") ? parseInt(this.getAttribute("height")) : 200
    });
  }
};
if (!window.customElements.get("qr-code")) {
  window.customElements.define("qr-code", QrCode);
}

// js/common/utilities/accessibility.js
function announceText(holderId, message) {
  const holder = document.getElementById(holderId);
  holder.textContent = "";
  setTimeout(() => {
    holder.textContent = message;
  }, 100);
}
function announceStatus(message) {
  announceText("status-announcement", message);
}
function announceError(message) {
  announceText("error-announcement", message);
}

// js/common/behavior/height-observer.js
var _resizeObserver, _HeightObserver_instances, updateCustomProperties_fn;
var HeightObserver = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _HeightObserver_instances);
    __privateAdd(this, _resizeObserver, new ResizeObserver(throttle(__privateMethod(this, _HeightObserver_instances, updateCustomProperties_fn).bind(this))));
  }
  connectedCallback() {
    __privateGet(this, _resizeObserver).observe(this);
    if (!window.ResizeObserver) {
      document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${this.clientHeight.toFixed(2)}px`);
    }
  }
  disconnectedCallback() {
    __privateGet(this, _resizeObserver).unobserve(this);
  }
};
_resizeObserver = new WeakMap();
_HeightObserver_instances = new WeakSet();
updateCustomProperties_fn = function(entries) {
  entries.forEach((entry) => {
    if (entry.target === this) {
      const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;
      document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${height.toFixed(2)}px`);
    }
  });
};
if (!window.customElements.get("height-observer")) {
  window.customElements.define("height-observer", HeightObserver);
}

// js/common/behavior/loading-bar.js
import { animate as animate2 } from "vendor";
var _LoadingBar_instances, onLoadingStart_fn, onLoadingEnd_fn;
var LoadingBar = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _LoadingBar_instances);
    document.addEventListener("theme:loading:start", __privateMethod(this, _LoadingBar_instances, onLoadingStart_fn).bind(this));
    document.addEventListener("theme:loading:end", __privateMethod(this, _LoadingBar_instances, onLoadingEnd_fn).bind(this));
  }
};
_LoadingBar_instances = new WeakSet();
onLoadingStart_fn = function() {
  animate2(this, { opacity: [0, 1], transform: ["scaleX(0)", "scaleX(0.4)"] }, { duration: 0.25 });
};
onLoadingEnd_fn = async function() {
  await animate2(this, { transform: [null, "scaleX(1)"] }, { duration: 0.25 }).finished;
  animate2(this, { opacity: 0 }, { duration: 0.25 });
};
if (!window.customElements.get("loading-bar")) {
  window.customElements.define("loading-bar", LoadingBar);
}

// js/common/behavior/safe-sticky.js
import { inView as inView2 } from "vendor";
var _resizeObserver2, _checkPositionListener, _initialTop, _lastKnownY, _currentTop, _position, _SafeSticky_instances, recalculateStyles_fn, checkPosition_fn;
var SafeSticky = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _SafeSticky_instances);
    __privateAdd(this, _resizeObserver2, new ResizeObserver(__privateMethod(this, _SafeSticky_instances, recalculateStyles_fn).bind(this)));
    __privateAdd(this, _checkPositionListener, throttle(__privateMethod(this, _SafeSticky_instances, checkPosition_fn).bind(this)));
    __privateAdd(this, _initialTop, 0);
    __privateAdd(this, _lastKnownY, 0);
    /* we could initialize it to window.scrollY but this avoids a costly reflow */
    __privateAdd(this, _currentTop, 0);
    __privateAdd(this, _position, "relative");
    if (window.Shopify.designMode) {
      document.addEventListener("shopify:section:load", __privateMethod(this, _SafeSticky_instances, recalculateStyles_fn).bind(this));
    }
  }
  connectedCallback() {
    inView2(
      this,
      () => {
        window.addEventListener("scroll", __privateGet(this, _checkPositionListener), { passive: true });
        __privateGet(this, _resizeObserver2).observe(this);
        return () => {
          window.removeEventListener("scroll", __privateGet(this, _checkPositionListener));
          __privateGet(this, _resizeObserver2).unobserve(this);
        };
      },
      { margin: "500px" }
    );
  }
  disconnectedCallback() {
    window.removeEventListener("scroll", __privateGet(this, _checkPositionListener));
    __privateGet(this, _resizeObserver2).unobserve(this);
  }
};
_resizeObserver2 = new WeakMap();
_checkPositionListener = new WeakMap();
_initialTop = new WeakMap();
_lastKnownY = new WeakMap();
_currentTop = new WeakMap();
_position = new WeakMap();
_SafeSticky_instances = new WeakSet();
recalculateStyles_fn = function() {
  this.style.removeProperty("top");
  const computedStyles = getComputedStyle(this);
  __privateSet(this, _initialTop, parseInt(computedStyles.top));
  __privateSet(this, _position, computedStyles.position);
  __privateMethod(this, _SafeSticky_instances, checkPosition_fn).call(this);
};
checkPosition_fn = function() {
  if (__privateGet(this, _position) !== "sticky") {
    return this.style.removeProperty("top");
  }
  let bounds = this.getBoundingClientRect(), maxTop = bounds.top + window.scrollY - this.offsetTop + __privateGet(this, _initialTop), minTop = this.clientHeight - window.innerHeight + 20;
  if (window.scrollY < __privateGet(this, _lastKnownY)) {
    __privateSet(this, _currentTop, __privateGet(this, _currentTop) - (window.scrollY - __privateGet(this, _lastKnownY)));
  } else {
    __privateSet(this, _currentTop, __privateGet(this, _currentTop) + (__privateGet(this, _lastKnownY) - window.scrollY));
  }
  __privateSet(this, _currentTop, Math.min(Math.max(__privateGet(this, _currentTop), -minTop), maxTop, __privateGet(this, _initialTop)));
  __privateSet(this, _lastKnownY, window.scrollY);
  this.style.top = `${Math.round(__privateGet(this, _currentTop))}px`;
};
if (!window.customElements.get("safe-sticky")) {
  window.customElements.define("safe-sticky", SafeSticky);
}

// js/common/carousel/carousel-navigation.js
var _abortController, _allItems, _CarouselNavigation_instances, onCarouselFilter_fn;
var CarouselNavigation = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CarouselNavigation_instances);
    __privateAdd(this, _abortController);
    __privateAdd(this, _allItems, []);
  }
  connectedCallback() {
    if (!this.carousel) {
      throw "Carousel navigation component requires an aria-controls attribute that refers to the controlled carousel.";
    }
    __privateSet(this, _abortController, new AbortController());
    __privateSet(this, _allItems, Array.from(this.querySelectorAll("button")));
    __privateGet(this, _allItems).forEach((button) => button.addEventListener("click", () => this.onButtonClicked(this.items.indexOf(button)), { signal: __privateGet(this, _abortController).signal }));
    this.carousel.addEventListener("carousel:change", (event) => this.onNavigationChange(event.detail.index), { signal: __privateGet(this, _abortController).signal });
    this.carousel.addEventListener("carousel:filter", __privateMethod(this, _CarouselNavigation_instances, onCarouselFilter_fn).bind(this), { signal: __privateGet(this, _abortController).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController).abort();
  }
  get items() {
    return __privateGet(this, _allItems).filter((item) => !item.hasAttribute("hidden"));
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
  get selectedIndex() {
    return this.items.findIndex((button) => button.getAttribute("aria-current") === "true");
  }
  onButtonClicked(newIndex) {
    this.carousel.select(newIndex);
    this.onNavigationChange(newIndex);
  }
  onNavigationChange(newIndex) {
    this.items.forEach((button, index) => button.setAttribute("aria-current", newIndex === index ? "true" : "false"));
    const selectedItem = this.items[newIndex];
    if (!selectedItem || !this.hasAttribute("align-selected")) {
      return;
    }
    if (this.scrollWidth !== this.clientWidth || this.scrollHeight !== this.clientHeight) {
      this.scrollTo({
        left: selectedItem.offsetLeft - this.clientWidth / 2 + selectedItem.clientWidth / 2,
        top: selectedItem.offsetTop - this.clientHeight / 2 + selectedItem.clientHeight / 2,
        behavior: matchesMediaQuery("motion-safe") ? "smooth" : "auto"
      });
    }
  }
};
_abortController = new WeakMap();
_allItems = new WeakMap();
_CarouselNavigation_instances = new WeakSet();
onCarouselFilter_fn = function(event) {
  __privateGet(this, _allItems).forEach((item, index) => {
    item.toggleAttribute("hidden", (event.detail.filteredIndexes || []).includes(index));
  });
};
var CarouselPrevButton = class extends HTMLElement {
  #abortController;
  connectedCallback() {
    if (!this.carousel) {
      throw "Carousel prev button component requires an aria-controls attribute that refers to the controlled carousel.";
    }
    this.#abortController = new AbortController();
    this.addEventListener("click", () => this.carousel.previous(), { signal: this.#abortController.signal });
    this.carousel.addEventListener("scroll:edge-nearing", (event) => this.firstElementChild.disabled = event.detail.position === "start", { signal: this.#abortController.signal });
    this.carousel.addEventListener(
      "scroll:edge-leaving",
      (event) => this.firstElementChild.disabled = event.detail.position === "start" ? false : this.firstElementChild.disabled,
      { signal: this.#abortController.signal }
    );
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
};
var CarouselNextButton = class extends HTMLElement {
  #abortController;
  connectedCallback() {
    if (!this.carousel) {
      throw "Carousel next button component requires an aria-controls attribute that refers to the controlled carousel.";
    }
    this.#abortController = new AbortController();
    this.addEventListener("click", () => this.carousel.next(), { signal: this.#abortController.signal });
    this.carousel.addEventListener("scroll:edge-nearing", (event) => this.firstElementChild.disabled = event.detail.position === "end", { signal: this.#abortController.signal });
    this.carousel.addEventListener("scroll:edge-leaving", (event) => this.firstElementChild.disabled = event.detail.position === "end" ? false : this.firstElementChild.disabled, {
      signal: this.#abortController.signal
    });
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
};
if (!window.customElements.get("carousel-prev-button")) {
  window.customElements.define("carousel-prev-button", CarouselPrevButton);
}
if (!window.customElements.get("carousel-next-button")) {
  window.customElements.define("carousel-next-button", CarouselNextButton);
}
if (!window.customElements.get("carousel-navigation")) {
  window.customElements.define("carousel-navigation", CarouselNavigation);
}

// js/common/carousel/effect-carousel.js
import { animate as animate3, timeline, inView as inView3 } from "vendor";
var _listenersAbortController, _gestureArea, _player, _targetIndex, _preventInitialTransition, _EffectCarousel_instances, setupListeners_fn, onKeyboardNavigation_fn, preloadImages_fn;
var EffectCarousel = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _EffectCarousel_instances);
    __privateAdd(this, _listenersAbortController);
    __privateAdd(this, _gestureArea);
    __privateAdd(this, _player);
    __privateAdd(this, _targetIndex, 0);
    __privateAdd(this, _preventInitialTransition, false);
    __privateMethod(this, _EffectCarousel_instances, setupListeners_fn).call(this);
    inView3(this, () => this.onBecameVisible());
    this.addEventListener("carousel:settle", (event) => {
      this.allCells.forEach((cell) => cell.classList.toggle("is-selected", cell === event.detail.cell));
    });
  }
  connectedCallback() {
    __privateSet(this, _targetIndex, Math.max(
      0,
      this.cells.findIndex((item) => item.classList.contains("is-selected"))
    ));
    inView3(this, () => __privateMethod(this, _EffectCarousel_instances, preloadImages_fn).call(this));
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (PROPERTIES)
   * -------------------------------------------------------------------------------------------------------------------
   */
  get allowSwipe() {
    return this.hasAttribute("allow-swipe");
  }
  get cellSelector() {
    return this.hasAttribute("cell-selector") ? this.getAttribute("cell-selector") : null;
  }
  get allCells() {
    return this.cellSelector ? Array.from(this.querySelectorAll(this.cellSelector)) : Array.from(this.children);
  }
  get cells() {
    return this.allCells.filter((cell) => !cell.hasAttribute("hidden"));
  }
  get selectedCell() {
    return this.cells[this.selectedIndex];
  }
  get selectedIndex() {
    return __privateGet(this, _targetIndex);
  }
  get player() {
    return __privateGet(this, _player);
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (METHODS)
   * -------------------------------------------------------------------------------------------------------------------
   */
  previous({ instant = false } = {}) {
    return this.select((this.selectedIndex - 1 + this.cells.length) % this.cells.length, { instant, direction: "previous" });
  }
  next({ instant = false } = {}) {
    return this.select((this.selectedIndex + 1 + this.cells.length) % this.cells.length, { instant, direction: "next" });
  }
  async select(index, { instant = false, direction = null } = {}) {
    if (!(index in this.cells)) {
      return Promise.resolve();
    }
    this.dispatchEvent(new CustomEvent("carousel:select", { detail: { index, cell: this.cells[index] } }));
    if (index === this.selectedIndex) {
      return Promise.resolve();
    }
    __privateGet(this, _player)?.pause();
    const [fromSlide, toSlide] = [this.selectedCell, this.cells[index]];
    direction ??= index > this.selectedIndex ? "next" : "previous";
    __privateSet(this, _targetIndex, index);
    this.dispatchEvent(new CustomEvent("carousel:change", { detail: { index, cell: this.cells[index] } }));
    const animationControls = this.createOnChangeAnimationControls(fromSlide, toSlide, { direction });
    if ("leaveControls" in animationControls && "enterControls" in animationControls) {
      const leaveAnimationControls = animationControls.leaveControls();
      if (instant) {
        leaveAnimationControls.finish();
      }
      await leaveAnimationControls.finished;
      __privateGet(this, _player)?.resume(true);
      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");
      const enterAnimationControls = animationControls.enterControls();
      if (instant) {
        enterAnimationControls.finish();
      }
      await enterAnimationControls.finished;
    } else {
      if (instant) {
        animationControls.finish();
      }
      __privateGet(this, _player)?.resume(true);
      toSlide.classList.add("is-selected");
      await animationControls.finished;
      fromSlide.classList.remove("is-selected");
    }
    this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index, cell: this.cells[index] } }));
  }
  /**
   * Filter cells by indexes. This will automatically add the "hidden" attribute to cells whose index belong to this
   * list. It will also take care of properly adjusting the controls. As a reaction, a "carousel:filter" with the
   * filtered indexes will be emitted.
   */
  filter(indexes = []) {
    this.allCells.forEach((cell, index) => {
      cell.toggleAttribute("hidden", indexes.includes(index));
    });
    this.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes: indexes } }));
  }
  async onBecameVisible() {
    const animationControls = await this.createOnBecameVisibleAnimationControls(this.selectedCell);
    [this.selectedCell, ...this.selectedCell.querySelectorAll("[reveal-on-scroll]")].forEach((element) => {
      element.removeAttribute("reveal-on-scroll");
    });
    if (__privateGet(this, _preventInitialTransition) && typeof animationControls.finish === "function") {
      animationControls.finish();
    }
    return animationControls.finished.then(() => {
      __privateGet(this, _player)?.resume(true);
      this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index: this.selectedIndex, cell: this.selectedCell } }));
    });
  }
  /**
   * The animation controls when the carousel enter into the view for the first time (by default, none)
   */
  createOnBecameVisibleAnimationControls(toSlide) {
    return animate3(toSlide, {}, { duration: 0 });
  }
  /**
   * Define the transition when the slide changes
   */
  createOnChangeAnimationControls(fromSlide, toSlide, { direction } = {}) {
    return timeline([
      [fromSlide, { opacity: [1, 0] }, { duration: 0.3 }],
      [toSlide, { opacity: [0, 1] }, { duration: 0.3, at: "<" }]
    ]);
  }
  /**
   * When the breakpoint changes (for instance from mobile to desktop), we may have to clean up the existing
   * attributes leave by Motion
   */
  cleanUpAnimations() {
    this.allCells.forEach((cell) => {
      cell.style.removeProperty("opacity");
      cell.style.removeProperty("visibility");
      cell.style.removeProperty("transform");
    });
  }
};
_listenersAbortController = new WeakMap();
_gestureArea = new WeakMap();
_player = new WeakMap();
_targetIndex = new WeakMap();
_preventInitialTransition = new WeakMap();
_EffectCarousel_instances = new WeakSet();
/**
 * -------------------------------------------------------------------------------------------------------------------
 * PRIVATE
 * -------------------------------------------------------------------------------------------------------------------
 */
setupListeners_fn = function() {
  if (this.hasAttribute("disabled-on")) {
    mediaQueryListener(this.getAttribute("disabled-on"), (event) => {
      if (event.matches) {
        __privateGet(this, _listenersAbortController)?.abort();
        this.cleanUpAnimations();
      } else {
        __privateMethod(this, _EffectCarousel_instances, setupListeners_fn).call(this);
      }
    });
    if (matchesMediaQuery(this.getAttribute("disabled-on"))) {
      return;
    }
  }
  __privateSet(this, _listenersAbortController, new AbortController());
  const listenerOptions = { signal: __privateGet(this, _listenersAbortController).signal };
  if (Shopify.designMode) {
    this.closest(".shopify-section").addEventListener("shopify:section:select", (event) => __privateSet(this, _preventInitialTransition, event.detail.load), listenerOptions);
  }
  if (this.allCells.length > 1) {
    this.addEventListener("carousel:change", __privateMethod(this, _EffectCarousel_instances, preloadImages_fn), listenerOptions);
    if (this.allowSwipe) {
      __privateSet(this, _gestureArea, new GestureArea(this, { signal: __privateGet(this, _listenersAbortController).signal }));
      this.addEventListener("swipeleft", this.next, listenerOptions);
      this.addEventListener("swiperight", this.previous, listenerOptions);
    }
    if (!this.hasAttribute("disable-keyboard-navigation")) {
      this.tabIndex = 0;
      this.addEventListener("keydown", __privateMethod(this, _EffectCarousel_instances, onKeyboardNavigation_fn), listenerOptions);
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.select(this.cells.indexOf(event.target), { instant: event.detail.load }), listenerOptions);
    }
    if (this.hasAttribute("autoplay")) {
      __privateGet(this, _player) ?? __privateSet(this, _player, new Player(this.getAttribute("autoplay") ?? 5));
      __privateGet(this, _player).addEventListener("player:end", this.next.bind(this), listenerOptions);
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", () => __privateGet(this, _player).stop(), listenerOptions);
        this.addEventListener("shopify:block:deselect", () => __privateGet(this, _player).start(), listenerOptions);
      }
    }
  }
};
/**
 * -------------------------------------------------------------------------------------------------------------------
 * OTHER
 * -------------------------------------------------------------------------------------------------------------------
 */
onKeyboardNavigation_fn = function(event) {
  if (event.target !== this) {
    return;
  }
  if (event.code === "ArrowLeft") {
    this.previous();
  } else if (event.code === "ArrowRight") {
    this.next();
  }
};
preloadImages_fn = function() {
  const previousSlide = this.cells[(this.selectedIndex - 1 + this.cells.length) % this.cells.length], nextSlide = this.cells[(this.selectedIndex + 1 + this.cells.length) % this.cells.length];
  [previousSlide, this.selectedCell, nextSlide].forEach((item) => {
    Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.removeAttribute("loading"));
    Array.from(item.querySelectorAll('video[preload="none"]')).forEach((video) => video.setAttribute("preload", "metadata"));
  });
};
if (!window.customElements.get("effect-carousel")) {
  window.customElements.define("effect-carousel", EffectCarousel);
}

// js/common/carousel/scroll-carousel.js
import { inView as inView4 } from "vendor";
var _hasPendingProgrammaticScroll, _onMouseDownListener, _onMouseMoveListener, _onMouseClickListener, _onMouseUpListener, _targetIndex2, _forceChangeEvent, _dragPosition, _isDragging, _dispatchableScrollEvents, _scrollTimeout, _scrollListenersBound, _ScrollCarousel_instances, setupListeners_fn2, updateTargetIndex_fn, onScroll_fn, onScrollEnd_fn, calculateLeftScroll_fn, calculateClosestIndexToAlignment_fn, onMouseDown_fn, onMouseMove_fn, onMouseClick_fn, onMouseUp_fn, onResize_fn, onMutate_fn, adaptHeight_fn, preloadImages_fn2;
var ScrollCarousel = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _ScrollCarousel_instances);
    __privateAdd(this, _hasPendingProgrammaticScroll, false);
    __privateAdd(this, _onMouseDownListener, __privateMethod(this, _ScrollCarousel_instances, onMouseDown_fn).bind(this));
    __privateAdd(this, _onMouseMoveListener, __privateMethod(this, _ScrollCarousel_instances, onMouseMove_fn).bind(this));
    __privateAdd(this, _onMouseClickListener, __privateMethod(this, _ScrollCarousel_instances, onMouseClick_fn).bind(this));
    __privateAdd(this, _onMouseUpListener, __privateMethod(this, _ScrollCarousel_instances, onMouseUp_fn).bind(this));
    __privateAdd(this, _targetIndex2, 0);
    // The cell index to which we are currently going to
    __privateAdd(this, _forceChangeEvent, false);
    __privateAdd(this, _dragPosition, {});
    __privateAdd(this, _isDragging, false);
    __privateAdd(this, _dispatchableScrollEvents, { nearingStart: true, nearingEnd: true, leavingStart: true, leavingEnd: true });
    __privateAdd(this, _scrollTimeout);
    __privateAdd(this, _scrollListenersBound, false);
    // Observers deferred until arm (FINAL SPEED) — avoids RO/MO thrash on below-fold rails
    // while content-visibility skips paint. CSS scroll-snap still works beforehand.
    this._jwResizeObserver = null;
    this._jwMutationObserver = null;
  }
  connectedCallback() {
    __privateSet(this, _targetIndex2, Math.max(
      0,
      this.cells.findIndex((item) => item.classList.contains("is-initial"))
    ));
    if (__privateGet(this, _targetIndex2) > 0) {
      this.select(__privateGet(this, _targetIndex2), { instant: true });
    }
    if (this.adaptiveHeight) {
      __privateMethod(this, _ScrollCarousel_instances, adaptHeight_fn).call(this);
    }
    const armCarousel = () => {
      if (!__privateGet(this, _scrollListenersBound)) {
        __privateSet(this, _scrollListenersBound, true);
        __privateMethod(this, _ScrollCarousel_instances, setupListeners_fn2).call(this);
      }
      if (!this._jwResizeObserver) {
        this._jwResizeObserver = new ResizeObserver(__privateMethod(this, _ScrollCarousel_instances, onResize_fn).bind(this));
        this._jwResizeObserver.observe(this);
      }
      if (!this._jwMutationObserver) {
        this._jwMutationObserver = new MutationObserver(__privateMethod(this, _ScrollCarousel_instances, onMutate_fn).bind(this));
        this._jwMutationObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ["hidden"] });
      }
      __privateMethod(this, _ScrollCarousel_instances, preloadImages_fn2).call(this);
      this.classList.toggle("is-scrollable", this.scrollWidth > this.clientWidth);
    };
    if (Shopify.designMode) {
      armCarousel();
    } else if (window.matchMedia("(max-width: 749px), (pointer: coarse)").matches) {
      // Mobile-first: arm immediately — CV is off on phones; avoid deferred swipe lag
      armCarousel();
    } else {
      // Desktop: 420px arm before fast scroll reaches the rail (was 280 — blank/jank risk)
      inView4(this, armCarousel, { margin: "420px 0px" });
    }
  }
  disconnectedCallback() {
    this._jwResizeObserver?.disconnect();
    this._jwMutationObserver?.disconnect();
    this.removeEventListener("pointermove", __privateGet(this, _onMouseMoveListener));
    this.removeEventListener("pointerup", __privateGet(this, _onMouseUpListener));
    this.removeEventListener("pointercancel", __privateGet(this, _onMouseUpListener));
    this.removeEventListener("mousemove", __privateGet(this, _onMouseMoveListener));
    document.removeEventListener("mouseup", __privateGet(this, _onMouseUpListener));
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (PROPERTIES)
   * -------------------------------------------------------------------------------------------------------------------
   */
  get cellSelector() {
    return this.hasAttribute("cell-selector") ? this.getAttribute("cell-selector") : null;
  }
  get allCells() {
    return this.cellSelector ? Array.from(this.querySelectorAll(this.cellSelector)) : Array.from(this.children);
  }
  get cells() {
    return this.allCells.filter((cell) => !cell.hasAttribute("hidden"));
  }
  get selectedCell() {
    return this.cells[this.selectedIndex];
  }
  get selectedIndex() {
    return __privateGet(this, _targetIndex2);
  }
  get cellAlign() {
    const firstCell = this.cells[0];
    if (!(firstCell instanceof Element)) {
      return "center";
    }
    const scrollSnapAlign = getComputedStyle(firstCell).scrollSnapAlign;
    return scrollSnapAlign === "none" ? "center" : scrollSnapAlign;
  }
  get groupCells() {
    if (this.hasAttribute("group-cells")) {
      const number = parseInt(this.getAttribute("group-cells"));
      if (!isNaN(number)) {
        return number;
      }
      const firstCell = this.cells[0];
      if (!(firstCell instanceof Element) || !firstCell.clientWidth) {
        return 1;
      }
      return Math.max(1, Math.floor(this.clientWidth / firstCell.clientWidth));
    } else {
      return 1;
    }
  }
  get adaptiveHeight() {
    return this.hasAttribute("adaptive-height");
  }
  get isScrollable() {
    const differenceWidth = this.scrollWidth - this.clientWidth;
    const differenceHeight = this.scrollHeight - this.clientHeight;
    return differenceWidth > 1 || differenceHeight > 1;
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (METHODS)
   * -------------------------------------------------------------------------------------------------------------------
   */
  previous({ instant = false } = {}) {
    this.select(Math.max(__privateGet(this, _targetIndex2) - this.groupCells, 0), { instant });
  }
  next({ instant = false } = {}) {
    this.select(Math.min(__privateGet(this, _targetIndex2) + this.groupCells, this.cells.length - 1), { instant });
  }
  select(index, { instant = false } = {}) {
    if (!(index in this.cells)) {
      return;
    }
    this.dispatchEvent(new CustomEvent("carousel:select", { detail: { index, cell: this.cells[index] } }));
    if ("checkVisibility" in this && this.checkVisibility() || this.offsetWidth > 0 && this.offsetHeight > 0) {
      const targetScrollLeft = __privateMethod(this, _ScrollCarousel_instances, calculateLeftScroll_fn).call(this, this.cells[index]);
      if (this.scrollLeft !== targetScrollLeft) {
        __privateMethod(this, _ScrollCarousel_instances, updateTargetIndex_fn).call(this, index);
        __privateSet(this, _hasPendingProgrammaticScroll, true);
        this.scrollTo({ left: targetScrollLeft, behavior: instant ? "auto" : "smooth" });
      } else {
        __privateMethod(this, _ScrollCarousel_instances, updateTargetIndex_fn).call(this, __privateMethod(this, _ScrollCarousel_instances, calculateClosestIndexToAlignment_fn).call(this));
      }
    } else {
      __privateSet(this, _targetIndex2, index);
      __privateSet(this, _forceChangeEvent, true);
    }
  }
  /**
   * Filter cells by indexes. This will automatically add the "hidden" attribute to cells whose index belong to this
   * list. It will also take care of properly adjusting the controls. As a reaction, a "carousel:filter" with the
   * filtered indexes will be emitted.
   */
  filter(indexes = []) {
    this.allCells.forEach((cell, index) => {
      cell.toggleAttribute("hidden", indexes.includes(index));
    });
    __privateSet(this, _forceChangeEvent, true);
    this.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes: indexes } }));
  }
};
_hasPendingProgrammaticScroll = new WeakMap();
_onMouseDownListener = new WeakMap();
_onMouseMoveListener = new WeakMap();
_onMouseClickListener = new WeakMap();
_onMouseUpListener = new WeakMap();
_targetIndex2 = new WeakMap();
_forceChangeEvent = new WeakMap();
_dragPosition = new WeakMap();
_isDragging = new WeakMap();
_dispatchableScrollEvents = new WeakMap();
_scrollTimeout = new WeakMap();
_scrollListenersBound = new WeakMap();
_ScrollCarousel_instances = new WeakSet();
/**
 * -------------------------------------------------------------------------------------------------------------------
 * PRIVATE METHODS
 * -------------------------------------------------------------------------------------------------------------------
 */
/**
 * Setup all the listeners needed for the carousel to work properly
 */
setupListeners_fn2 = function() {
  if (this.allCells.length > 1) {
    this.addEventListener("carousel:change", __privateMethod(this, _ScrollCarousel_instances, preloadImages_fn2), { passive: true });
    this.addEventListener("scroll", throttle(__privateMethod(this, _ScrollCarousel_instances, onScroll_fn).bind(this)), { passive: true });
    this.addEventListener("scrollend", __privateMethod(this, _ScrollCarousel_instances, onScrollEnd_fn), { passive: true });
    if (this.hasAttribute("allow-drag")) {
      // Pointer events for mouse + thumb: ancestor overflow-x:clip can block
      // native touch pan while carousel-prev/next (scrollLeft) still work.
      this.addEventListener("pointerdown", __privateGet(this, _onMouseDownListener));
    }
    if (this.adaptiveHeight) {
      this.addEventListener("carousel:settle", __privateMethod(this, _ScrollCarousel_instances, adaptHeight_fn));
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.select(this.cells.indexOf(event.target), { instant: event.detail.load }));
    }
  }
};
updateTargetIndex_fn = function(newValue) {
  if (newValue === __privateGet(this, _targetIndex2) && !__privateGet(this, _forceChangeEvent)) {
    return;
  }
  __privateSet(this, _targetIndex2, newValue);
  __privateSet(this, _forceChangeEvent, false);
  this.dispatchEvent(new CustomEvent("carousel:change", { detail: { index: newValue, cell: this.cells[newValue] } }));
};
/**
 * -------------------------------------------------------------------------------------------------------------------
 * SCROLL MANAGEMENT
 * -------------------------------------------------------------------------------------------------------------------
 */
onScroll_fn = function() {
  const scrollEdgeThreshold = 100, normalizedScrollLeft = Math.round(Math.abs(this.scrollLeft));
  if (normalizedScrollLeft < scrollEdgeThreshold && __privateGet(this, _dispatchableScrollEvents)["nearingStart"]) {
    this.dispatchEvent(new CustomEvent("scroll:edge-nearing", { detail: { position: "start" } }));
    __privateGet(this, _dispatchableScrollEvents)["nearingStart"] = false;
    __privateGet(this, _dispatchableScrollEvents)["leavingStart"] = true;
  }
  if (normalizedScrollLeft >= scrollEdgeThreshold && __privateGet(this, _dispatchableScrollEvents)["leavingStart"]) {
    this.dispatchEvent(new CustomEvent("scroll:edge-leaving", { detail: { position: "start" } }));
    __privateGet(this, _dispatchableScrollEvents)["leavingStart"] = false;
    __privateGet(this, _dispatchableScrollEvents)["nearingStart"] = true;
  }
  if (this.scrollWidth - this.clientWidth < normalizedScrollLeft + scrollEdgeThreshold && __privateGet(this, _dispatchableScrollEvents)["nearingEnd"]) {
    this.dispatchEvent(new CustomEvent("scroll:edge-nearing", { detail: { position: "end" } }));
    __privateGet(this, _dispatchableScrollEvents)["nearingEnd"] = false;
    __privateGet(this, _dispatchableScrollEvents)["leavingEnd"] = true;
  }
  if (this.scrollWidth - this.clientWidth >= normalizedScrollLeft + scrollEdgeThreshold && __privateGet(this, _dispatchableScrollEvents)["leavingEnd"]) {
    this.dispatchEvent(new CustomEvent("scroll:edge-leaving", { detail: { position: "end" } }));
    __privateGet(this, _dispatchableScrollEvents)["leavingEnd"] = false;
    __privateGet(this, _dispatchableScrollEvents)["nearingEnd"] = true;
  }
  if (!("onscrollend" in window)) {
    clearTimeout(__privateGet(this, _scrollTimeout));
    __privateSet(this, _scrollTimeout, setTimeout(() => {
      this.dispatchEvent(new CustomEvent("scrollend", { bubbles: true }));
    }, 75));
  }
  if (__privateGet(this, _hasPendingProgrammaticScroll)) {
    return;
  }
  __privateMethod(this, _ScrollCarousel_instances, updateTargetIndex_fn).call(this, __privateMethod(this, _ScrollCarousel_instances, calculateClosestIndexToAlignment_fn).call(this));
};
onScrollEnd_fn = function() {
  __privateSet(this, _hasPendingProgrammaticScroll, false);
  if (!__privateGet(this, _isDragging)) {
    this.style.removeProperty("scroll-snap-type");
  }
  __privateMethod(this, _ScrollCarousel_instances, updateTargetIndex_fn).call(this, __privateMethod(this, _ScrollCarousel_instances, calculateClosestIndexToAlignment_fn).call(this));
  this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index: this.selectedIndex, cell: this.selectedCell } }));
};
/**
 * Calculate the amount to scroll to align the cell with the "cell-align" rule
 */
calculateLeftScroll_fn = function(cell) {
  if (!cell) {
    return this.scrollLeft || 0;
  }
  let scrollLeft;
  switch (this.cellAlign) {
    case "start":
      scrollLeft = document.dir === "ltr" ? cell.offsetLeft - (parseInt(getComputedStyle(this).scrollPaddingInlineStart) || 0) : cell.offsetLeft + cell.offsetWidth - this.clientWidth + (parseInt(getComputedStyle(this).scrollPaddingInlineStart) || 0);
      break;
    case "center":
      scrollLeft = Math.round(cell.offsetLeft - this.clientWidth / 2 + cell.clientWidth / 2);
      break;
    case "end":
      scrollLeft = document.dir === "ltr" ? cell.offsetLeft + cell.offsetWidth - this.clientWidth + (parseInt(getComputedStyle(this).scrollPaddingInlineEnd) || 0) : cell.offsetLeft - (parseInt(getComputedStyle(this).scrollPaddingInlineEnd) || 0);
      break;
  }
  return document.dir === "ltr" ? Math.min(Math.max(scrollLeft, 0), this.scrollWidth - this.clientWidth) : Math.min(Math.max(scrollLeft, this.clientWidth - this.scrollWidth), 0);
};
calculateClosestIndexToAlignment_fn = function() {
  if (!this.cells.length) {
    return 0;
  }
  let cellAlign = this.cellAlign, offsetAccumulators, targetPoint;
  if (cellAlign === "center") {
    offsetAccumulators = this.cells.map((cell) => Math.round(cell.offsetLeft + cell.clientWidth / 2));
    targetPoint = Math.round(this.scrollLeft + this.clientWidth / 2);
  } else if (cellAlign === "start" && document.dir === "ltr" || cellAlign === "end" && document.dir === "rtl") {
    offsetAccumulators = this.cells.map((cell) => cell.offsetLeft);
    targetPoint = this.scrollLeft;
  } else {
    offsetAccumulators = this.cells.map((cell) => cell.offsetLeft + cell.clientWidth);
    targetPoint = this.scrollLeft + this.clientWidth;
  }
  if (!offsetAccumulators.length) {
    return 0;
  }
  return offsetAccumulators.indexOf(offsetAccumulators.reduce((prev, curr) => Math.abs(curr - targetPoint) < Math.abs(prev - targetPoint) ? curr : prev));
};
/**
 * -------------------------------------------------------------------------------------------------------------------
 * DRAG FEATURE
 * -------------------------------------------------------------------------------------------------------------------
 */
onMouseDown_fn = function(event) {
  if (!event.isPrimary) {
    return;
  }
  if (event.pointerType === "mouse" && event.button !== 0) {
    return;
  }
  // Skip drag on form controls and buttons (quick-buy / ATC) so those clicks
  // never compete with carousel click-suppression.
  if (event.target.closest("input, textarea, select, button, carousel-prev-button, carousel-next-button")) {
    return;
  }
  // Detect axis from movement for mouse + touch. Immediate axis:"x" on
  // mousedown made any 1px jitter set moved=true and kill product-card clicks.
  __privateSet(this, _dragPosition, {
    left: this.scrollLeft,
    top: this.scrollTop,
    x: event.clientX,
    y: event.clientY,
    pointerId: event.pointerId,
    axis: null,
    moved: false
  });
  this.addEventListener("pointermove", __privateGet(this, _onMouseMoveListener), { passive: false });
  this.addEventListener("click", __privateGet(this, _onMouseClickListener), { once: true, capture: true });
  this.addEventListener("pointerup", __privateGet(this, _onMouseUpListener));
  this.addEventListener("pointercancel", __privateGet(this, _onMouseUpListener));
};
onMouseMove_fn = function(event) {
  const pos = __privateGet(this, _dragPosition);
  if (!pos || event.pointerId !== pos.pointerId) {
    return;
  }
  const dx = event.clientX - pos.x, dy = event.clientY - pos.y;
  const dragThreshold = 8;
  if (!pos.axis) {
    if (Math.abs(dx) < dragThreshold && Math.abs(dy) < dragThreshold) {
      return;
    }
    if (Math.abs(dy) >= Math.abs(dx)) {
      this.removeEventListener("pointermove", __privateGet(this, _onMouseMoveListener));
      this.removeEventListener("pointerup", __privateGet(this, _onMouseUpListener));
      this.removeEventListener("pointercancel", __privateGet(this, _onMouseUpListener));
      __privateSet(this, _dragPosition, {});
      return;
    }
    pos.axis = "x";
    __privateSet(this, _isDragging, true);
    this.style.setProperty("scroll-snap-type", "none");
    try {
      this.setPointerCapture(event.pointerId);
    } catch (e) {
    }
  }
  if (pos.axis !== "x") {
    return;
  }
  event.preventDefault();
  pos.moved = true;
  this.scrollTop = pos.top - dy;
  this.scrollLeft = pos.left - dx;
};
onMouseClick_fn = function(event) {
  const pos = __privateGet(this, _dragPosition);
  // Only swallow clicks after a real horizontal drag — never on tap/micro-jitter.
  if (pos && pos.moved) {
    event.preventDefault();
    event.stopPropagation();
  }
};
onMouseUp_fn = function(event) {
  const pos = __privateGet(this, _dragPosition);
  if (pos && event.pointerId != null && pos.pointerId != null && event.pointerId !== pos.pointerId) {
    return;
  }
  const moved = !!(pos && pos.moved);
  __privateSet(this, _isDragging, false);
  if (!moved) {
    this.style.removeProperty("scroll-snap-type");
  } else if (!__privateGet(this, _hasPendingProgrammaticScroll)) {
    this.scrollTo({ left: __privateMethod(this, _ScrollCarousel_instances, calculateLeftScroll_fn).call(this, this.selectedCell), behavior: "smooth" });
  }
  this.removeEventListener("pointermove", __privateGet(this, _onMouseMoveListener));
  this.removeEventListener("pointerup", __privateGet(this, _onMouseUpListener));
  this.removeEventListener("pointercancel", __privateGet(this, _onMouseUpListener));
  try {
    if (pos?.pointerId != null) {
      this.releasePointerCapture(pos.pointerId);
    }
  } catch (e) {
  }
};
/**
 * -------------------------------------------------------------------------------------------------------------------
 * OTHER
 * -------------------------------------------------------------------------------------------------------------------
 */
onResize_fn = function() {
  if (this.selectedIndex !== __privateMethod(this, _ScrollCarousel_instances, calculateClosestIndexToAlignment_fn).call(this)) {
    this.select(this.selectedIndex, { instant: true });
  }
  if (this.adaptiveHeight) {
    __privateMethod(this, _ScrollCarousel_instances, adaptHeight_fn).call(this);
  }
  this.classList.toggle("is-scrollable", this.scrollWidth > this.clientWidth);
};
onMutate_fn = function() {
  __privateSet(this, _forceChangeEvent, true);
};
adaptHeight_fn = function() {
  if (!(this.selectedCell instanceof Element)) {
    return;
  }
  if (this.clientHeight === this.selectedCell.clientHeight) {
    return;
  }
  this.style.maxHeight = null;
  if (this.isScrollable) {
    this.style.maxHeight = `${this.selectedCell.clientHeight}px`;
  }
};
preloadImages_fn2 = function() {
  requestAnimationFrame(() => {
    if (!this.cells.length) {
      return;
    }
    // Mobile PDP free_scroll: unloaded lazy slides can be 0-tall (align-self:start +
    // height:auto), so native lazy never intersects. Unlock all visible cells.
    const isMobileGallery = this.classList.contains("product-gallery__carousel") && window.matchMedia("(max-width: 999px), (pointer: coarse)").matches;
    const items = isMobileGallery ? this.cells : [this.cells[Math.max(this.selectedIndex - 1, 0)], this.selectedCell, this.cells[Math.min(this.selectedIndex + 1, this.cells.length - 1)]].filter((item) => item instanceof Element);
    items.forEach((item) => {
      Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.removeAttribute("loading"));
      Array.from(item.querySelectorAll('video[preload="none"]')).forEach((video) => video.setAttribute("preload", "metadata"));
    });
  });
};
if (!window.customElements.get("scroll-carousel")) {
  window.customElements.define("scroll-carousel", ScrollCarousel);
}

// js/common/cart/fetch-cart.js
var createCartPromise = () => {
  return new Promise(async (resolve) => {
    resolve(await (await fetch(`${Shopify.routes.root}cart.js`)).json());
  });
};
var fetchCart = createCartPromise();
document.addEventListener("cart:change", (event) => {
  fetchCart = event.detail["cart"];
});
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    fetchCart = createCartPromise();
  }
});
document.addEventListener("cart:refresh", () => {
  fetchCart = createCartPromise();
});

// js/common/cart/cart-count.js
var _abortController2, _CartCount_instances, updateFromServer_fn;
var CartCount = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CartCount_instances);
    __privateAdd(this, _abortController2);
  }
  connectedCallback() {
    __privateSet(this, _abortController2, new AbortController());
    document.addEventListener("cart:change", (event) => this.itemCount = event.detail["cart"]["item_count"], { signal: __privateGet(this, _abortController2).signal });
    document.addEventListener("cart:refresh", __privateMethod(this, _CartCount_instances, updateFromServer_fn).bind(this), { signal: __privateGet(this, _abortController2).signal });
    window.addEventListener("pageshow", __privateMethod(this, _CartCount_instances, updateFromServer_fn).bind(this), { signal: __privateGet(this, _abortController2).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController2).abort();
  }
  set itemCount(count) {
    this.innerText = count;
  }
};
_abortController2 = new WeakMap();
_CartCount_instances = new WeakSet();
updateFromServer_fn = async function() {
  this.itemCount = (await fetchCart)["item_count"];
};
if (!window.customElements.get("cart-count")) {
  window.customElements.define("cart-count", CartCount);
}

// js/common/cart/cart-discount.js
var AbstractCartDiscount = class extends HTMLElement {
  async getDiscountCodes() {
    return (await fetchCart)["discount_codes"].filter((discount) => discount.applicable).map((discount) => discount.code.toLowerCase());
  }
  async toggleDiscount(event) {
    let target = event.currentTarget;
    target.setAttribute("aria-busy", "true");
    let discountCodes = await this.getDiscountCodes();
    if (target.hasAttribute("discount-code")) {
      discountCodes = discountCodes.filter((discount2) => discount2 !== target.getAttribute("discount-code").toLowerCase());
    }
    let discount = (discountCodes.length > 0 ? discountCodes.join(",") + "," : "") + (event.target.value || "");
    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({ discount })
    });
    target.setAttribute("aria-busy", "false");
    if (!response.ok) {
      this.dispatchEvent(new CustomEvent("cart:discount:error", { bubbles: true }));
      return;
    }
    const responseJson = await response.json();
    if (responseJson.discount_codes.some((obj) => obj.applicable === false)) {
      this.dispatchEvent(new CustomEvent("cart:discount:error", { bubbles: true }));
      return;
    } else {
      this.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }
    if (window.themeVariables.settings.pageType === "cart") {
      window.location.reload();
    }
  }
};
var _abortController3, _CartDiscountBanner_instances, onCartDiscountError_fn;
var CartDiscountBanner = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CartDiscountBanner_instances);
    __privateAdd(this, _abortController3);
  }
  connectedCallback() {
    __privateSet(this, _abortController3, new AbortController());
    document.addEventListener("cart:discount:error", __privateMethod(this, _CartDiscountBanner_instances, onCartDiscountError_fn).bind(this), { signal: __privateGet(this, _abortController3).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController3).abort();
  }
};
_abortController3 = new WeakMap();
_CartDiscountBanner_instances = new WeakSet();
onCartDiscountError_fn = function() {
  this.hidden = false;
};
var _hiddenDiscountInputOriginalValue, _CartDiscountField_instances, hiddenDiscountInput_get, updateHiddenInput_fn;
var CartDiscountField = class extends AbstractCartDiscount {
  constructor() {
    super();
    __privateAdd(this, _CartDiscountField_instances);
    __privateAdd(this, _hiddenDiscountInputOriginalValue);
    __privateSet(this, _hiddenDiscountInputOriginalValue, __privateGet(this, _CartDiscountField_instances, hiddenDiscountInput_get).value);
    this.addEventListener("change", this.toggleDiscount.bind(this));
    this.addEventListener("input", __privateMethod(this, _CartDiscountField_instances, updateHiddenInput_fn));
  }
};
_hiddenDiscountInputOriginalValue = new WeakMap();
_CartDiscountField_instances = new WeakSet();
hiddenDiscountInput_get = function() {
  return this.querySelector('[name="discount"]');
};
updateHiddenInput_fn = function(event) {
  __privateGet(this, _CartDiscountField_instances, hiddenDiscountInput_get).value = [__privateGet(this, _hiddenDiscountInputOriginalValue), event.target.value].filter((val) => val && val.trim() !== "").join(",");
};
var CartDiscountRemoveButton = class extends AbstractCartDiscount {
  constructor() {
    super();
    this.addEventListener("click", this.toggleDiscount.bind(this));
  }
};
if (!window.customElements.get("cart-discount-field")) {
  window.customElements.define("cart-discount-field", CartDiscountField);
}
if (!window.customElements.get("cart-discount-remove-button")) {
  window.customElements.define("cart-discount-remove-button", CartDiscountRemoveButton);
}
if (!window.customElements.get("cart-discount-banner")) {
  window.customElements.define("cart-discount-banner", CartDiscountBanner);
}

// js/common/cart/cart-dot.js
var _abortController4, _CartDot_instances, updateFromServer_fn2;
var CartDot = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CartDot_instances);
    __privateAdd(this, _abortController4);
  }
  connectedCallback() {
    __privateSet(this, _abortController4, new AbortController());
    document.addEventListener("cart:change", (event) => this.classList.toggle("is-visible", event.detail["cart"]["item_count"] > 0), { signal: __privateGet(this, _abortController4).signal });
    document.addEventListener("cart:refresh", __privateMethod(this, _CartDot_instances, updateFromServer_fn2).bind(this), { signal: __privateGet(this, _abortController4).signal });
    window.addEventListener("pageshow", __privateMethod(this, _CartDot_instances, updateFromServer_fn2).bind(this), { signal: __privateGet(this, _abortController4).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController4).abort();
  }
};
_abortController4 = new WeakMap();
_CartDot_instances = new WeakSet();
updateFromServer_fn2 = async function() {
  this.classList.toggle("is-visible", (await fetchCart)["item_count"] > 0);
};
if (!window.customElements.get("cart-dot")) {
  window.customElements.define("cart-dot", CartDot);
}

// js/common/cart/cart-note.js
var _CartNote_instances, onNoteChanged_fn;
var CartNote = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _CartNote_instances);
    this.addEventListener("change", __privateMethod(this, _CartNote_instances, onNoteChanged_fn));
  }
};
_CartNote_instances = new WeakSet();
onNoteChanged_fn = function(event) {
  if (event.target.getAttribute("name") !== "note") {
    return;
  }
  fetch(`${Shopify.routes.root}cart/update.js`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note: event.target.value }),
    keepalive: true
    // Allows to make sure the request is fired even when submitting the form
  });
};
if (!window.customElements.get("cart-note")) {
  window.customElements.define("cart-note", CartNote);
}

// js/common/cart/free-shipping-bar.js
// Defined early: __jwBuildFsBarInnerHtml (and cart-drawer builders) call this.
// A later reassignment in cart-drawer.js keeps the same behavior.
var __jwEscHtml = function(str) {
  return String(str == null ? "" : str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
};
var __jwCelebrateFreeShippingBar = function(bar) {
  if (!bar || typeof bar.classList === "undefined") return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  bar.classList.remove("jw-fsb--celebrate");
  void bar.offsetWidth;
  bar.classList.add("jw-fsb--celebrate");
  window.clearTimeout(bar.__jwFsCelebrateTimer);
  bar.__jwFsCelebrateTimer = window.setTimeout(function() {
    bar.classList.remove("jw-fsb--celebrate");
  }, 1100);
};
// Dual-tier free-product progress:
// B2G1 — 2 paid unlock 1 free (3 units). B4G2 — 4 paid unlock 2 free (6 units).
var __jwFsTier1Paid = 2;
var __jwFsTier2Paid = 4;
var __jwFsTier1Free = 1;
var __jwFsTier2Free = 2;
var __jwFsPaidTarget = __jwFsTier2Paid;
var __jwFsShipPos = 20;
var __jwFsShippingUnlockedChip = "Unlocked FREE SHIPPING";
var __jwFsIconGiftSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="10" width="18" height="11" rx="1.5"/><path d="M12 10v11M3 14h18"/><path d="M12 10s-2.8-5.2-5.8-5.2C4 4.8 3.6 7 5.2 9.2 7.4 10.4 12 10 12 10"/><path d="M12 10s2.8-5.2 5.8-5.2C20 4.8 20.4 7 18.8 9.2 16.6 10.4 12 10 12 10"/></svg>';
var __jwFsIconTruckSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h11v10H3z"/><path d="M14 10h4l3 3v4h-7V10z"/><circle cx="7.5" cy="18.5" r="1.75"/><circle cx="17.5" cy="18.5" r="1.75"/></svg>';
var __jwFsIconCheckSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.2 4.2L19 7"/></svg>';
var __jwFsProtectionHandles = [
  "jwear-shipping-protection",
  "package-protection",
  "order-protection",
  "shop-protection",
  "delivery-protection",
  "shipping-protection"
];
var __jwFsIsExcludedItem = function(item, protectionVariantId) {
  if (!item) return true;
  if (protectionVariantId && String(item.variant_id || "") === String(protectionVariantId)) return true;
  var handle = String(item.handle || "").toLowerCase();
  if (handle && __jwFsProtectionHandles.indexOf(handle) !== -1) return true;
  var title = String(item.product_title || item.title || "").toLowerCase();
  if (
    title.indexOf("shipping protection") !== -1 ||
    title.indexOf("package protection") !== -1 ||
    title.indexOf("order protection") !== -1 ||
    title.indexOf("delivery protection") !== -1
  ) {
    return true;
  }
  return false;
};
var __jwFsItemIsFreeLine = function(item) {
  // TEMP: Shopify discount only — ignore theme _jw_bundle_role free tags
  var finalLine = Number(item && item.final_line_price);
  var originalLine = Number(item && item.original_line_price);
  return finalLine === 0 && originalLine > 0;
};
var __jwFsAllowedFreeForPaid = function(paid) {
  paid = Math.max(0, Number(paid) || 0);
  if (paid >= __jwFsTier2Paid) return __jwFsTier2Free;
  if (paid >= __jwFsTier1Paid) return __jwFsTier1Free;
  return 0;
};
// Max free units that still leave a valid paid→free unlock (2→1, 4→2).
var __jwFsOptimalFreeForTotal = function(total) {
  total = Math.max(0, Number(total) || 0);
  var maxF = Math.min(total, Math.max(__jwFsTier1Free, __jwFsTier2Free));
  for (var f = maxF; f >= 0; f--) {
    if (f <= __jwFsAllowedFreeForPaid(total - f)) return f;
  }
  return 0;
};
var __jwFsUnitPriceCents = function(item) {
  var qty = Math.max(1, Number(item && item.quantity) || 1);
  var unit = Number(item && item.final_price);
  if (isFinite(unit) && unit >= 0) return unit;
  var line = Number(item && (item.final_line_price != null ? item.final_line_price : item.line_price)) || 0;
  return line / qty;
};
/**
 * Auto-designate cheapest eligible units as FREE when unlock qualifies
 * (non-bundle ATC never sets _jw_bundle_role). Shipping protection excluded.
 * Returns byKey[lineKey] = free unit count on that line.
 */
var __jwFsAllocateFreeUnits = function(items, protectionVariantId) {
  items = Array.isArray(items) ? items : [];
  var rows = [];
  var total = 0;
  for (var i = 0; i < items.length; i++) {
    var item = items[i];
    if (__jwFsIsExcludedItem(item, protectionVariantId)) continue;
    var qty = Math.max(0, Number(item.quantity) || 0);
    if (!qty || !item.key) continue;
    var alreadyFree = __jwFsItemIsFreeLine(item);
    var unitPrice = alreadyFree ? -1 : __jwFsUnitPriceCents(item);
    rows.push({
      key: String(item.key),
      index: i,
      qty: qty,
      unitPrice: unitPrice,
      alreadyFree: alreadyFree,
      item: item
    });
    total += qty;
  }
  var needFree = __jwFsOptimalFreeForTotal(total);
  var units = [];
  for (var r = 0; r < rows.length; r++) {
    var row = rows[r];
    for (var u = 0; u < row.qty; u++) {
      units.push({
        key: row.key,
        unitPrice: row.unitPrice,
        index: row.index,
        unitIndex: u,
        alreadyFree: row.alreadyFree
      });
    }
  }
  units.sort(function(a, b) {
    if (a.unitPrice !== b.unitPrice) return a.unitPrice - b.unitPrice;
    if (a.alreadyFree !== b.alreadyFree) return a.alreadyFree ? -1 : 1;
    if (a.index !== b.index) return a.index - b.index;
    return a.unitIndex - b.unitIndex;
  });
  var byKey = {};
  for (var k = 0; k < rows.length; k++) byKey[rows[k].key] = 0;
  for (var f = 0; f < needFree && f < units.length; f++) {
    byKey[units[f].key] = (byKey[units[f].key] || 0) + 1;
  }
  var free = needFree;
  var paid = Math.max(0, total - free);
  return {
    byKey: byKey,
    rows: rows,
    total: total,
    paid: paid,
    free: free,
    needFree: needFree
  };
};
var __jwFsCountCartProgress = function(items, protectionVariantId, paidTarget) {
  var target = Math.max(__jwFsTier1Paid, Number(paidTarget) || __jwFsPaidTarget);
  if (target >= 100) target = __jwFsPaidTarget;
  // Count after auto-free designation so fill/message match actual FREE treatment
  // (e.g. 3 untagged units → 2 paid + 1 free, not 3 paid / "Add 1 free").
  var alloc = __jwFsAllocateFreeUnits(items, protectionVariantId);
  var paid = alloc.paid;
  var free = alloc.free;
  var allowedFree = __jwFsAllowedFreeForPaid(paid);
  var freeRemaining = Math.max(0, allowedFree - free);
  // Rail: Free Shipping early (~20%), B2G1 at 50%, B4G2 at 100%.
  // Milestone done follows paid unlock (activated) even before free lines are added.
  var shippingUnlocked = alloc.total > 0;
  var tier1Done = paid >= __jwFsTier1Paid;
  var tier2Done = paid >= __jwFsTier2Paid;
  // B4G2 complete (celebration) when paid hits tier2 and free units are claimed.
  var reached = tier2Done && free >= __jwFsTier2Free;
  var progress = 0;
  if (tier2Done) progress = 1;
  else if (paid > 0) progress = Math.min(1, paid / __jwFsTier2Paid);
  if (shippingUnlocked) progress = Math.max(progress, __jwFsShipPos / 100);
  // Snap fill through unlocked gift nodes so they read as activated.
  if (tier1Done) progress = Math.max(progress, __jwFsTier1Paid / __jwFsTier2Paid);
  if (tier2Done) progress = 1;
  var remainingPaidTier1 = Math.max(0, __jwFsTier1Paid - paid);
  var remainingPaidTier2 = Math.max(0, __jwFsTier2Paid - paid);
  var remaining = !tier1Done ? remainingPaidTier1 : remainingPaidTier2;
  var isClose = !reached && freeRemaining > 0;
  var phase = reached ? "complete" : !tier1Done ? "tier1" : "tier2";
  return {
    paid: paid,
    free: free,
    progress: progress,
    reached: reached,
    tier1Done: tier1Done,
    tier2Done: tier2Done,
    shippingUnlocked: shippingUnlocked,
    isClose: isClose,
    target: target,
    remaining: remaining,
    remainingPaidTier1: remainingPaidTier1,
    remainingPaidTier2: remainingPaidTier2,
    allowedFree: allowedFree,
    freeRemaining: freeRemaining,
    phase: phase,
    freeByKey: alloc.byKey
  };
};
var __jwFsBarMessageHtml = function(state) {
  state = state || {};
  var paid = Math.max(0, Number(state.paid) || 0);
  var free = Math.max(0, Number(state.free) || 0);
  if (free >= __jwFsTier2Free && paid >= __jwFsTier2Paid) return "You've unlocked 2 free gifts!";
  if (paid >= __jwFsTier2Paid && free < __jwFsTier2Free) {
    var needFree = Math.max(1, __jwFsTier2Free - free);
    return needFree === 1
      ? "Claim your free gift — add <strong>1</strong> free product"
      : "Claim your free gifts — add <strong>" + needFree + "</strong> free products";
  }
  if (paid >= __jwFsTier1Paid && free >= __jwFsTier1Free) {
    var needPaid = Math.max(0, __jwFsTier2Paid - paid);
    if (needPaid > 0) {
      return needPaid === 1
        ? "Add <strong>1</strong> more to get 2 free total"
        : "Add <strong>" + needPaid + "</strong> more to get 2 free total";
    }
    return "You've unlocked 2 free gifts!";
  }
  if (paid >= __jwFsTier1Paid) return "Claim your free gift — add <strong>1</strong> free product";
  if (paid > 0) {
    var need = Math.max(1, __jwFsTier1Paid - paid);
    return need === 1
      ? "You're just <strong>1</strong> product away from 1 free gift"
      : "You're <strong>" + need + "</strong> products away from 1 free gift";
  }
  return "Add <strong>" + __jwFsTier1Paid + "</strong> products for 1 free gift";
};
var __jwBuildFsBarInnerHtml = function(fsbMsg, shippingSubline, progress, opts) {
  opts = opts || {};
  var pct = Math.round((Number(progress) || 0) * 100);
  var reached = !!opts.reached;
  var halfDone = !!opts.halfDone || !!opts.tier1Done;
  var shipDone = opts.shipDone != null ? !!opts.shipDone : !!opts.tier2Done || reached;
  var shippingUnlocked = opts.shippingUnlocked != null ? !!opts.shippingUnlocked : true;
  var halfLabel = opts.halfLabel || "1 Free";
  var shipLabel = opts.shipLabel || "2 Free";
  var fsLabel = opts.fsLabel || "Free Ship";
  var shipPos = opts.shipPos;
  if (shipPos == null || !isFinite(shipPos)) shipPos = __jwFsShipPos;
  // B2G1 sits at tier1Paid/tier2Paid (2/4 = 50%) on the B4G2 rail.
  var halfPos = opts.halfPos;
  if (halfPos == null || !isFinite(halfPos)) {
    halfPos = Math.round(__jwFsTier1Paid / __jwFsTier2Paid * 1000) / 10;
  }
  var sublineHtml = "";
  if (shippingSubline) {
    var subClass = "jw-free-shipping-bar__subline" + (shippingUnlocked ? " jw-free-shipping-bar__subline--shipping" : "");
    // Unlocked shipping copy may include intentional <strong>; other sublines are escaped upstream.
    sublineHtml = '<p class="' + subClass + '">' + shippingSubline + "</p>";
  }
  var stepDot = function(iconSvg) {
    return '<span class="jw-free-shipping-bar__dot"><span class="jw-free-shipping-bar__dot-truck jw-free-shipping-bar__dot-gift">' + iconSvg + '</span><span class="jw-free-shipping-bar__dot-check">' + __jwFsIconCheckSvg + "</span></span>";
  };
  return '<div class="jw-free-shipping-bar__inner"><div class="jw-free-shipping-bar__header"><p class="free-shipping-bar__message jw-free-shipping-bar__message" data-jw-fs-message>' + fsbMsg + "</p></div>" + sublineHtml + '<div class="jw-free-shipping-bar__milestones" aria-hidden="true"><div class="jw-free-shipping-bar__rail"><span class="jw-free-shipping-bar__rail-bg"></span><span class="free-shipping-bar__progress jw-free-shipping-bar__fill" style="width:' + pct + '%"></span></div><ol class="jw-free-shipping-bar__steps"><li class="jw-free-shipping-bar__step jw-free-shipping-bar__step--start" data-jw-fs-step="start" style="--jw-fs-pos:0%"><span class="jw-free-shipping-bar__start-icon" aria-hidden="true">' + __jwFsIconGiftSvg + '</span></li><li class="jw-free-shipping-bar__step jw-free-shipping-bar__step--fs' + (shippingUnlocked ? " is-done" : "") + '" data-jw-fs-step="shipping" style="--jw-fs-pos:' + shipPos + '%">' + stepDot(__jwFsIconTruckSvg) + '<span class="jw-free-shipping-bar__step-label">' + __jwEscHtml(fsLabel) + '</span></li><li class="jw-free-shipping-bar__step jw-free-shipping-bar__step--half' + (halfDone ? " is-done" : "") + '" data-jw-fs-step="half" style="--jw-fs-pos:' + halfPos + '%">' + stepDot(__jwFsIconGiftSvg) + '<span class="jw-free-shipping-bar__step-label">' + __jwEscHtml(halfLabel) + '</span></li><li class="jw-free-shipping-bar__step jw-free-shipping-bar__step--ship' + (shipDone ? " is-done" : "") + '" data-jw-fs-step="ship" style="--jw-fs-pos:100%">' + stepDot(__jwFsIconGiftSvg) + '<span class="jw-free-shipping-bar__step-label">' + __jwEscHtml(shipLabel) + '</span></li></ol></div><div class="jw-free-shipping-bar__confetti" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i><i></i></div></div>';
};
var __jwBuildFsPromoHtml = function(promoLine1, promoLine2) {
  var line1 = String(promoLine1 || "").trim();
  // Hide unlocked-free-shipping chip copy; keep gift-offer chip if present.
  var line2 = "";
  if (!line1 && !line2) return "";
  var html = '<div class="jw-free-shipping-bar__promo jw-free-shipping-bar__promo--split">';
  if (line2) html += '<span class="jw-free-shipping-bar__promo-chip jw-free-shipping-bar__promo-chip--fs">' + __jwEscHtml(line2) + "</span>";
  if (line1) html += '<span class="jw-free-shipping-bar__promo-chip">' + __jwEscHtml(line1) + "</span>";
  html += "</div>";
  return html;
};
var _onCartChangedListener, _threshold, _FreeShippingBar_instances, updateMessage_fn, onCartChanged_fn;
var FreeShippingBar = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _FreeShippingBar_instances);
    __privateAdd(this, _onCartChangedListener, __privateMethod(this, _FreeShippingBar_instances, onCartChanged_fn).bind(this));
    // Never throw during CE upgrade — a constructor error aborts theme.js before CartDrawer registers,
    // and bag clicks then fall through to /cart.
    __privateAdd(this, _threshold);
    var rawThreshold = this.getAttribute("threshold");
    var parsed = parseInt(String(rawThreshold == null ? String(__jwFsPaidTarget) : rawThreshold).replace(/[^0-9]/g, ""), 10);
    // Legacy free-shipping thresholds were dollar/cent amounts (>=100). Dual-tier uses unit counts (4).
    if (!isFinite(parsed) || parsed <= 0 || parsed >= 100) parsed = __jwFsPaidTarget;
    __privateSet(this, _threshold, parsed);
    this.setAttribute("threshold", String(__privateGet(this, _threshold)));
  }
  static get observedAttributes() {
    return ["threshold", "paid-count", "free-count", "total-price"];
  }
  connectedCallback() {
    document.addEventListener("cart:change", __privateGet(this, _onCartChangedListener));
  }
  disconnectedCallback() {
    document.removeEventListener("cart:change", __privateGet(this, _onCartChangedListener));
  }
  get paidCount() {
    var n = parseInt(this.getAttribute("paid-count") || "0", 10);
    return isFinite(n) ? n : 0;
  }
  set paidCount(value) {
    this.setAttribute("paid-count", String(value));
  }
  get freeCount() {
    var n = parseInt(this.getAttribute("free-count") || "0", 10);
    return isFinite(n) ? n : 0;
  }
  set freeCount(value) {
    this.setAttribute("free-count", String(value));
  }
  // Legacy setter kept so older callers that still assign money totals do not throw.
  get totalPrice() {
    return this.paidCount;
  }
  set totalPrice(value) {
    this.paidCount = value;
  }
  attributeChangedCallback() {
    __privateMethod(this, _FreeShippingBar_instances, updateMessage_fn).call(this);
  }
};
_onCartChangedListener = new WeakMap();
_threshold = new WeakMap();
_FreeShippingBar_instances = new WeakSet();
updateMessage_fn = function() {
  const messageElement = this.querySelector("[data-free-shipping-message]") || this.querySelector("[data-jw-fs-message]") || this.querySelector("span");
  if (!messageElement) return;
  const state = {
    paid: this.paidCount,
    free: this.freeCount,
    target: __privateGet(this, _threshold) || __jwFsPaidTarget,
    progress: 0,
    remaining: 0,
    remainingPaidTier1: 0,
    remainingPaidTier2: 0,
    allowedFree: 0,
    freeRemaining: 0,
    tier1Done: false,
    tier2Done: false,
    reached: false,
    isClose: false,
    phase: "tier1"
  };
  state.allowedFree = __jwFsAllowedFreeForPaid(state.paid);
  state.freeRemaining = Math.max(0, state.allowedFree - state.free);
  state.tier1Done = state.paid >= __jwFsTier1Paid;
  state.tier2Done = state.paid >= __jwFsTier2Paid;
  state.reached = state.tier2Done && state.free >= __jwFsTier2Free;
  state.shippingUnlocked = state.paid + state.free > 0;
  state.remainingPaidTier1 = Math.max(0, __jwFsTier1Paid - state.paid);
  state.remainingPaidTier2 = Math.max(0, __jwFsTier2Paid - state.paid);
  state.remaining = !state.tier1Done ? state.remainingPaidTier1 : state.remainingPaidTier2;
  state.isClose = !state.reached && state.freeRemaining > 0;
  if (state.tier2Done) state.progress = 1;
  else if (state.paid > 0) state.progress = Math.min(1, state.paid / __jwFsTier2Paid);
  if (state.shippingUnlocked) state.progress = Math.max(state.progress, __jwFsShipPos / 100);
  if (state.tier1Done) state.progress = Math.max(state.progress, __jwFsTier1Paid / __jwFsTier2Paid);
  if (state.tier2Done) state.progress = 1;
  state.phase = state.reached ? "complete" : !state.tier1Done ? "tier1" : "tier2";
  const wasReached = this.classList.contains("is-reached");
  const prevFreeTier = parseInt(this.getAttribute("data-jw-fs-free-tier") || "0", 10) || 0;
  const freeTier = state.tier2Done ? 2 : state.tier1Done ? 1 : 0;
  this.style.setProperty("--progress", String(state.progress));
  this.setAttribute("aria-valuenow", String(Math.round(state.progress * 100)));
  this.setAttribute("data-jw-fs-free-tier", String(freeTier));
  this.setAttribute("data-jw-fs-phase", state.phase);
  this.setAttribute("data-jw-fs-shipping-unlocked", state.shippingUnlocked ? "true" : "false");
  this.classList.toggle("is-reached", state.reached);
  this.classList.toggle("jw-free-shipping-bar--tier1-done", state.tier1Done && !state.reached);
  this.classList.toggle("jw-free-shipping-bar--shipping-unlocked", state.shippingUnlocked);
  this.classList.toggle("jw-free-shipping-bar--always-free", false);
  this.classList.toggle("jw-free-shipping-bar--close", state.isClose);
  const fillEl = this.querySelector(".jw-free-shipping-bar__fill, .free-shipping-bar__progress");
  if (fillEl) fillEl.style.width = Math.round(state.progress * 100) + "%";
  const shippingStep = this.querySelector('[data-jw-fs-step="shipping"]');
  const halfStep = this.querySelector('[data-jw-fs-step="half"]');
  const shipStep = this.querySelector('[data-jw-fs-step="ship"]');
  if (shippingStep) shippingStep.classList.toggle("is-done", state.shippingUnlocked);
  if (halfStep) halfStep.classList.toggle("is-done", state.tier1Done);
  if (shipStep) shipStep.classList.toggle("is-done", state.tier2Done);
  const sublineEl = this.querySelector(".jw-free-shipping-bar__subline");
  if (sublineEl && state.shippingUnlocked) {
    sublineEl.remove();
  }
  if (state.reached) {
    this.setAttribute("role", "status");
    this.removeAttribute("aria-valuemin");
    this.removeAttribute("aria-valuemax");
    this.removeAttribute("aria-valuenow");
    const reachedMsg = this.getAttribute("reached-message") || "You've unlocked 2 free gifts!";
    messageElement.innerHTML = reachedMsg;
    this.setAttribute("aria-label", reachedMsg.replace(/<[^>]+>/g, ""));
    if (!wasReached || freeTier > prevFreeTier) {
      __jwCelebrateFreeShippingBar(this);
    }
  } else {
    this.setAttribute("role", "progressbar");
    this.setAttribute("aria-valuemin", "0");
    this.setAttribute("aria-valuemax", "100");
    this.setAttribute("aria-valuenow", String(Math.round(state.progress * 100)));
    this.setAttribute("aria-label", "Free product progress");
    this.classList.remove("jw-fsb--celebrate");
    if (freeTier > prevFreeTier) {
      __jwCelebrateFreeShippingBar(this);
    }
    var templateAttr = state.isClose
      ? state.freeRemaining >= 2 ? "unreached-message-close-two" : "unreached-message-close"
      : state.phase === "tier2"
        ? state.remaining === 1 ? "unreached-message-tier2-one" : "unreached-message-tier2"
        : state.paid > 0 ? "unreached-message-one" : "unreached-message-far";
    var template = this.getAttribute(templateAttr) || this.getAttribute("unreached-message");
    if (template && template.indexOf("{{") !== -1) {
      var countToken = state.isClose ? state.freeRemaining || 1 : state.remaining || __jwFsTier1Paid;
      messageElement.innerHTML = template.replace(/\{\{[^}]+\}\}/g, "<strong>" + countToken + "</strong>");
    } else {
      messageElement.innerHTML = __jwFsBarMessageHtml(state);
    }
  }
};
onCartChanged_fn = function(event) {
  const cart = event.detail && event.detail.cart;
  if (!cart || !Array.isArray(cart.items)) {
    return;
  }
  var protectionVariantId = "";
  try {
    var host = document.getElementById("cart-drawer");
    if (host) protectionVariantId = host.getAttribute("data-jw-protection-variant-id") || "";
  } catch (e) {
  }
  var state = __jwFsCountCartProgress(cart.items, protectionVariantId, __privateGet(this, _threshold));
  this.paidCount = state.paid;
  this.freeCount = state.free;
};
if (!window.customElements.get("free-shipping-bar")) {
  window.customElements.define("free-shipping-bar", FreeShippingBar);
}

// js/common/cart/line-item-quantity.js
import { Delegate } from "vendor";
var _lineItemMutationStates, _mainCartMutationQueue, _mainCartMutationSequence, _delegate, _LineItemQuantity_instances, onQuantityChanged_fn, onQuantityKeydown_fn, onChangeLinkClicked_fn, changeLineItemQuantity_fn;
var LineItemQuantity = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _LineItemQuantity_instances);
    __privateAdd(this, _delegate, new Delegate(this));
    __privateGet(this, _delegate).on("change", "[data-line-key]", __privateMethod(this, _LineItemQuantity_instances, onQuantityChanged_fn).bind(this));
    __privateGet(this, _delegate).on("keydown", "[data-line-key]", __privateMethod(this, _LineItemQuantity_instances, onQuantityKeydown_fn).bind(this));
    __privateGet(this, _delegate).on("click", '[href*="/cart/change"]', __privateMethod(this, _LineItemQuantity_instances, onChangeLinkClicked_fn).bind(this));
  }
};
_delegate = new WeakMap();
_lineItemMutationStates = /* @__PURE__ */ new Map();
_mainCartMutationQueue = Promise.resolve();
_mainCartMutationSequence = 0;
_LineItemQuantity_instances = new WeakSet();
onQuantityChanged_fn = function(event, target) {
  __privateMethod(this, _LineItemQuantity_instances, changeLineItemQuantity_fn).call(this, target.getAttribute("data-line-key"), parseInt(target.value));
};
onQuantityKeydown_fn = function(event, target) {
  if (event.key !== "Enter") return;
  event.preventDefault();
  target.blur();
};
onChangeLinkClicked_fn = function(event, target) {
  event.preventDefault();
  const url = new URL(target.href);
  const lineKey = url.searchParams.get("id");
  const input = this.querySelector(`[data-line-key="${CSS.escape(lineKey)}"]`);
  const currentQuantity = parseInt(input?.value, 10) || 0;
  const linkedQuantity = parseInt(url.searchParams.get("quantity"), 10);
  const increment = Math.max(1, parseInt(input?.step, 10) || Math.abs(linkedQuantity - currentQuantity) || 1);
  const buttons = Array.from(target.closest("quantity-selector")?.querySelectorAll('[href*="/cart/change"]') || []);
  const delta = target === buttons[buttons.length - 1] ? increment : -increment;
  const pendingQuantity = _lineItemMutationStates.get(lineKey)?.targetQuantity;
  const targetQuantity = Math.max(0, (pendingQuantity ?? currentQuantity) + delta);
  if (input) input.value = String(targetQuantity);
  __privateMethod(this, _LineItemQuantity_instances, changeLineItemQuantity_fn).call(this, lineKey, targetQuantity);
};
changeLineItemQuantity_fn = function(lineKey, targetQuantity) {
  const owner = this;
  const lineItem = this.closest("line-item");
  const isCartPage = window.themeVariables.settings.pageType === "cart";
  const cartSection = isCartPage ? owner.closest(".shopify-section") : null;
  const cartSectionId = cartSection ? extractSectionId(cartSection) : "";
  const shouldRestoreFocus = owner.contains(document.activeElement);
  lineItem?.dispatchEvent(new CustomEvent("line-item:will-change", { bubbles: true, detail: { targetQuantity } }));
  const previousState = _lineItemMutationStates.get(lineKey);
  const sequence = isCartPage ? ++_mainCartMutationSequence : (previousState?.sequence || 0) + 1;
  const state = {
    sequence,
    targetQuantity,
    queue: isCartPage ? _mainCartMutationQueue : previousState?.queue || Promise.resolve()
  };
  _lineItemMutationStates.set(lineKey, state);
  const isLatestIntent = () => isCartPage ? _mainCartMutationSequence === sequence : _lineItemMutationStates.get(lineKey)?.sequence === sequence;
  state.queue = state.queue.catch(() => {
  }).then(async () => {
    owner.setAttribute("aria-busy", "true");
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  let sectionsToBundle = [];
  document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
    if (cartSectionId && !sectionsToBundle.includes(cartSectionId)) sectionsToBundle.push(cartSectionId);
    try {
  const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
        body: JSON.stringify({
          id: lineKey,
          quantity: targetQuantity,
          sections: sectionsToBundle.join(","),
          sections_url: window.location.pathname + window.location.search
        })
      });
  if (!response.ok) {
        const responseContent = await response.json().catch(() => ({}));
        if (!isLatestIntent()) return;
        owner.parentElement?.querySelector('[role="alert"]')?.remove();
    const errorSvg = `<svg width="13" height="13" fill="none" viewBox="0 0 13 13">
        <circle cx="6.5" cy="6.5" r="6.5" fill="#BF1515"/>
        <path fill="#fff" d="M6.75 7.97a.387.387 0 0 1-.3-.12.606.606 0 0 1-.12-.34l-.3-3.82c-.02-.247.033-.443.16-.59.127-.153.313-.23.56-.23.24 0 .42.077.54.23.127.147.18.343.16.59l-.3 3.82a.522.522 0 0 1-.12.34.344.344 0 0 1-.28.12Zm0 2.08a.744.744 0 0 1-.55-.21.751.751 0 0 1-.2-.54c0-.213.067-.387.2-.52.14-.14.323-.21.55-.21.233 0 .413.07.54.21.133.133.2.307.2.52 0 .22-.067.4-.2.54-.127.14-.307.21-.54.21Z"/>
      </svg>`;
        owner.insertAdjacentHTML("afterend", `<p class="h-stack gap-2 justify-center text-xs" role="alert">${errorSvg} ${responseContent["description"] || "Could not update quantity"}</p>`);
        try {
          owner.querySelector("quantity-selector")?.restoreDefaultValue();
        } catch (error) {
        }
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        return;
      }
    const cartContent = await response.json();
      if (!isLatestIntent()) return;
      if (isCartPage && cartSectionId && cartContent.sections?.[cartSectionId]) {
        const sectionDocument = new DOMParser().parseFromString(cartContent.sections[cartSectionId], "text/html");
        const replacement = sectionDocument.querySelector(`#shopify-section-${CSS.escape(cartSectionId)}`) || sectionDocument.body.firstElementChild;
        if (replacement && cartSection?.isConnected) {
          const insertedSection = document.importNode(replacement, true);
          cartSection.replaceWith(insertedSection);
          if (shouldRestoreFocus) {
            requestAnimationFrame(() => {
              const nextInput = insertedSection.querySelector(`[data-line-key="${CSS.escape(lineKey)}"]`);
              (nextInput || insertedSection.querySelector('button[name="checkout"], a[href*="/collections"]'))?.focus({ preventScroll: true });
            });
          }
        }
      }
      const lineItemAfterChange = cartContent["items"].filter((lineItem2) => lineItem2["key"] === lineKey);
    lineItem?.dispatchEvent(
      new CustomEvent("line-item:change", {
        bubbles: true,
        detail: {
            quantity: lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0]["quantity"],
          cart: cartContent
        }
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "line-item:change",
          cart: cartContent
        }
      })
    );
    } catch (error) {
      if (isLatestIntent()) {
        try {
          owner.querySelector("quantity-selector")?.restoreDefaultValue();
        } catch (restoreError) {
        }
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
    } finally {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      if (owner.isConnected) owner.removeAttribute("aria-busy");
      if (_lineItemMutationStates.get(lineKey) === state) {
        _lineItemMutationStates.delete(lineKey);
      }
    }
  });
  if (isCartPage) _mainCartMutationQueue = state.queue;
  return state.queue;
};
if (!window.customElements.get("line-item-quantity")) {
  window.customElements.define("line-item-quantity", LineItemQuantity);
}
// js/common/cart/shipping-estimator.js
var _estimateShippingListener, _ShippingEstimator_instances, estimateShipping_fn, getAsyncShippingRates_fn, formatShippingRates_fn, formatError_fn;
var ShippingEstimator = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ShippingEstimator_instances);
    __privateAdd(this, _estimateShippingListener, __privateMethod(this, _ShippingEstimator_instances, estimateShipping_fn).bind(this));
  }
  connectedCallback() {
    this.submitButton = this.querySelector('[type="button"]');
    this.resultsElement = this.querySelector('[aria-live="polite"]');
    this.submitButton.addEventListener("click", __privateGet(this, _estimateShippingListener));
  }
  disconnectedCallback() {
    this.submitButton.removeEventListener("click", __privateGet(this, _estimateShippingListener));
  }
};
_estimateShippingListener = new WeakMap();
_ShippingEstimator_instances = new WeakSet();
estimateShipping_fn = async function(event) {
  event.preventDefault();
  const zip = this.querySelector('[name="address[zip]"]').value, country = this.querySelector('[name="address[country]"]').value, province = this.querySelector('[name="address[province]"]').value;
  this.submitButton.setAttribute("aria-busy", "true");
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  const prepareResponse = await fetch(
    `${Shopify.routes.root}cart/prepare_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`,
    { method: "POST" }
  );
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
  if (prepareResponse.ok) {
    const shippingRates = await __privateMethod(this, _ShippingEstimator_instances, getAsyncShippingRates_fn).call(this, zip, country, province);
    __privateMethod(this, _ShippingEstimator_instances, formatShippingRates_fn).call(this, shippingRates);
  } else {
    const jsonError = await prepareResponse.json();
    __privateMethod(this, _ShippingEstimator_instances, formatError_fn).call(this, jsonError);
  }
  this.resultsElement.hidden = false;
  this.submitButton.removeAttribute("aria-busy");
};
getAsyncShippingRates_fn = async function(zip, country, province) {
  const response = await fetch(
    `${Shopify.routes.root}cart/async_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`
  );
  const responseAsText = await response.text();
  if (responseAsText === "null") {
    return __privateMethod(this, _ShippingEstimator_instances, getAsyncShippingRates_fn).call(this, zip, country, province);
  } else {
    return JSON.parse(responseAsText)["shipping_rates"];
  }
};
formatShippingRates_fn = function(shippingRates) {
  let formattedShippingRates = shippingRates.map((shippingRate) => {
    return `<li>${shippingRate["presentment_name"]}: ${shippingRate["currency"]} ${shippingRate["price"]}</li>`;
  });
  this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${shippingRates.length === 0 ? window.themeVariables.strings.shippingEstimatorNoResults : shippingRates.length === 1 ? window.themeVariables.strings.shippingEstimatorOneResult : window.themeVariables.strings.shippingEstimatorMultipleResults}</p>
        ${formattedShippingRates === "" ? "" : `<ul class="list-disc" role="list">${formattedShippingRates}</ul>`}
      </div>
    `;
};
formatError_fn = function(errors) {
  let formattedShippingRates = Object.keys(errors).map((errorKey) => {
    return `<li>${errors[errorKey]}</li>`;
  }).join("");
  this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${window.themeVariables.strings.shippingEstimatorError}</p>
        <ul class="list-disc" role="list">${formattedShippingRates}</ul>
      </div>
    `;
};
if (!window.customElements.get("shipping-estimator")) {
  window.customElements.define("shipping-estimator", ShippingEstimator);
}

// js/common/facets/facets-form.js
var _isDirty, _FacetsForm_instances, form_get, buildUrl_fn, onFormChanged_fn, onFormSubmitted_fn;
var FacetsForm = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _FacetsForm_instances);
    __privateAdd(this, _isDirty, false);
    this.addEventListener("change", __privateMethod(this, _FacetsForm_instances, onFormChanged_fn));
    this.addEventListener("submit", __privateMethod(this, _FacetsForm_instances, onFormSubmitted_fn));
  }
};
_isDirty = new WeakMap();
_FacetsForm_instances = new WeakSet();
form_get = function() {
  return this.querySelector("form");
};
buildUrl_fn = function() {
  const searchParams = new URLSearchParams(new FormData(__privateGet(this, _FacetsForm_instances, form_get))), url = new URL(__privateGet(this, _FacetsForm_instances, form_get).action);
  url.search = "";
  searchParams.forEach((value, key) => url.searchParams.append(key, value));
  ["page", "filter.v.price.gte", "filter.v.price.lte"].forEach((optionToClear) => {
    if (url.searchParams.get(optionToClear) === "") {
      url.searchParams.delete(optionToClear);
    }
  });
  url.searchParams.set("section_id", this.getAttribute("section-id"));
  return url;
};
onFormChanged_fn = function() {
  __privateSet(this, _isDirty, true);
  if (this.hasAttribute("update-on-change")) {
    if (HTMLFormElement.prototype.requestSubmit) {
      __privateGet(this, _FacetsForm_instances, form_get).requestSubmit();
    } else {
      __privateGet(this, _FacetsForm_instances, form_get).dispatchEvent(new Event("submit", { cancelable: true }));
    }
  } else {
    cachedFetch(__privateMethod(this, _FacetsForm_instances, buildUrl_fn).call(this).toString());
  }
};
onFormSubmitted_fn = function(event) {
  event.preventDefault();
  if (!__privateGet(this, _isDirty)) {
    return;
  }
  this.dispatchEvent(
    new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url: __privateMethod(this, _FacetsForm_instances, buildUrl_fn).call(this)
      }
    })
  );
  __privateSet(this, _isDirty, false);
};
if (!window.customElements.get("facets-form")) {
  window.customElements.define("facets-form", FacetsForm);
}

// js/common/overlay/dialog-element.js
import { animate as animate4, FocusTrap, Delegate as Delegate2 } from "vendor";
var lockLayerCount = 0;
var lockScrollY = 0;
var __jwApplyScrollLock = function(enable) {
  // Mobile browsers (esp. iOS) jump to scrollY=0 when overflow:hidden is applied while
  // scrolled. Pin the document with position:fixed + negative top, then restore on unlock.
  if (enable) {
    if (lockLayerCount === 0) {
      lockScrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.style.setProperty("--theme-lock-top", `-${lockScrollY}px`);
      document.documentElement.classList.add("lock");
    }
    lockLayerCount += 1;
    return;
  }
  lockLayerCount = Math.max(0, lockLayerCount - 1);
  if (lockLayerCount > 0) {
    document.documentElement.classList.add("lock");
    return;
  }
  document.documentElement.classList.remove("lock");
  document.documentElement.style.removeProperty("--theme-lock-top");
  window.scrollTo(0, lockScrollY);
};
var _isLocked, _delegate2, _abortController5, _focusTrap, _originalParentBeforeAppend, _DialogElement_instances, allowOutsideClick_fn, allowOutsideClickTouch_fn, allowOutsideClickMouse_fn, onToggleClicked_fn, updateSlotVisibility_fn;
var DialogElement = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _DialogElement_instances);
    __privateAdd(this, _isLocked, false);
    __privateAdd(this, _delegate2, new Delegate2(document.body));
    __privateAdd(this, _abortController5);
    __privateAdd(this, _focusTrap);
    __privateAdd(this, _originalParentBeforeAppend);
    if (this.shadowDomTemplate) {
      this.attachShadow({ mode: "open" }).appendChild(document.getElementById(this.shadowDomTemplate).content.cloneNode(true));
      this.shadowRoot.addEventListener("slotchange", (event) => __privateMethod(this, _DialogElement_instances, updateSlotVisibility_fn).call(this, event.target));
    }
    this.addEventListener("dialog:force-close", (event) => {
      this.hide();
      event.stopPropagation();
    });
  }
  static get observedAttributes() {
    return ["id", "open"];
  }
  connectedCallback() {
    if (this.id) {
      __privateGet(this, _delegate2).off().on("click", `[aria-controls="${this.id}"]`, __privateMethod(this, _DialogElement_instances, onToggleClicked_fn).bind(this));
    }
    __privateSet(this, _abortController5, new AbortController());
    this.setAttribute("role", "dialog");
    if (this.shadowDomTemplate) {
      this.getShadowPartByName("overlay")?.addEventListener("click", this.hide.bind(this), { signal: this.abortController.signal });
      Array.from(this.shadowRoot.querySelectorAll("slot")).forEach((slot) => __privateMethod(this, _DialogElement_instances, updateSlotVisibility_fn).call(this, slot));
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.show(!event.detail.load), { signal: this.abortController.signal });
      this.addEventListener("shopify:block:deselect", this.hide, { signal: this.abortController.signal });
      this._shopifySection = this._shopifySection || this.closest(".shopify-section");
      if (this._shopifySection) {
        if (this.hasAttribute("handle-editor-events")) {
          this._shopifySection.addEventListener("shopify:section:select", (event) => this.show(!event.detail.load), { signal: this.abortController.signal });
          this._shopifySection.addEventListener("shopify:section:deselect", this.hide.bind(this), { signal: this.abortController.signal });
        }
        this._shopifySection.addEventListener("shopify:section:unload", () => this.remove(), { signal: this.abortController.signal });
      }
    }
  }
  disconnectedCallback() {
    __privateGet(this, _delegate2).off();
    this.abortController.abort();
    this.focusTrap?.deactivate({ onDeactivate: () => {
    } });
    if (__privateGet(this, _isLocked)) {
      __privateSet(this, _isLocked, false);
      __jwApplyScrollLock(false);
    }
  }
  /**
   * Open the dialog element (the animation can be disabled by passing false as an argument). This function should
   * normally not be directly overriden on children classes
   */
  show(animate27 = true) {
    if (this.open) {
      return Promise.resolve();
    }
    this.setAttribute("open", animate27 ? "" : "immediate");
    return waitForEvent(this, "dialog:after-show");
  }
  /**
   * Hide the dialog element. This function should normally not be directly overriden on children classes
   */
  hide() {
    if (!this.open) {
      return Promise.resolve();
    }
    this.removeAttribute("open");
    return waitForEvent(this, "dialog:after-hide");
  }
  /**
   * Get the abort controller used to clean listeners. You can retrieve it in children classes to add your own listeners
   * that will be cleaned when the element is removed or re-rendered
   */
  get abortController() {
    return __privateGet(this, _abortController5);
  }
  /**
   * Get all the elements controlling this dialog (typically, button). An element controls this dialog if it has an
   * aria-controls attribute matching the ID of this dialog element
   */
  get controls() {
    return Array.from(this.getRootNode().querySelectorAll(`[aria-controls="${this.id}"]`));
  }
  /**
   * Returns if the dialog is open or closed
   */
  get open() {
    return this.hasAttribute("open");
  }
  /**
   * If true is returned, then FocusTrap will activate and manage all the focus management. This is required for good
   * accessibility (such as keyboard management) and should normally not be set to false in children classes unless
   * there is a very good reason to do so
   */
  get shouldTrapFocus() {
    return true;
  }
  /**
   * When the dialog focus is trapped, define if the page is lock (not scrollable). This is usually desirable on
   * full screen modals
   */
  get shouldLock() {
    return false;
  }
  /**
   * By default, when the focus is trapped on an element, a click outside the trapped element close it. Sometimes, it
   * may be desirable to turn off all interactions so that all clicks outside don't do anything
   */
  get clickOutsideDeactivates() {
    return true;
  }
  /**
   * Sometimes (especially for drawer) we need to ensure that an element is on top of everything else. To do that,
   * we need to move the element to the body. We are doing that on open, and then restore the initial position on
   * close
   */
  get shouldAppendToBody() {
    return false;
  }
  /**
   * Decide which element to focus first when the dialog focus is trapped. By default, the first focusable element
   * will be focused, but this can be overridden by passing a selector in the "initial-focus" attribute
   */
  get initialFocus() {
    return this.hasAttribute("initial-focus") ? this.getAttribute("initial-focus") === "false" ? false : this.querySelector(this.getAttribute("initial-focus")) : this.hasAttribute("tabindex") ? this : this.querySelector('input:not([type="hidden"])') || false;
  }
  /**
   * If set to true, then focus trap will not automatically scroll to the first focused element, which can cause
   * annoying experience.
   */
  get preventScrollWhenTrapped() {
    return true;
  }
  /**
   * Get the focus trap element configured with all the other attributes
   */
  get focusTrap() {
    return __privateSet(this, _focusTrap, __privateGet(this, _focusTrap) || new FocusTrap.createFocusTrap(this, {
      onDeactivate: this.hide.bind(this),
      allowOutsideClick: this.clickOutsideDeactivates ? __privateMethod(this, _DialogElement_instances, allowOutsideClick_fn).bind(this) : false,
      initialFocus: matchesMediaQuery("supports-hover") ? this.initialFocus : false,
      fallbackFocus: this,
      tabbableOptions: {
        getShadowRoot: true
      },
      preventScroll: this.preventScrollWhenTrapped
    }));
  }
  /**
   * Get the ShadowDOM template (if any). If there is one defined, the dialog automatically constructs it with the
   * shadow DOM
   */
  get shadowDomTemplate() {
    return this.getAttribute("template");
  }
  /**
   * For dialog that use Shadow DOM, this allows a quick retrieval of parts by name
   */
  getShadowPartByName(name) {
    return this.shadowRoot?.querySelector(`[part="${name}"]`);
  }
  /**
   * Callback called when attributes changes. This is the part that glues everything
   */
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "open":
        this.controls.forEach((toggle) => toggle.setAttribute("aria-expanded", newValue === null ? "false" : "true"));
        if (oldValue === null && (newValue === "" || newValue === "immediate")) {
          __privateSet(this, _originalParentBeforeAppend, null);
          this.style.setProperty("display", "block");
          this.dispatchEvent(new CustomEvent("dialog:before-show"));
          if (this.shouldAppendToBody && this.parentElement !== document.body) {
            __privateSet(this, _originalParentBeforeAppend, this.parentElement);
            document.body.append(this);
          }
          const animationControls = this.createEnterAnimationControls();
          if (newValue === "immediate") {
            animationControls.finish();
          }
          animationControls.finished.then(() => {
            this.dispatchEvent(new CustomEvent("dialog:after-show"));
          });
          if (this.shouldTrapFocus) {
            this.focusTrap.activate({
              checkCanFocusTrap: () => animationControls.finished
            });
          }
          if (this.shouldLock) {
            __privateSet(this, _isLocked, true);
            __jwApplyScrollLock(true);
          }
        } else if (oldValue !== null && newValue === null) {
          this.dispatchEvent(new CustomEvent("dialog:before-hide"));
          const hideTransitionPromise = this.createLeaveAnimationControls().finished;
          hideTransitionPromise.then(() => {
            this.style.setProperty("display", "none");
            if (this.parentElement === document.body && __privateGet(this, _originalParentBeforeAppend)) {
              __privateGet(this, _originalParentBeforeAppend).appendChild(this);
              __privateSet(this, _originalParentBeforeAppend, null);
            }
            this.dispatchEvent(new CustomEvent("dialog:after-hide"));
          });
          this.focusTrap?.deactivate({
            checkCanReturnFocus: () => hideTransitionPromise
          });
          if (this.shouldLock) {
            __privateSet(this, _isLocked, false);
            __jwApplyScrollLock(false);
          }
        }
        this.dispatchEvent(new CustomEvent("toggle", { bubbles: true }));
        break;
    }
  }
  /**
   * Create the animation controls for the enter animation
   */
  createEnterAnimationControls() {
    return animate4(this, {}, { duration: 0 });
  }
  /**
   * Create the animation controls for the leave animation
   */
  createLeaveAnimationControls() {
    return animate4(this, {}, { duration: 0 });
  }
  /**
   * When "clickOutsideDeactivates" is true, this method is called on the final click destination. If this method
   * returns true, then the dialog closes (if false, the dialog remains in its current state). By default, this
   * will close the dialog if a click is done outside the dialog. However, this may be overridden in children classes
   * to provide custom behavior (for instance, to only allow some elements to close the dialog)
   */
  hideForOutsideClickTarget(target) {
    return !this.contains(target);
  }
  /**
   * When "clickOutsideDeactivates" is set to true, this method allows to control which element, when clicked, allows
   * to pass-through and have its behavior being executed
   */
  allowOutsideClickForTarget(target) {
    return false;
  }
};
_isLocked = new WeakMap();
_delegate2 = new WeakMap();
_abortController5 = new WeakMap();
_focusTrap = new WeakMap();
_originalParentBeforeAppend = new WeakMap();
_DialogElement_instances = new WeakSet();
/**
 * If "clickOutsideDeactivates" is true, then this listener will be called on every click outside the element. This
 * allows function separates touch and non-touch events
 */
allowOutsideClick_fn = function(event) {
  if ("TouchEvent" in window && event instanceof TouchEvent) {
    return __privateMethod(this, _DialogElement_instances, allowOutsideClickTouch_fn).call(this, event);
  } else {
    return __privateMethod(this, _DialogElement_instances, allowOutsideClickMouse_fn).call(this, event);
  }
};
/**
 * If "clickOutsideDeactivates" is true, this listener will be called on every touch click outside the trapped
 * element. By default, this will allow any click outside to cause the dialog to close
 */
allowOutsideClickTouch_fn = function(event) {
  event.target.addEventListener(
    "touchend",
    (subEvent) => {
      const endTarget = document.elementFromPoint(subEvent.changedTouches.item(0).clientX, subEvent.changedTouches.item(0).clientY);
      if (this.hideForOutsideClickTarget(endTarget)) {
        this.hide();
      }
    },
    { once: true, signal: this.abortController.signal }
  );
  return this.allowOutsideClickForTarget(event.target);
};
/**
 * If "clickOutsideDeactivates" is true, this listener will be called on every mouse click outside the trapped
 * element. By default, this will allow any click outside to cause the dialog to close.
 */
allowOutsideClickMouse_fn = function(event) {
  if (event.type !== "click") {
    return false;
  }
  if (this.hideForOutsideClickTarget(event.target)) {
    this.hide();
  }
  if (this.allowOutsideClickForTarget(event.target)) {
    return true;
  }
  let target = event.target, closestControl = event.target.closest("[aria-controls]");
  if (closestControl && closestControl.getAttribute("aria-controls") === this.id) {
    target = closestControl;
  }
  return this.id !== target.getAttribute("aria-controls");
};
/**
 * This function is called whenever a toggle (an element controlling this dialog) is called. This simply open
 * the dialog if closed, or close it if open
 */
onToggleClicked_fn = function(event) {
  event?.preventDefault();
  this.open ? this.hide() : this.show();
};
/**
 * Hide the slots that do not have any children
 */
updateSlotVisibility_fn = function(slot) {
  if (!["header", "footer"].includes(slot.name)) {
    return;
  }
  slot.parentElement.hidden = slot.assignedElements({ flatten: true }).length === 0;
};
var DialogCloseButton = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("dialog:force-close", { bubbles: true, cancelable: true, composed: true })));
  }
};
if (!window.customElements.get("dialog-element")) {
  window.customElements.define("dialog-element", DialogElement);
}
if (!window.customElements.get("dialog-close-button")) {
  window.customElements.define("dialog-close-button", DialogCloseButton);
}

// js/common/overlay/drawer.js
import { timeline as timeline3 } from "vendor";

// js/common/overlay/modal.js
import { animate as animate5, timeline as timeline2 } from "vendor";
var Modal = class extends DialogElement {
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("aria-modal", "true");
  }
  get shadowDomTemplate() {
    return this.getAttribute("template") || "modal-default-template";
  }
  get shouldLock() {
    return true;
  }
  get shouldAppendToBody() {
    return true;
  }
  createEnterAnimationControls() {
    if (matchesMediaQuery("sm")) {
      return animate5(this, { opacity: [0, 1] }, { duration: 0.2 });
    } else {
      return timeline2([
        [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] }],
        [this.getShadowPartByName("content"), { transform: ["translateY(100%)", "translateY(0)"] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1], at: "<" }]
      ]);
    }
  }
  createLeaveAnimationControls() {
    if (matchesMediaQuery("sm")) {
      return animate5(this, { opacity: [1, 0] }, { duration: 0.2 });
    } else {
      return timeline2([
        [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] }],
        [this.getShadowPartByName("content"), { transform: ["translateY(0)", "translateY(100%)"] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1], at: "<" }]
      ]);
    }
  }
};
if (!window.customElements.get("x-modal")) {
  window.customElements.define("x-modal", Modal);
}

// js/common/overlay/drawer.js
var Drawer = class extends Modal {
  get shadowDomTemplate() {
    return this.getAttribute("template") || "drawer-default-template";
  }
  get openFrom() {
    return this.getAttribute("open-from") || "right";
  }
  createEnterAnimationControls() {
    this.getShadowPartByName("content").style.marginInlineStart = this.openFrom === "right" ? "auto" : 0;
    return timeline3([
      [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] }],
      [
        this.getShadowPartByName("content"),
        { transform: [`translateX(calc(var(--transform-logical-flip) * ${this.openFrom === "right" ? "100%" : "-100%"}))`, "translateX(0)"] },
        { duration: 0.3, at: "<", easing: [0.645, 0.045, 0.355, 1] }
      ]
    ]);
  }
  createLeaveAnimationControls() {
    return timeline3([
      [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] }],
      [
        this.getShadowPartByName("content"),
        { transform: ["translateX(0)", `translateX(calc(var(--transform-logical-flip) * ${this.openFrom === "right" ? "100%" : "-100%"}))`] },
        { duration: 0.3, at: "<", easing: [0.645, 0.045, 0.355, 1] }
      ]
    ]);
  }
};
if (!window.customElements.get("x-drawer")) {
  window.customElements.define("x-drawer", Drawer);
}

// js/common/overlay/popin.js
import { animate as animate6 } from "vendor";
var PopIn = class extends DialogElement {
  get shouldTrapFocus() {
    return false;
  }
  createEnterAnimationControls() {
    return animate6(this, { opacity: [0, 1], transform: ["translateY(25px)", "translateY(0)"] }, { duration: 0.4, easing: [0.645, 0.045, 0.355, 1] });
  }
  createLeaveAnimationControls() {
    return animate6(this, { opacity: [1, 0], transform: ["translateY(0)", "translateY(25px)"] }, { duration: 0.4, easing: [0.645, 0.045, 0.355, 1] });
  }
};
if (!window.customElements.get("pop-in")) {
  window.customElements.define("pop-in", PopIn);
}

// js/common/overlay/popover.js
import { animate as animate7, timeline as timeline4 } from "vendor";
var Popover = class extends DialogElement {
  connectedCallback() {
    super.connectedCallback();
    this.controls.forEach((control) => control.setAttribute("aria-haspopup", "dialog"));
    if (this.hasAttribute("close-on-listbox-select")) {
      this.addEventListener("listbox:select", this.hide, { signal: this.abortController.signal });
    }
    if (this.hasAttribute("close-on-listbox-change")) {
      this.addEventListener("change", this.hide, { signal: this.abortController.signal });
    }
  }
  get shadowDomTemplate() {
    return this.getAttribute("template") || "popover-default-template";
  }
  get shouldLock() {
    return matchesMediaQuery("md-max");
  }
  get shouldAppendToBody() {
    return matchesMediaQuery("md-max");
  }
  get preventScrollWhenTrapped() {
    return true;
  }
  createEnterAnimationControls() {
    if (matchesMediaQuery("md")) {
      return animate7(this, { opacity: [0, 1] }, { duration: 0.2 });
    } else {
      return timeline4([
        [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] }],
        [this.getShadowPartByName("content"), { transform: ["translateY(100%)", "translateY(0)"] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1], at: "<" }]
      ]);
    }
  }
  createLeaveAnimationControls() {
    if (matchesMediaQuery("md")) {
      return animate7(this, { opacity: [1, 0] }, { duration: 0.2 });
    } else {
      return timeline4([
        [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] }],
        [this.getShadowPartByName("content"), { transform: ["translateY(0)", "translateY(100%)"] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1], at: "<" }]
      ]);
    }
  }
};
if (!window.customElements.get("x-popover")) {
  window.customElements.define("x-popover", Popover);
}

// js/common/facets/facets-drawer.js
var _FacetsDrawer_instances, updateFacets_fn;
var FacetsDrawer = class extends Drawer {
  constructor() {
    super();
    __privateAdd(this, _FacetsDrawer_instances);
    this.addEventListener("dialog:after-hide", __privateMethod(this, _FacetsDrawer_instances, updateFacets_fn));
  }
};
_FacetsDrawer_instances = new WeakSet();
updateFacets_fn = function() {
  const form = this.querySelector("facets-form form");
  if (HTMLFormElement.prototype.requestSubmit) {
    form?.requestSubmit();
  } else {
    form?.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
  }
};
if (!window.customElements.get("facets-drawer")) {
  window.customElements.define("facets-drawer", FacetsDrawer);
}

// js/common/facets/facet-link.js
var _FacetLink_instances, onFacetUpdate_fn;
var FacetLink = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _FacetLink_instances);
    this.addEventListener("click", __privateMethod(this, _FacetLink_instances, onFacetUpdate_fn).bind(this));
  }
};
_FacetLink_instances = new WeakSet();
onFacetUpdate_fn = function(event) {
  event.preventDefault();
  const sectionId = extractSectionId(event.target), url = new URL(this.firstElementChild.href);
  url.searchParams.set("section_id", sectionId);
  this.dispatchEvent(
    new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url
      }
    })
  );
};
if (!window.customElements.get("facet-link")) {
  window.customElements.define("facet-link", FacetLink);
}

// js/common/facets/facets-sort-popover.js
var _FacetsSortPopover_instances, onSortChange_fn;
var FacetsSortPopover = class extends Popover {
  constructor() {
    super();
    __privateAdd(this, _FacetsSortPopover_instances);
    this.addEventListener("listbox:change", __privateMethod(this, _FacetsSortPopover_instances, onSortChange_fn));
  }
};
_FacetsSortPopover_instances = new WeakSet();
onSortChange_fn = function(event) {
  const url = new URL(window.location.href);
  url.searchParams.set("sort_by", event.detail.value);
  url.searchParams.delete("page");
  url.searchParams.set("section_id", this.getAttribute("section-id"));
  this.dispatchEvent(
    new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url
      }
    })
  );
};
if (!window.customElements.get("facets-sort-popover")) {
  window.customElements.define("facets-sort-popover", FacetsSortPopover);
}

// js/common/facets/facets-listeners.js
import { Delegate as Delegate3 } from "vendor";
var abortController = null;
var delegate = new Delegate3(document.body);
var openDetailsValues = new Set(Array.from(document.querySelectorAll('facets-form details[open] input[name*="filter."]'), (item) => item.name));
delegate.on(
  "toggle",
  "facets-form details",
  (event, detailsElement) => {
    const inputNames = [...new Set(Array.from(detailsElement.querySelectorAll('input[name*="filter."]'), (item) => item.name))];
    inputNames.forEach((inputName) => {
      detailsElement.open ? openDetailsValues.add(inputName) : openDetailsValues.delete(inputName);
    });
  },
  true
);
document.addEventListener("facet:update", async (event) => {
  if (abortController) {
    abortController.abort();
  }
  abortController = new AbortController();
  const url = event.detail.url, shopifySection = document.getElementById(`shopify-section-${url.searchParams.get("section_id")}`);
  const clonedUrl = new URL(url);
  clonedUrl.searchParams.delete("section_id");
  history.replaceState({}, "", clonedUrl.toString());
  try {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    const tempContent = new DOMParser().parseFromString(await (await cachedFetch(url.toString(), { signal: abortController.signal })).text(), "text/html");
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    const newShopifySection = tempContent.querySelector(".shopify-section");
    newShopifySection.querySelectorAll("facets-form details").forEach((detailsElement) => {
      const inputNames = [...new Set(Array.from(detailsElement.querySelectorAll('input[name*="filter."]'), (item) => item.name))];
      inputNames.forEach((inputName) => {
        detailsElement.open = openDetailsValues.has(inputName);
      });
    });
    const focusedElement = document.activeElement;
    shopifySection.replaceChildren(...document.importNode(tempContent.querySelector(".shopify-section"), true).childNodes);
    if (focusedElement?.id && document.getElementById(focusedElement.id)) {
      document.getElementById(focusedElement.id).focus();
    }
    const scrollToProductList = () => shopifySection.querySelector(".collection").scrollIntoView({ block: "start", behavior: "smooth" });
    if ("requestIdleCallback" in window) {
      requestIdleCallback(scrollToProductList, { timeout: 500 });
    } else {
      requestAnimationFrame(scrollToProductList);
    }
  } catch (e) {
  }
});

// js/common/feedback/progress-bar.js
import { inView as inView5 } from "vendor";
var _allowUpdatingProgress, _ProgressBar_instances, calculateProgressBar_fn;
var ProgressBar = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProgressBar_instances);
    __privateAdd(this, _allowUpdatingProgress, !this.hasAttribute("animate-on-scroll"));
  }
  static get observedAttributes() {
    return ["aria-valuenow", "aria-valuemax"];
  }
  connectedCallback() {
    if (this.hasAttribute("animate-on-scroll")) {
      inView5(this, () => {
        __privateSet(this, _allowUpdatingProgress, true);
        __privateMethod(this, _ProgressBar_instances, calculateProgressBar_fn).call(this);
      });
    }
  }
  get progress() {
    return Math.min(1, this.getAttribute("aria-valuenow") / this.getAttribute("aria-valuemax"));
  }
  set valueMax(value) {
    this.setAttribute("aria-valuemax", value);
  }
  set valueNow(value) {
    this.setAttribute("aria-valuenow", value);
  }
  attributeChangedCallback() {
    if (__privateGet(this, _allowUpdatingProgress)) {
      __privateMethod(this, _ProgressBar_instances, calculateProgressBar_fn).call(this);
    }
  }
};
_allowUpdatingProgress = new WeakMap();
_ProgressBar_instances = new WeakSet();
calculateProgressBar_fn = function() {
  this.style.setProperty("--progress", `${this.progress}`);
};
if (!window.customElements.get("progress-bar")) {
  window.customElements.define("progress-bar", ProgressBar);
}

// js/common/form/price-range.js
var PriceRange = class extends HTMLElement {
  #abortController;
  connectedCallback() {
    this.#abortController = new AbortController();
    const rangeLowerBound = this.querySelector('input[type="range"]:first-child'), rangeHigherBound = this.querySelector('input[type="range"]:last-child'), textInputLowerBound = this.querySelector('input[name="filter.v.price.gte"]'), textInputHigherBound = this.querySelector('input[name="filter.v.price.lte"]');
    textInputLowerBound.addEventListener("focus", () => textInputLowerBound.select(), { signal: this.#abortController.signal });
    textInputHigherBound.addEventListener("focus", () => textInputHigherBound.select(), { signal: this.#abortController.signal });
    textInputLowerBound.addEventListener(
      "change",
      (event) => {
        event.preventDefault();
        event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(textInputHigherBound.value || event.target.max) - 1), event.target.min);
        rangeLowerBound.value = event.target.value;
        rangeLowerBound.parentElement.style.setProperty("--range-min", `${parseInt(rangeLowerBound.value) / parseInt(rangeLowerBound.max) * 100}%`);
      },
      { signal: this.#abortController.signal }
    );
    textInputHigherBound.addEventListener(
      "change",
      (event) => {
        event.preventDefault();
        event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(textInputLowerBound.value || event.target.min) + 1), event.target.max);
        rangeHigherBound.value = event.target.value;
        rangeHigherBound.parentElement.style.setProperty("--range-max", `${parseInt(rangeHigherBound.value) / parseInt(rangeHigherBound.max) * 100}%`);
      },
      { signal: this.#abortController.signal }
    );
    rangeLowerBound.addEventListener(
      "change",
      (event) => {
        event.stopPropagation();
        textInputLowerBound.value = event.target.value;
        textInputLowerBound.dispatchEvent(new Event("change", { bubbles: true }));
      },
      { signal: this.#abortController.signal }
    );
    rangeHigherBound.addEventListener(
      "change",
      (event) => {
        event.stopPropagation();
        textInputHigherBound.value = event.target.value;
        textInputHigherBound.dispatchEvent(new Event("change", { bubbles: true }));
      },
      { signal: this.#abortController.signal }
    );
    rangeLowerBound.addEventListener(
      "input",
      (event) => {
        event.target.value = Math.min(parseInt(event.target.value), parseInt(textInputHigherBound.value || event.target.max) - 1);
        event.target.parentElement.style.setProperty("--range-min", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        textInputLowerBound.value = event.target.value;
      },
      { signal: this.#abortController.signal }
    );
    rangeHigherBound.addEventListener(
      "input",
      (event) => {
        event.target.value = Math.max(parseInt(event.target.value), parseInt(textInputLowerBound.value || event.target.min) + 1);
        event.target.parentElement.style.setProperty("--range-max", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        textInputHigherBound.value = event.target.value;
      },
      { signal: this.#abortController.signal }
    );
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
};
if (!window.customElements.get("price-range")) {
  window.customElements.define("price-range", PriceRange);
}

// js/common/form/quantity-selector.js
var _abortController6, _decreaseButton, _increaseButton, _inputElement, _QuantitySelector_instances, onDecreaseQuantity_fn, onIncreaseQuantity_fn, updateUI_fn;
var QuantitySelector = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _QuantitySelector_instances);
    __privateAdd(this, _abortController6);
    __privateAdd(this, _decreaseButton);
    __privateAdd(this, _increaseButton);
    __privateAdd(this, _inputElement);
  }
  connectedCallback() {
    __privateSet(this, _abortController6, new AbortController());
    __privateSet(this, _decreaseButton, this.querySelector("button:first-of-type"));
    __privateSet(this, _increaseButton, this.querySelector("button:last-of-type"));
    __privateSet(this, _inputElement, this.querySelector("input"));
    __privateGet(this, _decreaseButton)?.addEventListener("click", __privateMethod(this, _QuantitySelector_instances, onDecreaseQuantity_fn).bind(this), { signal: __privateGet(this, _abortController6).signal });
    __privateGet(this, _increaseButton)?.addEventListener("click", __privateMethod(this, _QuantitySelector_instances, onIncreaseQuantity_fn).bind(this), { signal: __privateGet(this, _abortController6).signal });
    __privateGet(this, _inputElement)?.addEventListener("input", () => __privateMethod(this, _QuantitySelector_instances, updateUI_fn).call(this), { signal: __privateGet(this, _abortController6).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController6).abort();
  }
  get quantity() {
    return __privateGet(this, _inputElement).value;
  }
  set quantity(quantity) {
    __privateGet(this, _inputElement).value = quantity;
    __privateGet(this, _inputElement).dispatchEvent(new Event("change", { bubbles: true }));
    __privateMethod(this, _QuantitySelector_instances, updateUI_fn).call(this);
  }
  restoreDefaultValue() {
    __privateGet(this, _inputElement).value = __privateGet(this, _inputElement).defaultValue;
    __privateMethod(this, _QuantitySelector_instances, updateUI_fn).call(this);
  }
};
_abortController6 = new WeakMap();
_decreaseButton = new WeakMap();
_increaseButton = new WeakMap();
_inputElement = new WeakMap();
_QuantitySelector_instances = new WeakSet();
onDecreaseQuantity_fn = function() {
  if (this.hasAttribute("allow-reset-to-zero") && __privateGet(this, _inputElement).value === __privateGet(this, _inputElement).min) {
    __privateGet(this, _inputElement).value = 0;
  } else {
    __privateGet(this, _inputElement).stepDown();
  }
  announceStatus(__privateGet(this, _inputElement).value);
  __privateGet(this, _inputElement).dispatchEvent(new Event("change", { bubbles: true }));
  __privateMethod(this, _QuantitySelector_instances, updateUI_fn).call(this);
};
onIncreaseQuantity_fn = function() {
  __privateGet(this, _inputElement).stepUp();
  announceStatus(__privateGet(this, _inputElement).value);
  __privateGet(this, _inputElement).dispatchEvent(new Event("change", { bubbles: true }));
  __privateMethod(this, _QuantitySelector_instances, updateUI_fn).call(this);
};
updateUI_fn = function() {
  if (__privateGet(this, _decreaseButton)) {
    if (this.hasAttribute("allow-reset-to-zero") && __privateGet(this, _inputElement).value === __privateGet(this, _inputElement).min) {
      __privateGet(this, _decreaseButton).disabled = false;
    } else {
      __privateGet(this, _decreaseButton).disabled = parseInt(__privateGet(this, _inputElement).value) <= parseInt(__privateGet(this, _inputElement).min);
    }
  }
  if (__privateGet(this, _increaseButton)) {
    __privateGet(this, _increaseButton).disabled = __privateGet(this, _inputElement).hasAttribute("max") ? parseInt(__privateGet(this, _inputElement).value) >= parseInt(__privateGet(this, _inputElement).max) : false;
  }
};
var _QuantityInput_instances, inputElement_get, onValueInput_fn, onValueChange_fn;
var QuantityInput = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _QuantityInput_instances);
    __privateGet(this, _QuantityInput_instances, inputElement_get).addEventListener("input", __privateMethod(this, _QuantityInput_instances, onValueInput_fn).bind(this));
    __privateGet(this, _QuantityInput_instances, inputElement_get).addEventListener("change", __privateMethod(this, _QuantityInput_instances, onValueChange_fn).bind(this));
    __privateGet(this, _QuantityInput_instances, inputElement_get).addEventListener("focus", () => __privateGet(this, _QuantityInput_instances, inputElement_get).select());
  }
  connectedCallback() {
    this.style.setProperty("--quantity-selector-character-count", `${__privateGet(this, _QuantityInput_instances, inputElement_get).value.length}ch`);
  }
  get quantity() {
    return parseInt(__privateGet(this, _QuantityInput_instances, inputElement_get).value);
  }
};
_QuantityInput_instances = new WeakSet();
inputElement_get = function() {
  return this.firstElementChild;
};
onValueInput_fn = function() {
  if (__privateGet(this, _QuantityInput_instances, inputElement_get).value === "") {
    __privateGet(this, _QuantityInput_instances, inputElement_get).value = __privateGet(this, _QuantityInput_instances, inputElement_get).min || 1;
  }
  this.style.setProperty("--quantity-selector-character-count", `${__privateGet(this, _QuantityInput_instances, inputElement_get).value.length}ch`);
};
onValueChange_fn = function() {
  if (!__privateGet(this, _QuantityInput_instances, inputElement_get).checkValidity()) {
    __privateGet(this, _QuantityInput_instances, inputElement_get).stepDown();
  }
};
if (!window.customElements.get("quantity-selector")) {
  window.customElements.define("quantity-selector", QuantitySelector);
}
if (!window.customElements.get("quantity-input")) {
  window.customElements.define("quantity-input", QuantityInput);
}

// js/common/list/listbox.js
var _accessibilityInitialized, _hiddenInput, _Listbox_instances, setupAccessibility_fn, onOptionClicked_fn, onInputChanged_fn, onKeyDown_fn;
var Listbox = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _Listbox_instances);
    __privateAdd(this, _accessibilityInitialized, false);
    __privateAdd(this, _hiddenInput);
    this.addEventListener("keydown", __privateMethod(this, _Listbox_instances, onKeyDown_fn));
  }
  static get observedAttributes() {
    return ["aria-activedescendant"];
  }
  connectedCallback() {
    __privateSet(this, _hiddenInput, this.querySelector('input[type="hidden"]'));
    __privateGet(this, _hiddenInput)?.addEventListener("change", __privateMethod(this, _Listbox_instances, onInputChanged_fn).bind(this));
    if (!__privateGet(this, _accessibilityInitialized)) {
      requestAnimationFrame(__privateMethod(this, _Listbox_instances, setupAccessibility_fn).bind(this));
    }
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "aria-activedescendant" && oldValue !== null && newValue !== oldValue) {
      Array.from(this.querySelectorAll('[role="option"]')).forEach((option) => {
        if (option.id === newValue) {
          option.setAttribute("aria-selected", "true");
          if (__privateGet(this, _hiddenInput) && __privateGet(this, _hiddenInput).value !== option.value) {
            __privateGet(this, _hiddenInput).value = option.value;
            __privateGet(this, _hiddenInput).dispatchEvent(new Event("change", { bubbles: true }));
          }
          if (this.hasAttribute("aria-owns")) {
            this.getAttribute("aria-owns").split(" ").forEach((boundId) => {
              document.getElementById(boundId).textContent = option.getAttribute("title") || option.innerText || option.value;
            });
          }
          option.dispatchEvent(
            new CustomEvent("listbox:change", {
              bubbles: true,
              detail: {
                value: option.value
              }
            })
          );
        } else {
          option.setAttribute("aria-selected", "false");
        }
      });
    }
  }
};
_accessibilityInitialized = new WeakMap();
_hiddenInput = new WeakMap();
_Listbox_instances = new WeakSet();
setupAccessibility_fn = function() {
  this.setAttribute("role", "listbox");
  Array.from(this.querySelectorAll('[role="option"]')).forEach((option) => {
    option.addEventListener("click", __privateMethod(this, _Listbox_instances, onOptionClicked_fn).bind(this));
    option.id = "option-" + (crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4));
    if (option.getAttribute("aria-selected") === "true") {
      this.setAttribute("aria-activedescendant", option.id);
    }
  });
  __privateSet(this, _accessibilityInitialized, true);
};
onOptionClicked_fn = function(event) {
  if (event.currentTarget.getAttribute("type") === "submit") {
    return;
  }
  this.setAttribute("aria-activedescendant", event.currentTarget.id);
  event.currentTarget.dispatchEvent(
    new CustomEvent("listbox:select", {
      bubbles: true,
      detail: {
        value: event.currentTarget.value
      }
    })
  );
};
onInputChanged_fn = function(event) {
  this.setAttribute("aria-activedescendant", this.querySelector(`[role="option"][value="${CSS.escape(event.target.value)}"]`).id);
};
onKeyDown_fn = function(event) {
  if (event.key === "ArrowUp") {
    event.target.previousElementSibling?.focus();
    event.preventDefault();
  } else if (event.key === "ArrowDown") {
    event.target.nextElementSibling?.focus();
    event.preventDefault();
  }
};
if (!window.customElements.get("x-listbox")) {
  window.customElements.define("x-listbox", Listbox);
}

// js/common/media/image-parallax.js
import { scroll, animate as animate8 } from "vendor";
var _ImageParallax_instances, setupParallax_fn;
var ImageParallax = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ImageParallax_instances);
  }
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      __privateMethod(this, _ImageParallax_instances, setupParallax_fn).call(this);
    }
  }
};
_ImageParallax_instances = new WeakSet();
setupParallax_fn = function() {
  const [scale, translate] = [1.3, 0.15 * 100 / 1.3], isFirstSection = this.closest(".shopify-section").matches(":first-child");
  scroll(animate8(this.querySelector("img"), { transform: [`scale(${scale}) translateY(-${translate}%)`, `scale(${scale}) translateY(${translate}%)`] }, { easing: "linear" }), {
    target: this.querySelector("img"),
    offset: [isFirstSection ? "start start" : "start end", "end start"]
  });
};
if (!window.customElements.get("image-parallax")) {
  window.customElements.define("image-parallax", ImageParallax);
}

// js/common/product/gift-card-recipient.js
var _recipientCheckbox, _recipientOtherProperties, _recipientSendOnProperty, _offsetProperty, _recipientFieldsContainer, _GiftCardRecipient_instances, synchronizeProperties_fn, formatDate_fn;
var GiftCardRecipient = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _GiftCardRecipient_instances);
    __privateAdd(this, _recipientCheckbox);
    __privateAdd(this, _recipientOtherProperties, []);
    __privateAdd(this, _recipientSendOnProperty);
    __privateAdd(this, _offsetProperty);
    __privateAdd(this, _recipientFieldsContainer);
  }
  connectedCallback() {
    const properties = Array.from(this.querySelectorAll('[name*="properties"]')), checkboxPropertyName = "properties[__shopify_send_gift_card_to_recipient]";
    __privateSet(this, _recipientCheckbox, properties.find((input) => input.name === checkboxPropertyName));
    __privateSet(this, _recipientOtherProperties, properties.filter((input) => input.name !== checkboxPropertyName));
    __privateSet(this, _recipientFieldsContainer, this.querySelector(".gift-card-recipient__fields"));
    __privateSet(this, _offsetProperty, this.querySelector('[name="properties[__shopify_offset]"]'));
    if (__privateGet(this, _offsetProperty)) {
      __privateGet(this, _offsetProperty).value = (/* @__PURE__ */ new Date()).getTimezoneOffset().toString();
    }
    __privateSet(this, _recipientSendOnProperty, this.querySelector('[name="properties[Send on]"]'));
    const minDate = /* @__PURE__ */ new Date();
    const maxDate = /* @__PURE__ */ new Date();
    maxDate.setDate(minDate.getDate() + 90);
    __privateGet(this, _recipientSendOnProperty)?.setAttribute("min", __privateMethod(this, _GiftCardRecipient_instances, formatDate_fn).call(this, minDate));
    __privateGet(this, _recipientSendOnProperty)?.setAttribute("max", __privateMethod(this, _GiftCardRecipient_instances, formatDate_fn).call(this, maxDate));
    __privateGet(this, _recipientCheckbox)?.addEventListener("change", __privateMethod(this, _GiftCardRecipient_instances, synchronizeProperties_fn).bind(this));
    __privateMethod(this, _GiftCardRecipient_instances, synchronizeProperties_fn).call(this);
  }
};
_recipientCheckbox = new WeakMap();
_recipientOtherProperties = new WeakMap();
_recipientSendOnProperty = new WeakMap();
_offsetProperty = new WeakMap();
_recipientFieldsContainer = new WeakMap();
_GiftCardRecipient_instances = new WeakSet();
synchronizeProperties_fn = function() {
  __privateGet(this, _recipientOtherProperties).forEach((property) => property.disabled = !__privateGet(this, _recipientCheckbox).checked);
  __privateGet(this, _recipientFieldsContainer).toggleAttribute("hidden", !__privateGet(this, _recipientCheckbox).checked);
};
formatDate_fn = function(date) {
  const offset = date.getTimezoneOffset();
  const offsetDate = new Date(date.getTime() - offset * 60 * 1e3);
  return offsetDate.toISOString().split("T")[0];
};
if (!window.customElements.get("gift-card-recipient")) {
  window.customElements.define("gift-card-recipient", GiftCardRecipient);
}

// js/common/product/product-card.js
import { Delegate as Delegate4 } from "vendor";
var _delegate3, _ProductCard_instances, onSwatchHovered_fn, onSwatchChanged_fn, createMediaImg_fn;
var ProductCard = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductCard_instances);
    __privateAdd(this, _delegate3, new Delegate4(this));
  }
  connectedCallback() {
    __privateGet(this, _delegate3).on("change", '.product-card__info [type="radio"]', __privateMethod(this, _ProductCard_instances, onSwatchChanged_fn).bind(this));
    __privateGet(this, _delegate3).on("pointerover", '.product-card__info [type="radio"] + label', __privateMethod(this, _ProductCard_instances, onSwatchHovered_fn).bind(this), true);
  }
  disconnectedCallback() {
    __privateGet(this, _delegate3).off();
  }
};
_delegate3 = new WeakMap();
_ProductCard_instances = new WeakSet();
onSwatchHovered_fn = async function(event, target) {
  const control = target.control;
  const primaryMediaElement = this.querySelector(".product-card__image--primary");
  if (control.hasAttribute("data-variant-media")) {
    __privateMethod(this, _ProductCard_instances, createMediaImg_fn).call(this, JSON.parse(control.getAttribute("data-variant-media")), primaryMediaElement.className, primaryMediaElement.sizes);
  }
};
onSwatchChanged_fn = async function(event, target) {
  if (target.hasAttribute("data-product-url")) {
    this.querySelectorAll(`a[href^="${Shopify.routes.root}products/"`).forEach((link) => {
      link.href = target.getAttribute("data-product-url");
    });
  } else if (target.hasAttribute("data-variant-id")) {
    this.querySelectorAll(`a[href^="${Shopify.routes.root}products/"`).forEach((link) => {
      const url = new URL(link.href);
      url.searchParams.set("variant", target.getAttribute("data-variant-id"));
      link.href = `${url.pathname}${url.search}${url.hash}`;
    });
  }
  if (!target.hasAttribute("data-variant-media")) {
    return;
  }
  let newMedia = JSON.parse(target.getAttribute("data-variant-media")), primaryMediaElement = this.querySelector(".product-card__image--primary"), secondaryMediaElement = this.querySelector(".product-card__image--secondary"), newPrimaryMediaElement = __privateMethod(this, _ProductCard_instances, createMediaImg_fn).call(this, newMedia, primaryMediaElement.className, primaryMediaElement.sizes), newSecondaryMediaElement = null;
  if (secondaryMediaElement && target.hasAttribute("data-variant-secondary-media")) {
    let newSecondaryMedia = JSON.parse(target.getAttribute("data-variant-secondary-media"));
    newSecondaryMediaElement = __privateMethod(this, _ProductCard_instances, createMediaImg_fn).call(this, newSecondaryMedia, secondaryMediaElement.className, secondaryMediaElement.sizes);
    newSecondaryMediaElement.ariaHidden = "true";
  }
  if (primaryMediaElement.src !== newPrimaryMediaElement.src) {
    if (secondaryMediaElement && newSecondaryMediaElement) {
      secondaryMediaElement.replaceWith(newSecondaryMediaElement);
    }
    await primaryMediaElement.animate({ opacity: [1, 0] }, { duration: 150, easing: "ease-in", fill: "forwards" }).finished;
    await new Promise((resolve) => newPrimaryMediaElement.complete ? resolve() : newPrimaryMediaElement.onload = () => resolve());
    primaryMediaElement.replaceWith(newPrimaryMediaElement);
    newPrimaryMediaElement.animate({ opacity: [0, 1] }, { duration: 150, easing: "ease-in" });
  }
};
createMediaImg_fn = function(media, className, sizes) {
  return createMediaImg(media, [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800], { class: className, sizes });
};
if (!window.customElements.get("product-card")) {
  window.customElements.define("product-card", ProductCard);
}

// js/common/product/product-form.js
var _abortController7, _ProductForm_instances, form_get2, onSubmit_fn;
var ProductForm = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductForm_instances);
    __privateAdd(this, _abortController7);
  }
  connectedCallback() {
    __privateSet(this, _abortController7, new AbortController());
    if (__privateGet(this, _ProductForm_instances, form_get2)) {
      __privateGet(this, _ProductForm_instances, form_get2).addEventListener("submit", __privateMethod(this, _ProductForm_instances, onSubmit_fn).bind(this), { signal: __privateGet(this, _abortController7).signal });
      __privateGet(this, _ProductForm_instances, form_get2).id.disabled = false;
    }
  }
  disconnectedCallback() {
    __privateGet(this, _abortController7).abort();
  }
};
_abortController7 = new WeakMap();
_ProductForm_instances = new WeakSet();
form_get2 = function() {
  return this.querySelector('form[action*="/cart/add"]');
};
onSubmit_fn = async function(event) {
  event.preventDefault();
  if (event.submitter?.getAttribute("aria-busy") === "true") {
    return;
  }
  if (!__privateGet(this, _ProductForm_instances, form_get2).checkValidity()) {
    __privateGet(this, _ProductForm_instances, form_get2).reportValidity();
    return;
  }
  const submitButtons = Array.from(__privateGet(this, _ProductForm_instances, form_get2).elements).filter((button) => button.type === "submit");
  submitButtons.forEach((submitButton) => {
    submitButton.setAttribute("aria-busy", "true");
  });
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  let sectionsToBundle = [];
  document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
  const formData = new FormData(__privateGet(this, _ProductForm_instances, form_get2));
  formData.set("sections", sectionsToBundle.join(","));
  formData.set("sections_url", `${Shopify.routes.root}variants/${__privateGet(this, _ProductForm_instances, form_get2).id.value}`);
  try {
  const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
    body: formData,
    method: "POST",
    headers: {
      "X-Requested-With": "XMLHttpRequest"
      // Needed for Shopify to check inventory
    }
  });
  const responseJson = await response.json();
  if (response.ok) {
      // Side-cart only: never hard-navigate to /cart when the drawer exists.
      // Keep page-type cart updates on the cart template itself.
      const cartDrawerEl = document.getElementById("cart-drawer");
      if (!cartDrawerEl && (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart")) {
      return window.location.href = `${Shopify.routes.root}cart`;
    }
    const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cartContent["sections"] = responseJson["sections"];
    __privateGet(this, _ProductForm_instances, form_get2).dispatchEvent(
      new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items: responseJson.hasOwnProperty("items") ? responseJson["items"] : [responseJson],
          cart: cartContent,
            onSuccessDo: formData.get("on_success")
        }
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
            onSuccessDo: formData.get("on_success"),
          cart: cartContent
        }
      })
    );
  } else {
    __privateGet(this, _ProductForm_instances, form_get2).dispatchEvent(
      new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: responseJson["description"]
        }
      })
    );
    document.dispatchEvent(new CustomEvent("cart:refresh"));
    }
  } catch (error) {
    __privateGet(this, _ProductForm_instances, form_get2).dispatchEvent(
      new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: "Could not add to cart"
        }
      })
    );
  } finally {
    submitButtons.forEach((submitButton) => {
      submitButton.removeAttribute("aria-busy");
    });
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
  }
};
if (!window.customElements.get("product-form")) {
  window.customElements.define("product-form", ProductForm);
}

// js/common/product/product-form-listeners.js
var _abortController8, _BuyButtons_instances, onVariantAdded_fn, onCartError_fn;
var BuyButtons = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _BuyButtons_instances);
    __privateAdd(this, _abortController8);
  }
  connectedCallback() {
    __privateSet(this, _abortController8, new AbortController());
    document.forms[this.getAttribute("form")]?.addEventListener("cart:error", __privateMethod(this, _BuyButtons_instances, onCartError_fn).bind(this), { signal: __privateGet(this, _abortController8).signal });
    document.forms[this.getAttribute("form")]?.addEventListener("variant:add", __privateMethod(this, _BuyButtons_instances, onVariantAdded_fn).bind(this), { signal: __privateGet(this, _abortController8).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController8).abort();
  }
};
_abortController8 = new WeakMap();
_BuyButtons_instances = new WeakSet();
onVariantAdded_fn = function(event) {
  if (window.themeVariables.settings.cartType === "message") {
    const bannerElement = document.createRange().createContextualFragment(`
        <div class="banner banner--success" role="status" aria-live="polite">
          ${window.themeVariables.strings.addedToCart}
        </div>
      `).firstElementChild;
    this.prepend(bannerElement);
    setTimeout(() => {
      bannerElement.remove();
    }, 2500);
  } else if (window.themeVariables.settings.cartType === "drawer") {
    const items = event.detail.items;
    let content = [];
    items.forEach((item) => {
      content.push(window.themeVariables.strings.addedToCartWithTitle.replace("{{ product_title }}", item?.title || ""));
    });
    announceStatus(content.join(", "));
  }
};
onCartError_fn = function(event) {
  const bannerElement = document.createRange().createContextualFragment(`
      <div class="banner banner--error" role="alert" aria-live="polite">
        ${event.detail.error}
      </div>
    `).firstElementChild;
  this.prepend(bannerElement);
  setTimeout(() => {
    bannerElement.remove();
  }, 2500);
};
if (!window.customElements.get("buy-buttons")) {
  window.customElements.define("buy-buttons", BuyButtons);
}

// js/common/product/product-gallery.js
import { PhotoSwipeLightbox } from "vendor";
var _abortController9, _photoSwipeInstance, _onGestureChangedListener, _settledMedia, _ProductGallery_instances, registerLightboxUi_fn, onSectionRerender_fn, onVariantChange_fn, onMediaChange_fn, onMediaSettle_fn, onCarouselClick_fn, onGestureStart_fn, onGestureChanged_fn;
var ProductGallery = class extends HTMLElement {
  /* Keep track of the currently settled media */
  constructor() {
    super();
    __privateAdd(this, _ProductGallery_instances);
    __privateAdd(this, _abortController9);
    __privateAdd(this, _photoSwipeInstance);
    __privateAdd(this, _onGestureChangedListener, __privateMethod(this, _ProductGallery_instances, onGestureChanged_fn).bind(this));
    __privateAdd(this, _settledMedia);
    this.addEventListener("lightbox:open", (event) => this.openLightBox(event?.detail?.index));
  }
  connectedCallback() {
    __privateSet(this, _abortController9, new AbortController());
    if (!this.carousel) {
      return;
    }
    const form = document.forms[this.getAttribute("form")];
    if (!form) {
      return;
    }
    form.addEventListener("product:rerender", __privateMethod(this, _ProductGallery_instances, onSectionRerender_fn).bind(this), { signal: __privateGet(this, _abortController9).signal });
    form.addEventListener("variant:change", __privateMethod(this, _ProductGallery_instances, onVariantChange_fn).bind(this), { signal: __privateGet(this, _abortController9).signal });
    this.carousel.addEventListener("carousel:change", __privateMethod(this, _ProductGallery_instances, onMediaChange_fn).bind(this), { signal: __privateGet(this, _abortController9).signal });
    this.carousel.addEventListener("carousel:settle", __privateMethod(this, _ProductGallery_instances, onMediaSettle_fn).bind(this), { signal: __privateGet(this, _abortController9).signal });
    this.carousel.addEventListener("click", __privateMethod(this, _ProductGallery_instances, onCarouselClick_fn).bind(this), { signal: __privateGet(this, _abortController9).signal });
    if (this.hasAttribute("allow-zoom")) {
      this.carousel.addEventListener("gesturestart", __privateMethod(this, _ProductGallery_instances, onGestureStart_fn).bind(this), { capture: false, signal: __privateGet(this, _abortController9).signal });
    }
    __privateMethod(this, _ProductGallery_instances, onMediaChange_fn).call(this);
  }
  disconnectedCallback() {
    __privateGet(this, _abortController9).abort();
  }
  get viewInSpaceButton() {
    return this.querySelector("[data-shopify-xr]");
  }
  get carousel() {
    return this.querySelector(".product-gallery__carousel");
  }
  /**
   * Create the PhotoSwipe instance if it does not already exist. This is done on demand, so until the lightbox is
   * open, nothing is created to not impact performance
   */
  get lightBox() {
    if (__privateGet(this, _photoSwipeInstance)) {
      return __privateGet(this, _photoSwipeInstance);
    }
    __privateSet(this, _photoSwipeInstance, new PhotoSwipeLightbox({
      pswpModule: () => import("photoswipe"),
      bgOpacity: 1,
      maxZoomLevel: parseInt(this.getAttribute("allow-zoom")) || 3,
      closeTitle: window.themeVariables.strings.closeGallery,
      zoomTitle: window.themeVariables.strings.zoomGallery,
      errorMsg: window.themeVariables.strings.errorGallery,
      // UX
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      close: false,
      zoom: false
    }));
    __privateGet(this, _photoSwipeInstance).on("uiRegister", __privateMethod(this, _ProductGallery_instances, registerLightboxUi_fn).bind(this));
    __privateGet(this, _photoSwipeInstance).addFilter("thumbEl", (thumbEl, data) => data.thumbnailElement);
    __privateGet(this, _photoSwipeInstance).init();
    return __privateGet(this, _photoSwipeInstance);
  }
  get filteredIndexes() {
    try {
      return JSON.parse(this.getAttribute("filtered-indexes") || "[]").map((index) => parseInt(index, 10) - 1).filter((index) => !Number.isNaN(index));
    } catch (error) {
      return [];
    }
  }
  /**
   * Open the lightbox at the given index (by default, it opens the selected image)
   */
  openLightBox(index) {
    const images = this.carousel.cells.flatMap((cell) => Array.from(cell.querySelectorAll(":scope > img")));
    const dataSource = images.map((image) => {
      return {
        thumbnailElement: image,
        src: image.src,
        srcset: image.srcset,
        msrc: image.currentSrc || image.src,
        width: parseInt(image.getAttribute("width")),
        height: parseInt(image.getAttribute("height")),
        alt: image.alt,
        thumbCropped: true
      };
    });
    const imageCells = this.carousel.cells.filter((cell) => cell.getAttribute("data-media-type") === "image");
    this.lightBox.loadAndOpen(index ?? imageCells.indexOf(this.carousel.selectedCell), dataSource);
  }
};
_abortController9 = new WeakMap();
_photoSwipeInstance = new WeakMap();
_onGestureChangedListener = new WeakMap();
_settledMedia = new WeakMap();
_ProductGallery_instances = new WeakSet();
/**
 * Add custom elements to PhotoSwipe gallery
 */
registerLightboxUi_fn = function() {
  __privateGet(this, _photoSwipeInstance).pswp.ui.registerElement({
    name: "close-button",
    className: "circle-button circle-button--xl hover:animate-icon-block",
    ariaLabel: window.themeVariables.strings.closeGallery,
    order: 2,
    isButton: true,
    html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
          <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
        </svg>
      `,
    onClick: () => {
      __privateGet(this, _photoSwipeInstance).pswp.close();
    }
  });
  if (__privateGet(this, _photoSwipeInstance).pswp.options.dataSource.length > 1) {
    __privateGet(this, _photoSwipeInstance).pswp.ui.registerElement({
      name: "previous-button",
      className: "circle-button hover:animate-icon-inline",
      ariaLabel: window.themeVariables.strings.previous,
      order: 1,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
          <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/>
        </svg>
      `,
      onClick: () => {
        __privateGet(this, _photoSwipeInstance).pswp.prev();
      }
    });
    __privateGet(this, _photoSwipeInstance).pswp.ui.registerElement({
      name: "next-button",
      className: "circle-button hover:animate-icon-inline",
      ariaLabel: window.themeVariables.strings.next,
      order: 3,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
          <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/>
        </svg>
      `,
      onClick: () => {
        __privateGet(this, _photoSwipeInstance).pswp.next();
      }
    });
  }
};
/**
 * When the section is re-rendered upon variant changes, hydrate deferred media
 * (empty jw-media-slot placeholders) and re-apply filtered indexes. Prestige only
 * toggles [hidden]; Jwear defers off-variant image decode, so without hydrate the
 * newly visible slides stay empty beige boxes.
 */
onSectionRerender_fn = function(event) {
  const galleryMarkup = deepQuerySelector(event.detail.htmlFragment, `${this.tagName}[form="${this.getAttribute("form")}"]`);
  if (!galleryMarkup || !this.carousel) {
    return;
  }
  const liveMediaSelector = "img, video, model-viewer, iframe, .shopify-model-viewer-ui";
  const freshById = /* @__PURE__ */ new Map();
  galleryMarkup.querySelectorAll(".product-gallery__media[data-media-id]").forEach((fresh) => {
    freshById.set(fresh.getAttribute("data-media-id"), fresh);
  });
  this.carousel.allCells.forEach((cell) => {
    const mediaId = cell.getAttribute("data-media-id");
    const fresh = mediaId ? freshById.get(mediaId) : null;
    if (!fresh) {
      return;
    }
    const hasLiveMedia = cell.querySelector(liveMediaSelector);
    const freshHasLiveMedia = fresh.querySelector(liveMediaSelector);
    const isDeferred = !!cell.querySelector("[data-jw-deferred-media]");
    const freshIsDeferred = !!fresh.querySelector("[data-jw-deferred-media]");
    if ((isDeferred || !hasLiveMedia) && freshHasLiveMedia) {
      cell.innerHTML = fresh.innerHTML;
    } else if (!freshIsDeferred && freshHasLiveMedia && cell.innerHTML !== fresh.innerHTML && !hasLiveMedia) {
      cell.innerHTML = fresh.innerHTML;
    } else if (freshIsDeferred && hasLiveMedia) {
      // Keep decoded media in DOM when re-hidden — faster color toggles; filter still hides the cell.
    } else if ((isDeferred || !hasLiveMedia) && !freshHasLiveMedia) {
      // Both sides still deferred placeholders — hydrate from data-jw-src on live or fresh slot.
      const slot = cell.querySelector("[data-jw-deferred-media]") || fresh.querySelector("[data-jw-deferred-media]");
      const src = slot?.getAttribute("data-jw-src");
      if (slot && src && !cell.querySelector(liveMediaSelector)) {
        const img = document.createElement("img");
        img.src = src;
        img.srcset = slot.getAttribute("data-jw-srcset") || "";
        img.sizes = slot.getAttribute("data-jw-sizes") || "100vw";
        img.width = parseInt(slot.getAttribute("data-jw-width") || "1200", 10);
        img.height = parseInt(slot.getAttribute("data-jw-height") || "1600", 10);
        img.alt = slot.getAttribute("data-jw-alt") || "";
        img.loading = "lazy";
        img.decoding = "async";
        const targetSlot = cell.querySelector("[data-jw-deferred-media]");
        if (targetSlot) {
          targetSlot.replaceWith(img);
        } else {
          cell.appendChild(img);
        }
      }
    }
    ["data-media-id", "data-media-type"].forEach((attr) => {
      if (fresh.hasAttribute(attr)) {
        cell.setAttribute(attr, fresh.getAttribute(attr));
      }
    });
    if (fresh.className) {
      cell.className = fresh.className;
    }
  });
  const nav = this.querySelector("product-gallery-navigation");
  if (nav) {
    const freshThumbsById = /* @__PURE__ */ new Map();
    galleryMarkup.querySelectorAll(".product-gallery__thumbnail[data-media-id]").forEach((fresh) => {
      freshThumbsById.set(fresh.getAttribute("data-media-id"), fresh);
    });
    Array.from(nav.querySelectorAll(".product-gallery__thumbnail")).forEach((thumb) => {
      const mediaId = thumb.getAttribute("data-media-id");
      const fresh = mediaId ? freshThumbsById.get(mediaId) : null;
      if (!fresh) {
        return;
      }
      const thumbHasMedia = !!thumb.querySelector("img, svg, .placeholder");
      const freshHasMedia = !!fresh.querySelector("img, svg, .placeholder");
      if (!thumbHasMedia && freshHasMedia) {
        thumb.innerHTML = fresh.innerHTML;
      }
      ["data-media-id", "data-media-position", "data-media-type", "aria-current"].forEach((attr) => {
        if (fresh.hasAttribute(attr)) {
          thumb.setAttribute(attr, fresh.getAttribute(attr));
        }
      });
      thumb.toggleAttribute("hidden", fresh.hasAttribute("hidden"));
    });
  }
  let nextFiltered = [];
  try {
    nextFiltered = JSON.parse(galleryMarkup.getAttribute("filtered-indexes") || "[]").map((index) => parseInt(index, 10) - 1).filter((index) => !Number.isNaN(index));
  } catch (error) {
    nextFiltered = [];
  }
  const prevFiltered = this.filteredIndexes;
  const filterChanged = nextFiltered.length !== prevFiltered.length || nextFiltered.some((value, index) => value !== prevFiltered[index]);
  if (filterChanged) {
    const cellCount = this.carousel.allCells.length;
    // Never hide every slide — empty gallery on color switch.
    if (cellCount > 0 && nextFiltered.length >= cellCount) {
      this.carousel.filter([]);
      this.setAttribute("filtered-indexes", "[]");
    } else {
      this.carousel.filter(nextFiltered);
      this.setAttribute("filtered-indexes", galleryMarkup.getAttribute("filtered-indexes") || "[]");
    }
  }
  // After filter, ensure every visible cell has real media (index-mismatch safety).
  this.carousel.cells.forEach((cell) => {
    if (cell.querySelector(liveMediaSelector)) {
      return;
    }
    const mediaId = cell.getAttribute("data-media-id");
    const fresh = mediaId ? freshById.get(mediaId) : null;
    if (fresh?.querySelector(liveMediaSelector)) {
      cell.innerHTML = fresh.innerHTML;
      if (fresh.className) {
        cell.className = fresh.className;
      }
    }
  });
  if (nav) {
    Array.from(nav.querySelectorAll(".product-gallery__thumbnail:not([hidden])")).forEach((thumb) => {
      if (thumb.querySelector("img, svg, .placeholder")) {
        return;
      }
      const mediaId = thumb.getAttribute("data-media-id");
      const fresh = mediaId ? galleryMarkup.querySelector(`.product-gallery__thumbnail[data-media-id="${mediaId}"]`) : null;
      if (fresh?.querySelector("img, svg, .placeholder")) {
        thumb.innerHTML = fresh.innerHTML;
      }
    });
  }
  const selectFeatured = () => {
    if (!this.carousel.cells.length) {
      return;
    }
    let selectIndex = this.carousel.cells.findIndex((cell) => cell.classList.contains("is-initial"));
    if (selectIndex < 0 || this.carousel.selectedCell?.querySelector("[data-jw-deferred-media]") || !(this.carousel.selectedIndex in this.carousel.cells)) {
      selectIndex = Math.max(0, selectIndex);
    }
    try {
      this.carousel.select(selectIndex, { instant: true });
    } catch (error) {
    }
  };
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(selectFeatured);
  } else {
    selectFeatured();
  }
};
/**
 * When the variant changes, jump to that variant's featured media among visible cells.
 */
onVariantChange_fn = function(event) {
  if (!event.detail.variant || !this.carousel) {
    return;
  }
  const featuredMedia = event.detail.variant["featured_media"];
  if (!featuredMedia) {
    return;
  }
  if (event.detail.previousVariant?.["featured_media"]?.["id"] === featuredMedia["id"]) {
    return;
  }
  const featuredId = String(featuredMedia["id"]);
  let visibleIndex = this.carousel.cells.findIndex((cell) => cell.getAttribute("data-media-id") === featuredId);
  if (visibleIndex < 0) {
    const position = featuredMedia["position"] - 1;
    const filteredIndexBelowPosition = this.filteredIndexes.filter((filteredIndex) => filteredIndex < position);
    visibleIndex = Math.max(0, position - filteredIndexBelowPosition.length);
  }
  if (!(visibleIndex in this.carousel.cells)) {
    visibleIndex = 0;
  }
  if (!(visibleIndex in this.carousel.cells)) {
    return;
  }
    if (this.carousel.isScrollable) {
    this.carousel.select(visibleIndex, { instant: true });
    } else {
    this.carousel.cells[visibleIndex]?.scrollIntoView({ block: "start", behavior: "smooth" });
  }
};
/**
 * When the media is about to change, we perform some logic
 */
onMediaChange_fn = function() {
  if (!__privateGet(this, _settledMedia)) {
    return;
  }
  switch (__privateGet(this, _settledMedia).getAttribute("data-media-type")) {
    case "external_video":
    case "video":
    case "model":
      __privateGet(this, _settledMedia).firstElementChild.pause();
  }
};
/**
 * When the media settles, we have to update various elements such as the AR button, the autoplay strategy...
 */
onMediaSettle_fn = function(event) {
  const media = event ? event.detail.cell : this.carousel.selectedCell, zoomButton = this.querySelector(".product-gallery__zoom-button");
  if (!media) {
    return;
  }
  switch (media.getAttribute("data-media-type")) {
    case "image":
      this.viewInSpaceButton?.setAttribute("data-shopify-model3d-id", this.viewInSpaceButton?.getAttribute("data-shopify-model3d-default-id"));
      zoomButton?.classList.remove("product-gallery__zoom-button--hidden");
      break;
    case "external_video":
    case "video":
      this.viewInSpaceButton?.setAttribute("data-shopify-model3d-id", this.viewInSpaceButton?.getAttribute("data-shopify-model3d-default-id"));
      zoomButton?.classList.add("product-gallery__zoom-button--hidden");
      if (this.hasAttribute("autoplay-media")) {
        media.firstElementChild.play();
      }
      break;
    case "model":
      if (matchesMediaQuery("md")) {
        media.firstElementChild.play();
      }
      this.viewInSpaceButton?.setAttribute("data-shopify-model3d-id", event.detail.cell.getAttribute("data-media-id"));
      zoomButton?.classList.add("product-gallery__zoom-button--hidden");
      break;
  }
  __privateSet(this, _settledMedia, media);
};
/**
 * Detect a click on an image on desktop, and open the lightbox for the corresponding image
 */
onCarouselClick_fn = function(event) {
  if (!this.hasAttribute("allow-zoom") || !matchesMediaQuery("md") || event.target.tagName !== "IMG") {
    return;
  }
  const media = event.target.closest(".product-gallery__media");
  if (!media || media.getAttribute("data-media-type") !== "image") {
    return;
  }
  const imageCells = this.carousel.cells.filter((cell) => cell.getAttribute("data-media-type") === "image");
  this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true, detail: { index: imageCells.indexOf(media) } }));
};
/**
 * For iOS devices only, we use the gesturechange event to easily detect a "pinch to zoom"
 */
onGestureStart_fn = function(event) {
  event.preventDefault();
  this.carousel.addEventListener("gesturechange", __privateGet(this, _onGestureChangedListener), { capture: false, signal: __privateGet(this, _abortController9).signal });
};
onGestureChanged_fn = function(event) {
  event.preventDefault();
  const media = event.target.closest(".product-gallery__media");
  if (!media || media.getAttribute("data-media-type") !== "image") {
    return;
  }
  const imageCells = this.carousel.cells.filter((cell) => cell.getAttribute("data-media-type") === "image");
  if (event.scale > 1.5) {
    this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true, detail: { index: imageCells.indexOf(media) } }));
    this.removeEventListener("gesturechange", __privateGet(this, _onGestureChangedListener));
  }
};
var _intersectionObserver, _hasProgrammaticScroll, _scrollDirection, _lastScrollPosition, _onWindowScrollListener, _ProductGalleryNavigation_instances, onMediaObserve_fn;
var ProductGalleryNavigation = class extends CarouselNavigation {
  constructor() {
    super();
    __privateAdd(this, _ProductGalleryNavigation_instances);
    __privateAdd(this, _intersectionObserver, new IntersectionObserver(__privateMethod(this, _ProductGalleryNavigation_instances, onMediaObserve_fn).bind(this), { threshold: [0, 0.5, 1] }));
    __privateAdd(this, _hasProgrammaticScroll, false);
    __privateAdd(this, _scrollDirection, "bottom");
    __privateAdd(this, _lastScrollPosition, window.scrollY);
    __privateAdd(this, _onWindowScrollListener, () => {
      if (window.scrollY > __privateGet(this, _lastScrollPosition)) {
        __privateSet(this, _scrollDirection, "bottom");
      } else {
        __privateSet(this, _scrollDirection, "top");
      }
      __privateSet(this, _lastScrollPosition, window.scrollY);
    });
    window.addEventListener("scroll", __privateGet(this, _onWindowScrollListener), { passive: true });
  }
  connectedCallback() {
    super.connectedCallback();
    this.carousel.allCells.forEach((cell) => __privateGet(this, _intersectionObserver).observe(cell));
  }
  disconnectedCallback() {
    __privateGet(this, _intersectionObserver).disconnect();
    window.removeEventListener("scroll", __privateGet(this, _onWindowScrollListener));
    super.disconnectedCallback();
  }
  onButtonClicked(newIndex) {
    if (this.carousel.isScrollable) {
      super.onButtonClicked(newIndex);
    } else {
      this.carousel.cells[newIndex]?.scrollIntoView({ block: "start", behavior: "smooth" });
      this.onNavigationChange(newIndex);
      __privateSet(this, _hasProgrammaticScroll, true);
      if (!("onscrollend" in window)) {
        setTimeout(() => {
          __privateSet(this, _hasProgrammaticScroll, false);
        }, 1e3);
      } else {
        window.addEventListener(
          "scrollend",
          () => {
            __privateSet(this, _hasProgrammaticScroll, false);
          },
          { once: true }
        );
      }
    }
  }
};
_intersectionObserver = new WeakMap();
_hasProgrammaticScroll = new WeakMap();
_scrollDirection = new WeakMap();
_lastScrollPosition = new WeakMap();
_onWindowScrollListener = new WeakMap();
_ProductGalleryNavigation_instances = new WeakSet();
/**
 * Use the intersection observer to change the selected icon
 */
onMediaObserve_fn = function(entries) {
  if (this.carousel.isScrollable) {
    return;
  }
  const firstEntry = entries.find((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5);
  if (!firstEntry || __privateGet(this, _hasProgrammaticScroll)) {
    return;
  }
  const selectedItem = this.items.find((item) => item.getAttribute("aria-current") === "true"), candidateItem = this.items.find((item) => item.getAttribute("data-media-id") === firstEntry.target.getAttribute("data-media-id"));
  if (!selectedItem || !candidateItem || !candidateItem.isConnected) {
    return;
  }
  if (__privateGet(this, _scrollDirection) === "bottom" && parseInt(candidateItem.getAttribute("data-media-position")) > parseInt(selectedItem.getAttribute("data-media-position"))) {
    selectedItem.setAttribute("aria-current", "false");
    candidateItem.setAttribute("aria-current", "true");
  } else if (__privateGet(this, _scrollDirection) === "top" && parseInt(candidateItem.getAttribute("data-media-position")) < parseInt(selectedItem.getAttribute("data-media-position"))) {
    selectedItem.setAttribute("aria-current", "false");
    candidateItem.setAttribute("aria-current", "true");
  }
};
var OpenLightBoxButton = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true })));
  }
};
if (!window.customElements.get("product-gallery")) {
  window.customElements.define("product-gallery", ProductGallery);
}
if (!window.customElements.get("product-gallery-navigation")) {
  window.customElements.define("product-gallery-navigation", ProductGalleryNavigation);
}
if (!window.customElements.get("open-lightbox-button")) {
  window.customElements.define("open-lightbox-button", OpenLightBoxButton);
}

// js/common/product/product-list.js
import { inView as inView6, animate as animate9, stagger } from "vendor";
var ProductList = class extends HTMLElement {
  connectedCallback() {
    this._finishReveal = (elements) => {
      elements.forEach((el) => {
        el.style.opacity = "1";
        el.style.transform = "translateY(0px)";
        el.removeAttribute("reveal-on-scroll");
      });
    };
    // Reveal immediately — never leave cards at CSS opacity:0 waiting on inView
    // (homepage featured-collections white void when IO/vendor misses).
    this.revealPending(true);
    if (matchesMediaQuery("motion-safe")) {
      const revealElements = this.querySelectorAll('product-card[reveal-on-scroll="true"], .product-list__promo[reveal-on-scroll="true"]');
      if (revealElements.length > 0) {
        inView6(this, () => this.revealPending(true), { margin: "280px 0px" });
      }
    }
    // Deferred tabs / AJAX append cards after first reveal pass.
    this._onJwProductsLoaded = (event) => {
      if (event.detail?.list && event.detail.list !== this) return;
      this.revealPending(true);
    };
    document.addEventListener("jwear:products-loaded", this._onJwProductsLoaded);
    this._childObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('product-card[reveal-on-scroll="true"], .product-list__promo[reveal-on-scroll="true"]') || node.querySelector?.('product-card[reveal-on-scroll="true"], .product-list__promo[reveal-on-scroll="true"]')) {
            this.revealPending(true);
            return;
          }
        }
      }
    });
    this._childObserver.observe(this, { childList: true, subtree: true });
    this._revealSafetyTimer = window.setTimeout(() => this.revealPending(true), 400);
  }
  disconnectedCallback() {
    if (this._onJwProductsLoaded) {
      document.removeEventListener("jwear:products-loaded", this._onJwProductsLoaded);
    }
    this._childObserver?.disconnect();
    if (this._revealSafetyTimer) window.clearTimeout(this._revealSafetyTimer);
  }
  /** Instantly show cards still stuck at CSS opacity:0 (AJAX / missed stagger). */
  revealPending(instant = false) {
    const revealElements = this.querySelectorAll('product-card[reveal-on-scroll="true"], .product-list__promo[reveal-on-scroll="true"]');
    if (revealElements.length === 0) return;
    if (this._revealSafetyTimer) {
      window.clearTimeout(this._revealSafetyTimer);
      this._revealSafetyTimer = null;
    }
    if (instant) {
      this._finishReveal(revealElements);
      return;
    }
    this.reveal();
  }
  reveal() {
    const revealElements = this.querySelectorAll('product-card[reveal-on-scroll="true"], .product-list__promo[reveal-on-scroll="true"]');
    if (revealElements.length === 0) return;
    const animation = animate9(
      revealElements,
      {
        opacity: [0, 1],
        transform: ["translateY(20px)", "translateY(0)"]
      },
      {
        duration: 0.2,
        easing: "ease-in-out",
        delay: stagger(0.05, { start: 0.4, easing: "ease-out" })
      }
    );
    const clearReveal = () => this._finishReveal(revealElements);
    if (animation?.finished?.then) {
      animation.finished.then(clearReveal).catch(clearReveal);
    } else {
      setTimeout(clearReveal, 2e3);
    }
  }
};
if (!window.customElements.get("product-list")) {
  window.customElements.define("product-list", ProductList);
}

// js/common/product/product-loader.js
var loadedProducts = {};
var ProductLoader = class {
  static load(productHandle) {
    if (!productHandle) {
      return;
    }
    if (loadedProducts[productHandle]) {
      return loadedProducts[productHandle];
    }
    loadedProducts[productHandle] = new Promise(async (resolve, reject) => {
      const response = await fetch(`${Shopify.routes.root}products/${productHandle}.js`);
      if (response.ok) {
        const responseAsJson = await response.json();
        resolve(responseAsJson);
      } else {
        reject(`
          Attempted to load information for product with handle ${productHandle}, but this product is in "draft" mode. You won't be able to
          switch between variants or access to per-variant information. To fully preview this product, change temporarily its status
          to "active".
        `);
      }
    });
    return loadedProducts[productHandle];
  }
};

// js/common/product/product-rerender.js
var _abortController10, _ProductRerender_instances, onRerender_fn;
var ProductRerender = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductRerender_instances);
    __privateAdd(this, _abortController10);
  }
  connectedCallback() {
    __privateSet(this, _abortController10, new AbortController());
    if (!this.id || !this.hasAttribute("observe-form")) {
      console.warn('The <product-rerender> requires an ID to identify the element to re-render, and an "observe-form" attribute referencing to the form to monitor.');
    }
    document.forms[this.getAttribute("observe-form")].addEventListener("product:rerender", __privateMethod(this, _ProductRerender_instances, onRerender_fn).bind(this), { signal: __privateGet(this, _abortController10).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController10).abort();
  }
};
_abortController10 = new WeakMap();
_ProductRerender_instances = new WeakSet();
onRerender_fn = function(event) {
  const matchingElement = deepQuerySelector(event.detail.htmlFragment, `#${this.id}`);
  if (!matchingElement) {
    return;
  }
  const focusedElement = document.activeElement;
  if (!this.hasAttribute("allow-partial-rerender") || event.detail.productChange) {
    this.replaceWith(matchingElement);
  } else {
    const blockTypes = [
      "sku",
      "badges",
      "quantity-selector",
      "volume-pricing",
      "price",
      "payment-terms",
      "variant-picker",
      "inventory",
      "buy-buttons",
      "pickup-availability",
      "liquid"
    ];
    blockTypes.forEach((blockType) => {
      this.querySelectorAll(`[data-block-type="${blockType}"]`).forEach((element) => {
        const matchingBlock = matchingElement.querySelector(`[data-block-type="${blockType}"][data-block-id="${element.getAttribute("data-block-id")}"]`);
        if (matchingBlock) {
          if (blockType === "buy-buttons") {
            const liveBuyButtons = element.querySelector("buy-buttons");
            const nextBuyButtons = matchingBlock.querySelector("buy-buttons");
            if (liveBuyButtons && nextBuyButtons) {
              liveBuyButtons.replaceWith(nextBuyButtons);
            } else if (nextBuyButtons && !liveBuyButtons) {
              element.appendChild(nextBuyButtons);
            } else {
              element.replaceWith(matchingBlock);
            }
          } else if (blockType === "payment-terms") {
            const liveId = element.querySelector('[name="id"]');
            const nextId = matchingBlock.querySelector('[name="id"]');
            if (liveId && nextId) {
              liveId.value = nextId.value;
              liveId.dispatchEvent(new Event("change", { bubbles: true }));
            }
          } else if (blockType === "quantity-selector") {
            const quantitySelectorElement = element.querySelector("quantity-selector");
            if (quantitySelectorElement) {
              const existingQuantity = quantitySelectorElement.quantity;
              element.replaceWith(matchingBlock);
              const nextQuantitySelector = matchingBlock.querySelector("quantity-selector");
              if (nextQuantitySelector) {
                nextQuantitySelector.quantity = existingQuantity;
              }
            }
          } else {
            element.replaceWith(matchingBlock);
          }
        }
      });
    });
  }
  if (focusedElement.id) {
    const element = document.getElementById(focusedElement.id);
    const productRerenderScope = document.getElementById(this.id);
    if (productRerenderScope.contains(element)) {
      element.focus();
    }
  }
};
if (!window.customElements.get("product-rerender")) {
  window.customElements.define("product-rerender", ProductRerender);
}

// js/common/product/quick-buy-modal.js
var _QuickBuyModal_instances, onAfterHide_fn;
var QuickBuyModal = class extends Modal {
  constructor() {
    super();
    __privateAdd(this, _QuickBuyModal_instances);
    if (window.themeVariables.settings.cartType === "drawer") {
      document.addEventListener("variant:add", this.hide.bind(this));
    }
    this.addEventListener("dialog:after-hide", __privateMethod(this, _QuickBuyModal_instances, onAfterHide_fn).bind(this));
  }
  async show() {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    const responseContent = await (await cachedFetch(`${window.Shopify.routes.root}products/${this.getAttribute("handle")}`)).text();
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    let quickBuyContent;
    const templateMatch = responseContent.match(/<template\s+id="quick-buy-content"[^>]*>([\s\S]*?)<\/template>/i);
    if (templateMatch) {
      const template = document.createElement("template");
      template.innerHTML = templateMatch[1];
      quickBuyContent = template.content;
    } else {
      const tempDoc = new DOMParser().parseFromString(responseContent, "text/html");
      const quickBuyTemplate = tempDoc.getElementById("quick-buy-content");
      if (!quickBuyTemplate?.content) {
        return super.show();
      }
      quickBuyContent = quickBuyTemplate.content;
    }
    Array.from(quickBuyContent.querySelectorAll("noscript")).forEach((noScript) => noScript.remove());
    quickBuyContent.querySelectorAll(
      "shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button, [data-jw-dynamic-checkout-host], template[id$='-dynamic-checkout-tpl'], x-modal, [data-jw-size-chart]"
    ).forEach((node) => node.remove());
    quickBuyContent.querySelectorAll("script").forEach((script) => {
      if (script.getAttribute("data-variant") != null) {
        return;
      }
      script.remove();
    });
    quickBuyContent.querySelectorAll("style").forEach((style) => style.remove());
    this.replaceChildren(quickBuyContent);
    const jwOffer = this.getAttribute("data-jw-offer");
    const jwStampOffer = (form) => {
      if (!jwOffer || !form || form.querySelector('[name="properties[_jw_offer]"]')) return;
      [
        ["properties[_jw_offer]", jwOffer],
        ["properties[_jw_offer_code]", this.getAttribute("data-jw-offer-code") || (window.jwHalloween && window.jwHalloween.code) || "HALLOWEEN"],
        ["properties[_jw_offer_pct]", this.getAttribute("data-jw-offer-pct") || (window.jwHalloween && window.jwHalloween.pct) || "15"]
      ].forEach((pair) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = pair[0];
        input.value = pair[1];
        form.appendChild(input);
      });
    };
    if (jwOffer) {
      jwStampOffer(this.querySelector('form[action*="/cart/add"]'));
      if (!this.__jwOfferBound) {
        this.__jwOfferBound = true;
        this.addEventListener("submit", (event) => {
          const form = event.target;
          if (form && form.matches && form.matches('form[action*="/cart/add"]')) jwStampOffer(form);
        }, true);
      }
    }
    return super.show();
  }
};
_QuickBuyModal_instances = new WeakSet();
onAfterHide_fn = function() {
  this.innerHTML = "";
};
if (!window.customElements.get("quick-buy-modal")) {
  window.customElements.define("quick-buy-modal", QuickBuyModal);
}

// js/common/product/variant-picker.js
import { Delegate as Delegate5 } from "vendor";
var CACHE_EVICTION_TIME = 1e3 * 60 * 5;
var VARIANT_CACHE_MAX_ENTRIES = 8;
var VARIANT_CACHE_MAX_BYTES = 3 * 1024 * 1024;
var VARIANT_PREFETCH_MAX_CONCURRENT = 2;
var _preloadedHtml, _delegate4, _form, _selectedVariant, _variants, _requestSequence, _selectionAbortController, _prefetchControllers, _VariantPicker_instances, getActiveOptionValues_fn, getOptionValuesFromOption_fn, updateOptionAvailability_fn, onOptionChanged_fn, onOptionPreload_fn, abortPrefetches_fn, prunePreloadedHtml_fn, renderForCombination_fn, createHashKeyForHtml_fn;
var _VariantPicker = class _VariantPicker extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _VariantPicker_instances);
    __privateAdd(this, _delegate4, new Delegate5(document.body));
    __privateAdd(this, _form);
    __privateAdd(this, _selectedVariant);
    __privateAdd(this, _variants, []);
    __privateAdd(this, _requestSequence, 0);
    __privateAdd(this, _selectionAbortController);
    __privateAdd(this, _prefetchControllers, /* @__PURE__ */ new Set());
  }
  async connectedCallback() {
    __privateSet(this, _selectedVariant, JSON.parse(this.querySelector("script[data-variant]")?.textContent || "{}"));
    __privateSet(this, _variants, JSON.parse(this.querySelector("script[data-product-variants]")?.textContent || "[]"));
    __privateSet(this, _form, document.forms[this.getAttribute("form-id")]);
    __privateMethod(this, _VariantPicker_instances, updateOptionAvailability_fn).call(this);
    __privateGet(this, _delegate4).on("change", `input[data-option-position][form="${this.getAttribute("form-id")}"]`, __privateMethod(this, _VariantPicker_instances, onOptionChanged_fn).bind(this));
    if (this.getAttribute("context") !== "quick_buy") {
      __privateGet(this, _delegate4).on("pointerenter", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, __privateMethod(this, _VariantPicker_instances, onOptionPreload_fn).bind(this), true);
      __privateGet(this, _delegate4).on("touchstart", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, __privateMethod(this, _VariantPicker_instances, onOptionPreload_fn).bind(this), true);
      __privateGet(this, _delegate4).on("focus", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked)`, __privateMethod(this, _VariantPicker_instances, onOptionPreload_fn).bind(this), true);
    }
  }
  disconnectedCallback() {
    __privateSet(this, _requestSequence, __privateGet(this, _requestSequence) + 1);
    __privateGet(this, _selectionAbortController)?.abort();
    __privateSet(this, _selectionAbortController, void 0);
    __privateMethod(this, _VariantPicker_instances, abortPrefetches_fn).call(this);
    __privateGet(this, _delegate4).off();
  }
  get selectedVariant() {
    return __privateGet(this, _selectedVariant);
  }
  get productHandle() {
    return this.getAttribute("handle");
  }
  get updateUrl() {
    return this.hasAttribute("update-url");
  }
  /**
   * Select a variant using a list of option values. The list of option values might lead to no variant (for instance)
   * in the case of a combination that does not exist
   */
  async selectCombination({ optionValues, productChange }) {
    const requestSequence = __privateGet(this, _requestSequence) + 1;
    __privateSet(this, _requestSequence, requestSequence);
    __privateGet(this, _selectionAbortController)?.abort();
    __privateMethod(this, _VariantPicker_instances, abortPrefetches_fn).call(this);
    const selectionAbortController = new AbortController();
    __privateSet(this, _selectionAbortController, selectionAbortController);
    const previousVariant = this.selectedVariant;
    let responseHtml;
    try {
      responseHtml = await __privateMethod(this, _VariantPicker_instances, renderForCombination_fn).call(this, optionValues, { kind: "selection", signal: selectionAbortController.signal, controller: selectionAbortController });
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Unable to render selected product combination.", error);
      }
      if (__privateGet(this, _selectionAbortController) === selectionAbortController) {
        __privateSet(this, _selectionAbortController, void 0);
      }
      return;
    }
    if (__privateGet(this, _selectionAbortController) === selectionAbortController) {
      __privateSet(this, _selectionAbortController, void 0);
    }
    if (requestSequence !== __privateGet(this, _requestSequence) || !this.isConnected) {
      return;
    }
    const newContent = document.createRange().createContextualFragment(responseHtml);
    if (!productChange) {
      const newVariantPicker = deepQuerySelector(newContent, `${this.tagName}[form-id="${this.getAttribute("form-id")}"]`);
      if (!newVariantPicker) {
        return;
      }
      const newVariant = JSON.parse(newVariantPicker.querySelector("script[data-variant]")?.textContent || "{}");
      __privateSet(this, _selectedVariant, newVariant);
      const idInput = __privateGet(this, _form).elements.namedItem("id");
      if (idInput) {
        idInput.value = __privateGet(this, _selectedVariant)?.id || "";
        idInput.disabled = !__privateGet(this, _selectedVariant)?.id;
        idInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
      if (this.updateUrl && __privateGet(this, _selectedVariant)?.id) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("variant", __privateGet(this, _selectedVariant).id);
        window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());
      }
    }
    __privateGet(this, _form).dispatchEvent(
      new CustomEvent("product:rerender", {
        detail: {
          htmlFragment: newContent,
          productChange
        }
      })
    );
    if (!productChange) {
      __privateGet(this, _form).dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: {
            formId: __privateGet(this, _form).id,
            variant: __privateGet(this, _selectedVariant),
            previousVariant
          }
        })
      );
    }
    if (this.getAttribute("context") !== "quick_buy") {
      Shopify?.PaymentButton?.init();
    }
  }
};
_preloadedHtml = new WeakMap();
_delegate4 = new WeakMap();
_form = new WeakMap();
_selectedVariant = new WeakMap();
_variants = new WeakMap();
_requestSequence = new WeakMap();
_selectionAbortController = new WeakMap();
_prefetchControllers = new WeakMap();
_VariantPicker_instances = new WeakSet();
/**
 * Get the option values for the active combination
 */
getActiveOptionValues_fn = function() {
  return Array.from(__privateGet(this, _form).elements).filter((item) => item.matches("input[data-option-position]:checked")).sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input) => input.value);
};
/**
 * Get the option values for a given input
 */
getOptionValuesFromOption_fn = function(input) {
  const optionValues = [input, ...Array.from(__privateGet(this, _form).elements).filter((item) => item.matches(`input[data-option-position]:not([name="${input.name}"]):checked`))].sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input2) => input2.value);
  return optionValues;
};
updateOptionAvailability_fn = function() {
  const form = __privateGet(this, _form);
  const variants = __privateGet(this, _variants);
  if (!form || !Array.isArray(variants) || !variants.length) return;
  const inputs = Array.from(form.elements).filter((item) => item.matches?.("input[data-option-position][data-option-value-name]"));
  const selectedByPosition = /* @__PURE__ */ new Map();
  inputs.filter((input) => input.checked).forEach((input) => {
    selectedByPosition.set(parseInt(input.dataset.optionPosition, 10), input.dataset.optionValueName);
  });
  inputs.forEach((input) => {
    const position = parseInt(input.dataset.optionPosition, 10);
    const candidate = input.dataset.optionValueName;
    if (!position || !candidate) return;
    const combinationExists = variants.some((variant) => {
      const options = variant && Array.isArray(variant.options) ? variant.options : [];
      if (String(options[position - 1] || "") !== String(candidate)) return false;
      for (let earlierPosition = 1; earlierPosition < position; earlierPosition++) {
        const selectedValue = selectedByPosition.get(earlierPosition);
        if (selectedValue != null && String(options[earlierPosition - 1] || "") !== String(selectedValue)) return false;
      }
      return true;
    });
    const label = input.id ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : input.closest("label");
    if (!combinationExists) {
      input.disabled = true;
      input.dataset.combinationUnavailable = "true";
      label?.classList.add("is-disabled");
    } else {
      input.disabled = false;
      delete input.dataset.combinationUnavailable;
      label?.classList.remove("is-disabled");
    }
  });
};
onOptionChanged_fn = async function(event) {
  if (!event.target.name.includes("option")) {
    return;
  }
  __privateMethod(this, _VariantPicker_instances, updateOptionAvailability_fn).call(this);
  this.selectCombination({
    optionValues: __privateMethod(this, _VariantPicker_instances, getActiveOptionValues_fn).call(this),
    productChange: event.target.hasAttribute("data-product-url")
  });
};
/**
 * Preload only a choice that received direct pointer, touch, or keyboard intent.
 */
onOptionPreload_fn = function(event, target) {
  if (this.getAttribute("context") === "quick_buy") {
    return;
  }
  const input = target.control || target;
  if (!input?.matches?.("input[data-option-position]") || input.disabled || __privateGet(this, _prefetchControllers).size >= VARIANT_PREFETCH_MAX_CONCURRENT) {
    return;
  }
  const controller = new AbortController();
  __privateGet(this, _prefetchControllers).add(controller);
  __privateMethod(this, _VariantPicker_instances, renderForCombination_fn).call(
    this,
    __privateMethod(this, _VariantPicker_instances, getOptionValuesFromOption_fn).call(this, input),
    { kind: "prefetch", signal: controller.signal, controller }
  ).catch((error) => {
    if (error?.name !== "AbortError") {
      console.warn("Unable to preload product combination.", error);
    }
  }).finally(() => {
    __privateGet(this, _prefetchControllers).delete(controller);
    });
  };
abortPrefetches_fn = function() {
  for (const controller of __privateGet(this, _prefetchControllers)) {
    controller.abort();
  }
  __privateGet(this, _prefetchControllers).clear();
};
prunePreloadedHtml_fn = function(protectedKey) {
  const cache = __privateGet(_VariantPicker, _preloadedHtml);
  const getTotalBytes = () => Array.from(cache.values()).reduce((total, entry) => total + (entry.bytes || 0), 0);
  while (cache.size > VARIANT_CACHE_MAX_ENTRIES || getTotalBytes() > VARIANT_CACHE_MAX_BYTES) {
    let candidateKey;
    for (const [key, entry] of cache) {
      if (key === protectedKey && cache.size > 1) continue;
      if (entry.html != null || entry.kind === "prefetch" || entry.controller?.signal.aborted) {
        candidateKey = key;
        break;
      }
    }
    if (candidateKey == null) break;
    const candidate = cache.get(candidateKey);
    if (candidate?.kind === "prefetch" && candidate.html == null) {
      candidate.controller?.abort();
    }
    cache.delete(candidateKey);
  }
};
renderForCombination_fn = function(optionValues, { kind = "selection", signal, controller } = {}) {
  const optionValuesAsString = optionValues.join(",");
  let productUrl = `${Shopify.routes.root}products/${this.productHandle}`;
  for (const optionValue of optionValues) {
    const inputForOptionValue = Array.from(__privateGet(this, _form).elements).find((item) => item.matches("input[data-option-position]") && String(item.value) === String(optionValue));
    if (inputForOptionValue?.dataset.productUrl) {
      productUrl = inputForOptionValue.dataset.productUrl;
      break;
    }
  }
  const hashKey = __privateMethod(this, _VariantPicker_instances, createHashKeyForHtml_fn).call(this, optionValuesAsString, productUrl);
  const cache = __privateGet(_VariantPicker, _preloadedHtml);
  let cachedEntry = cache.get(hashKey);
  if (cachedEntry && (Date.now() - cachedEntry.lastUsed > CACHE_EVICTION_TIME || cachedEntry.controller?.signal.aborted)) {
    cache.delete(hashKey);
    cachedEntry = void 0;
  }
  if (cachedEntry) {
    cache.delete(hashKey);
    cachedEntry.lastUsed = Date.now();
    cache.set(hashKey, cachedEntry);
    return cachedEntry.html != null ? Promise.resolve(cachedEntry.html) : cachedEntry.htmlPromise;
  }
    const sectionQueryParam = this.getAttribute("context") === "quick_buy" ? "" : `&section_id=${this.getAttribute("section-id")}`;
  const entry = {
    html: null,
    htmlPromise: null,
    bytes: 0,
    kind,
    controller,
    lastUsed: Date.now()
  };
  entry.htmlPromise = (async () => {
    try {
      const response = await fetch(`${productUrl}?option_values=${optionValuesAsString}${sectionQueryParam}`, { signal });
      if (!response.ok) {
        throw new Error(`Product combination request failed: ${response.status}`);
      }
      const html = await response.text();
      if (signal?.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
      entry.html = html;
      entry.htmlPromise = null;
      entry.bytes = html.length * 2;
      entry.lastUsed = Date.now();
      if (cache.get(hashKey) === entry) {
        cache.delete(hashKey);
        cache.set(hashKey, entry);
        __privateMethod(this, _VariantPicker_instances, prunePreloadedHtml_fn).call(this, hashKey);
      }
      return html;
    } catch (error) {
      if (cache.get(hashKey) === entry) {
        cache.delete(hashKey);
      }
      throw error;
    }
  })();
  cache.set(hashKey, entry);
  __privateMethod(this, _VariantPicker_instances, prunePreloadedHtml_fn).call(this, hashKey);
  return entry.htmlPromise;
};
createHashKeyForHtml_fn = function(optionValuesAsString, productUrl) {
  return `${this.productHandle}|${this.getAttribute("context") || "main"}|${productUrl}|${optionValuesAsString}|${this.getAttribute("section-id")}`;
};
__privateAdd(_VariantPicker, _preloadedHtml, /* @__PURE__ */ new Map());
var VariantPicker = _VariantPicker;
if (!window.customElements.get("variant-picker")) {
  window.customElements.define("variant-picker", VariantPicker);
}

// js/common/media/base-media.js
import { inView as inView7 } from "vendor";
var BaseMedia = class extends HTMLElement {
  static get observedAttributes() {
    return ["playing"];
  }
  connectedCallback() {
    this._abortController = new AbortController();
    if (this.hasAttribute("autoplay")) {
      inView7(this, this.play.bind(this), { margin: "0px 0px 0px 0px" });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get playing() {
    return this.hasAttribute("playing");
  }
  get player() {
    return this._playerProxy = this._playerProxy || new Proxy(this._playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target;
          this._playerHandler(target, prop);
        };
      }
    });
  }
  play() {
    if (!this.playing) {
      this.player.play();
    }
  }
  pause() {
    if (this.playing) {
      this.player.pause();
    }
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "playing") {
      if (oldValue === null && newValue === "") {
        this.dispatchEvent(new CustomEvent("media:play", { bubbles: true }));
        if (this.hasAttribute("group")) {
          Array.from(document.querySelectorAll(`[group="${this.getAttribute("group")}"]`)).filter((item) => item !== this).forEach((itemToPause) => {
            itemToPause.pause();
          });
        }
      } else if (newValue === null) {
        this.dispatchEvent(new CustomEvent("media:pause", { bubbles: true }));
      }
    }
  }
};

// js/common/media/model.js
var ModelMedia = class extends BaseMedia {
  connectedCallback() {
    super.connectedCallback();
    this.player;
  }
  _playerTarget() {
    return new Promise((resolve) => {
      this.setAttribute("loaded", "");
      window.Shopify.loadFeatures([
        {
          name: "shopify-xr",
          version: "1.0",
          onLoad: this._setupShopifyXr.bind(this)
        },
        {
          name: "model-viewer-ui",
          version: "1.0",
          onLoad: () => {
            const modelViewer = this.querySelector("model-viewer");
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_play", () => this.setAttribute("playing", ""));
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_pause", () => this.removeAttribute("playing"));
            resolve(new window.Shopify.ModelViewerUI(modelViewer, { focusOnPlay: true }));
          }
        }
      ]);
    });
  }
  _playerHandler(target, prop) {
    target[prop]();
  }
  async _setupShopifyXr() {
    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", this._setupShopifyXr.bind(this));
    } else {
      const models = (await ProductLoader.load(this.getAttribute("handle")))["media"].filter((media) => media["media_type"] === "model");
      window.ShopifyXR.addModels(models);
      window.ShopifyXR.setupXRElements();
    }
  }
};
if (!window.customElements.get("model-media")) {
  window.customElements.define("model-media", ModelMedia);
}

// js/common/media/video.js
import { inView as inView8 } from "vendor";
var onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});
var VideoMedia = class extends BaseMedia {
  #mustRemoveControlsAfterSuspend = false;
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute("autoplay")) {
      this.addEventListener("click", this.play, { once: true, signal: this._abortController.signal });
    }
    if (this.hasAttribute("show-play-button") && !this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(document.getElementById("video-media-default-template").content.cloneNode(true));
    }
    if (this.getAttribute("type") === "video") {
      inView8(
        this,
        () => {
          this.querySelector("video")?.setAttribute("preload", "metadata");
        },
        { margin: "800px" }
      );
    }
  }
  _playerTarget() {
    if (this.hasAttribute("host")) {
      this.setAttribute("loaded", "");
      return new Promise(async (resolve) => {
        const templateElement = this.querySelector("template");
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        const muteVideo = this.hasAttribute("autoplay") || matchesMediaQuery("md-max");
        const script = document.createElement("script");
        script.type = "text/javascript";
        if (this.getAttribute("host") === "youtube") {
          if (!window.YT || !window.YT.Player) {
            script.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          await onYouTubePromise;
          const player = new YT.Player(this.querySelector("iframe"), {
            events: {
              onReady: () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute("playing", "");
                } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute("playing");
                }
              }
            }
          });
        }
        if (this.getAttribute("host") === "vimeo") {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = "https://player.vimeo.com/api/player.js";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          const player = new Vimeo.Player(this.querySelector("iframe"));
          if (muteVideo) {
            player.setMuted(true);
          }
          player.on("play", () => {
            this.setAttribute("playing", "");
          });
          player.on("pause", () => this.removeAttribute("playing"));
          player.on("ended", () => this.removeAttribute("playing"));
          resolve(player);
        }
      });
    } else {
      const videoElement = this.querySelector("video");
      this.setAttribute("loaded", "");
      videoElement.addEventListener("play", () => {
        this.setAttribute("playing", "");
        this.removeAttribute("suspended");
        if (this.#mustRemoveControlsAfterSuspend) {
          videoElement.controls = false;
        }
      });
      videoElement.addEventListener("pause", () => {
        if (!videoElement.seeking && videoElement.paused) {
          this.removeAttribute("playing");
        }
      });
      return videoElement;
    }
  }
  _playerHandler(target, prop) {
    if (this.getAttribute("host") === "youtube") {
      prop === "play" ? target.playVideo() : target.pauseVideo();
    } else {
      if (prop === "play" && !this.hasAttribute("host")) {
        target.play().catch((error) => {
          if (error.name === "NotAllowedError") {
            this.setAttribute("suspended", "");
            if (!this.hasAttribute("controls")) {
              this.#mustRemoveControlsAfterSuspend = true;
              target.controls = true;
            }
          }
        });
      } else {
        target[prop]();
      }
    }
  }
};
if (!window.customElements.get("video-media")) {
  window.customElements.define("video-media", VideoMedia);
}

// js/common/navigation/accordion-disclosure.js
import { timeline as timeline5 } from "vendor";

// js/common/navigation/custom-details.js
var _onSummaryClickedListener, _CustomDetails_instances, onSummaryClicked_fn;
var CustomDetails = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _CustomDetails_instances);
    __privateAdd(this, _onSummaryClickedListener, __privateMethod(this, _CustomDetails_instances, onSummaryClicked_fn).bind(this));
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.toggle(true, !event.detail.load));
      this.addEventListener("shopify:block:deselect", (event) => this.toggle(false, !event.detail.load));
    }
  }
  static get observedAttributes() {
    return ["open", "aria-expanded"];
  }
  connectedCallback() {
    this.disclosureElement.setAttribute("aria-expanded", this.disclosureElement.open ? "true" : "false");
    this.summaryElement.addEventListener("click", __privateGet(this, _onSummaryClickedListener));
  }
  disconnectedCallback() {
    this.summaryElement.removeEventListener("click", __privateGet(this, _onSummaryClickedListener));
  }
  get disclosureElement() {
    return this.querySelector("details");
  }
  get summaryElement() {
    return this.disclosureElement.firstElementChild;
  }
  get contentElement() {
    return this.disclosureElement.lastElementChild;
  }
  toggle(force = void 0, animate27 = true) {
    const newValue = typeof force === "boolean" ? force : !(this.disclosureElement.getAttribute("aria-expanded") === "true");
    if (newValue) {
      this.open({ instant: !animate27 });
    } else {
      this.close();
    }
  }
  async open({ instant = false } = {}) {
    if (this.disclosureElement.getAttribute("aria-expanded") === "true") {
      return;
    }
    this.disclosureElement.open = true;
    this.disclosureElement.setAttribute("aria-expanded", "true");
    const controls = this.createShowAnimationControls();
    if (instant) {
      controls.finish();
    }
  }
  async close() {
    this.disclosureElement.setAttribute("aria-expanded", "false");
    if (!this.disclosureElement.open) {
      return;
    }
    this.createHideAnimationControls()?.finished.then((event) => {
      if (event !== void 0) {
        this.disclosureElement.removeAttribute("open");
      }
    });
  }
  createShowAnimationControls() {
  }
  createHideAnimationControls() {
  }
};
_onSummaryClickedListener = new WeakMap();
_CustomDetails_instances = new WeakSet();
/**
 * By default, when clicking on the summary, the browser directly toggle the "open" attribute, which prevent to
 * perform animation. We therefore block that to allow doing an animation
 */
onSummaryClicked_fn = function(event) {
  event.preventDefault();
  if (this.disclosureElement.open && this.summaryElement.hasAttribute("data-follow-link")) {
    return window.location.href = this.summaryElement.getAttribute("data-follow-link");
  }
  this.toggle();
};

// js/common/navigation/accordion-disclosure.js
var AccordionDisclosure = class extends CustomDetails {
  async open({ instant = false } = {}) {
    const group = this.closest(".accordion-group");
    if (group) {
      group.querySelectorAll("accordion-disclosure").forEach((sibling) => {
        if (sibling !== this) {
          sibling.close();
        }
      });
    }
    return super.open({ instant });
  }
  createShowAnimationControls() {
    this.disclosureElement.style.overflow = "hidden";
    const animationControls = timeline5([
      [this.disclosureElement, { height: [`${this.summaryElement.clientHeight}px`, `${this.disclosureElement.scrollHeight}px`] }, { duration: 0.25, easing: "ease" }],
      [this.contentElement, { opacity: [0, 1], transform: ["translateY(4px)", `translateY(0)`] }, { duration: 0.15, at: "-0.1" }]
    ]);
    animationControls.finished.then(() => {
      this.disclosureElement.style.height = null;
      this.disclosureElement.style.overflow = null;
    });
    return animationControls;
  }
  createHideAnimationControls() {
    const animationControls = timeline5([
      [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
      [this.disclosureElement, { height: [`${this.disclosureElement.clientHeight}px`, `${this.summaryElement.clientHeight}px`] }, { duration: 0.25, at: "<", easing: "ease" }]
    ]);
    animationControls.finished.then(() => {
      this.disclosureElement.style.height = null;
    });
    return animationControls;
  }
};
if (!window.customElements.get("accordion-disclosure")) {
  window.customElements.define("accordion-disclosure", AccordionDisclosure);
}

// js/common/navigation/menu-disclosure.js
var _hoverTimer, _detectClickOutsideListener, _detectEscKeyboardListener, _detectFocusOutListener, _detectHoverOutsideListener, _detectHoverListener, _MenuDisclosure_instances, handleKeyboard_fn, detectClickOutside_fn, detectHover_fn, detectHoverOutside_fn, detectEscKeyboard_fn, detectFocusOut_fn;
var _MenuDisclosure = class _MenuDisclosure extends CustomDetails {
  constructor() {
    super();
    __privateAdd(this, _MenuDisclosure_instances);
    __privateAdd(this, _hoverTimer);
    __privateAdd(this, _detectClickOutsideListener, __privateMethod(this, _MenuDisclosure_instances, detectClickOutside_fn).bind(this));
    __privateAdd(this, _detectEscKeyboardListener, __privateMethod(this, _MenuDisclosure_instances, detectEscKeyboard_fn).bind(this));
    __privateAdd(this, _detectFocusOutListener, __privateMethod(this, _MenuDisclosure_instances, detectFocusOut_fn).bind(this));
    __privateAdd(this, _detectHoverOutsideListener, __privateMethod(this, _MenuDisclosure_instances, detectHoverOutside_fn).bind(this));
    __privateAdd(this, _detectHoverListener, __privateMethod(this, _MenuDisclosure_instances, detectHover_fn).bind(this));
    this.disclosureElement.addEventListener("mouseover", __privateGet(this, _detectHoverListener).bind(this));
    this.disclosureElement.addEventListener("mouseout", __privateGet(this, _detectHoverListener).bind(this));
    this.disclosureElement.addEventListener("keydown", __privateMethod(this, _MenuDisclosure_instances, handleKeyboard_fn).bind(this));
  }
  /**
   * Get the trigger mode (can be "click" or "hover"). However, for touch devices, it is always forced to click
   * to provide a better user experience
   */
  get trigger() {
    return !window.matchMedia("screen and (pointer: fine)").matches ? "click" : this.getAttribute("trigger");
  }
  /**
   * In ms, describe the delay before which we close the menu
   */
  get mouseOverDelayTolerance() {
    return 250;
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PRIVATE API
   * -------------------------------------------------------------------------------------------------------------------
   */
  async open({ instant = false } = {}) {
    super.open({ instant });
    document.addEventListener("click", __privateGet(this, _detectClickOutsideListener));
    document.addEventListener("keydown", __privateGet(this, _detectEscKeyboardListener));
    document.addEventListener("focusout", __privateGet(this, _detectFocusOutListener));
    document.addEventListener("mouseover", __privateGet(this, _detectHoverOutsideListener));
  }
  async close() {
    super.close();
    document.removeEventListener("click", __privateGet(this, _detectClickOutsideListener));
    document.removeEventListener("keydown", __privateGet(this, _detectEscKeyboardListener));
    document.removeEventListener("focusout", __privateGet(this, _detectFocusOutListener));
    document.removeEventListener("mouseover", __privateGet(this, _detectHoverOutsideListener));
  }
};
_hoverTimer = new WeakMap();
_detectClickOutsideListener = new WeakMap();
_detectEscKeyboardListener = new WeakMap();
_detectFocusOutListener = new WeakMap();
_detectHoverOutsideListener = new WeakMap();
_detectHoverListener = new WeakMap();
_MenuDisclosure_instances = new WeakSet();
/**
 * Handle the keyboard events to ensure we can close the menu with Esc
 *
 * @param {KeyboardEvent} event
 */
handleKeyboard_fn = function(event) {
  if (event.key === "Escape") {
    this.close();
    this.summaryElement.focus();
  }
};
/**
 * When dropdown menu is configured to open on click, we add a listener to detect click outside and automatically
 * close the navigation.
 */
detectClickOutside_fn = function(event) {
  if (this.trigger !== "click") {
    return;
  }
  if (!this.contains(event.target) && !(event.target.closest("details") instanceof _MenuDisclosure)) {
    this.toggle(false);
  }
};
/**
 * On desktop device, if the mode is set to hover, we open/close the dropdown on hover
 */
detectHover_fn = function(event) {
  if (this.trigger !== "hover") {
    return;
  }
  if (event.type === "mouseover") {
    clearTimeout(__privateGet(this, _hoverTimer));
    this.toggle(true);
  } else if (event.type === "mouseout") {
    __privateSet(this, _hoverTimer, setTimeout(() => this.toggle(false), this.mouseOverDelayTolerance));
  }
};
/**
 * Try to detect when the user hover a different link, to immediately close the item without extra delay
 */
detectHoverOutside_fn = function(event) {
  if (this.trigger !== "hover") {
    return;
  }
  const closestDetails = event.target.closest("details");
  if (closestDetails instanceof _MenuDisclosure && closestDetails !== this && !closestDetails.contains(this) && !this.contains(closestDetails)) {
    clearTimeout(__privateGet(this, _hoverTimer));
    this.toggle(false);
  }
};
/**
 * Detect if we hit the "Escape" key to automatically close the dropdown
 */
detectEscKeyboard_fn = function(event) {
  if (event.code === "Escape") {
    const targetMenu = event.target.closest("details[open]");
    if (targetMenu && targetMenu instanceof _MenuDisclosure) {
      targetMenu.toggle(false);
      event.stopPropagation();
    }
  }
};
/**
 * Close the dropdown automatically when the dropdown is focused out
 */
detectFocusOut_fn = function(event) {
  if (event.relatedTarget && !this.contains(event.relatedTarget)) {
    this.toggle(false);
  }
};
var MenuDisclosure = _MenuDisclosure;

// js/common/navigation/tabs.js
import { Delegate as Delegate6, animate as animate10, timeline as timeline6 } from "vendor";
var _componentID, _buttons, _panels, _delegate5, _Tabs_instances, setupComponent_fn, onButtonClicked_fn, onSlotChange_fn, handleKeyboard_fn2;
var Tabs = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _Tabs_instances);
    __privateAdd(this, _componentID, crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4));
    __privateAdd(this, _buttons, []);
    __privateAdd(this, _panels, []);
    __privateAdd(this, _delegate5, new Delegate6(this));
    if (!this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(this.querySelector("template").content.cloneNode(true));
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.selectedIndex = __privateGet(this, _buttons).indexOf(event.target));
    }
    __privateGet(this, _delegate5).on("click", 'button[role="tab"]', __privateMethod(this, _Tabs_instances, onButtonClicked_fn).bind(this));
    this.shadowRoot.addEventListener("slotchange", __privateMethod(this, _Tabs_instances, onSlotChange_fn).bind(this));
    this.addEventListener("keydown", __privateMethod(this, _Tabs_instances, handleKeyboard_fn2));
  }
  static get observedAttributes() {
    return ["selected-index"];
  }
  connectedCallback() {
    __privateMethod(this, _Tabs_instances, setupComponent_fn).call(this);
    this.selectedIndex = this.selectedIndex;
  }
  disconnectedCallback() {
    __privateGet(this, _delegate5).destroy();
  }
  /**
   * --------------------------------------------------------------------------
   * GETTERS AND SETTERS
   * --------------------------------------------------------------------------
   */
  get animationDuration() {
    return this.hasAttribute("animation-duration") ? parseFloat(this.getAttribute("animation-duration")) : 0.3;
  }
  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }
  set selectedIndex(index) {
    this.setAttribute("selected-index", Math.min(Math.max(index, 0), Math.max(0, __privateGet(this, _buttons).length - 1)).toString());
    this.style.setProperty("--selected-index", this.selectedIndex.toString());
  }
  /**
   * --------------------------------------------------------------------------
   * METHODS
   * --------------------------------------------------------------------------
   */
  attributeChangedCallback(name, oldValue, newValue) {
    __privateGet(this, _buttons).forEach((button, index) => button.setAttribute("aria-selected", index === parseInt(newValue) ? "true" : "false"));
    if (name === "selected-index" && oldValue !== null && oldValue !== newValue) {
      this.transition(__privateGet(this, _panels)[parseInt(oldValue)], __privateGet(this, _panels)[parseInt(newValue)]);
    }
  }
  /**
   * Perform a custom transition (can be overridden in subclasses). To "from" and "to" are hash representing the panel
   */
  async transition(fromPanel, toPanel) {
    const beforeHeight = this.clientHeight;
    await animate10(fromPanel, { transform: ["translateY(0px)", "translateY(10px)"], opacity: [1, 0] }, { duration: this.animationDuration }).finished;
    fromPanel.hidden = true;
    toPanel.hidden = false;
    await timeline6([
      [this, { height: [`${beforeHeight}px`, `${this.clientHeight}px`], overflow: ["hidden", "visible"] }, { duration: 0.15, easing: [0.85, 0, 0.15, 1] }],
      [toPanel, { transform: ["translateY(10px)", "translateY(0px)"], opacity: [0, 1] }, { duration: this.animationDuration, at: "+0.1" }]
    ]).finished;
    this.style.removeProperty("height");
  }
};
_componentID = new WeakMap();
_buttons = new WeakMap();
_panels = new WeakMap();
_delegate5 = new WeakMap();
_Tabs_instances = new WeakSet();
setupComponent_fn = function() {
  __privateSet(this, _buttons, Array.from(
    this.shadowRoot.querySelector('slot[name="title"]')?.assignedNodes() ?? [],
    (item) => item.matches("button") && item || item.querySelector("button")
  ));
  __privateSet(this, _panels, Array.from(this.shadowRoot.querySelector('slot[name="content"]')?.assignedNodes() ?? []));
  __privateGet(this, _buttons).forEach((button, index) => {
    button.setAttribute("role", "tab");
    button.setAttribute("aria-controls", `tab-panel-${__privateGet(this, _componentID)}-${index}`);
    button.id = `tab-${__privateGet(this, _componentID)}-${index}`;
  });
  __privateGet(this, _panels).forEach((panel, index) => {
    panel.setAttribute("role", "tabpanel");
    panel.setAttribute("aria-labelledby", `tab-${__privateGet(this, _componentID)}-${index}`);
    panel.id = `tab-panel-${__privateGet(this, _componentID)}-${index}`;
    panel.hidden = index !== this.selectedIndex;
  });
  this.style.setProperty("--item-count", __privateGet(this, _buttons).length.toString());
};
onButtonClicked_fn = function(event, button) {
  this.selectedIndex = __privateGet(this, _buttons).indexOf(button);
};
onSlotChange_fn = function() {
  __privateMethod(this, _Tabs_instances, setupComponent_fn).call(this);
};
/**
 * As per https://www.w3.org/WAI/ARIA/apg/example-index/tabs/tabs-automatic.html, when a tab is currently focused,
 * left and right arrow should switch the tab
 */
handleKeyboard_fn2 = function(event) {
  const index = __privateGet(this, _buttons).indexOf(document.activeElement);
  if (index === -1 || !["ArrowLeft", "ArrowRight"].includes(event.key)) {
    return;
  }
  if (event.key === "ArrowLeft") {
    this.selectedIndex = (this.selectedIndex - 1 + __privateGet(this, _buttons).length) % __privateGet(this, _buttons).length;
  } else {
    this.selectedIndex = (this.selectedIndex + 1 + __privateGet(this, _buttons).length) % __privateGet(this, _buttons).length;
  }
  __privateGet(this, _buttons)[this.selectedIndex].focus();
};
if (!window.customElements.get("x-tabs")) {
  window.customElements.define("x-tabs", Tabs);
}

// js/common/search/predictive-search.js
var _listenersAbortController2, _fetchAbortController, _searchForm, _queryInput, _PredictiveSearch_instances, onInputChanged_fn2, onFormSubmitted_fn2, doPredictiveSearch_fn, onSearchCleared_fn;
var PredictiveSearch = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _PredictiveSearch_instances);
    __privateAdd(this, _listenersAbortController2);
    __privateAdd(this, _fetchAbortController);
    __privateAdd(this, _searchForm);
    __privateAdd(this, _queryInput);
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(document.createRange().createContextualFragment(`<slot name="results"></slot>`));
  }
  connectedCallback() {
    __privateSet(this, _listenersAbortController2, new AbortController());
    __privateSet(this, _searchForm, document.querySelector(`[aria-owns="${this.id}"]`));
    __privateSet(this, _queryInput, __privateGet(this, _searchForm).elements["q"]);
    __privateGet(this, _searchForm).addEventListener("submit", __privateMethod(this, _PredictiveSearch_instances, onFormSubmitted_fn2).bind(this), { signal: __privateGet(this, _listenersAbortController2).signal });
    __privateGet(this, _searchForm).addEventListener("reset", __privateMethod(this, _PredictiveSearch_instances, onSearchCleared_fn).bind(this), { signal: __privateGet(this, _listenersAbortController2).signal });
    __privateGet(this, _queryInput).addEventListener("input", debounce(__privateMethod(this, _PredictiveSearch_instances, onInputChanged_fn2).bind(this), this.autoCompleteDelay, { signal: __privateGet(this, _listenersAbortController2).signal }));
  }
  disconnectedCallback() {
    __privateGet(this, _listenersAbortController2).abort();
  }
  /**
   * Return the delay in ms before we send the autocomplete request. Using a value too low can cause the results to
   * refresh too often, so we recommend to keep the default one
   */
  get autoCompleteDelay() {
    return 280;
  }
  /**
   * Check if the store supports the predictive API (some languages do not). When not supported, the predictive
   * search is simply disabled and only the standard search is used
   */
  supportsPredictiveApi() {
    return JSON.parse(document.getElementById("shopify-features").innerHTML)["predictiveSearch"];
  }
};
_listenersAbortController2 = new WeakMap();
_fetchAbortController = new WeakMap();
_searchForm = new WeakMap();
_queryInput = new WeakMap();
_PredictiveSearch_instances = new WeakSet();
/**
 * Check if the input is not empty, and if so, trigger the predictive search
 */
onInputChanged_fn2 = function() {
  if (__privateGet(this, _queryInput).value === "") {
    return __privateMethod(this, _PredictiveSearch_instances, onSearchCleared_fn).call(this);
  }
  __privateGet(this, _fetchAbortController)?.abort();
  __privateSet(this, _fetchAbortController, new AbortController());
  try {
    return __privateMethod(this, _PredictiveSearch_instances, doPredictiveSearch_fn).call(this);
  } catch (e) {
    if (e.name !== "AbortError") {
      throw e;
    }
  }
};
/**
 * Prevent the form submission if the query is empty
 */
onFormSubmitted_fn2 = function(event) {
  if (__privateGet(this, _queryInput).value === "") {
    return event.preventDefault();
  }
};
doPredictiveSearch_fn = async function() {
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  const url = `${window.Shopify.routes.root}search${this.supportsPredictiveApi() ? "/suggest" : ""}`, queryParams = `q=${encodeURIComponent(__privateGet(this, _queryInput).value)}&section_id=predictive-search&resources[limit]=10&resources[limit_scope]=each`, tempDoc = new DOMParser().parseFromString(await (await cachedFetch(`${url}?${queryParams}`, { signal: __privateGet(this, _fetchAbortController).signal })).text(), "text/html");
  this.querySelector('[slot="results"]').replaceChildren(...document.importNode(tempDoc.querySelector(".shopify-section"), true).children);
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
};
/**
 * If any search is pending, we abort them, and transition to the idle slot
 */
onSearchCleared_fn = function() {
  __privateGet(this, _fetchAbortController)?.abort();
  __privateGet(this, _queryInput).focus();
  this.querySelector('[slot="results"]').innerHTML = "";
};
if (!window.customElements.get("predictive-search")) {
  window.customElements.define("predictive-search", PredictiveSearch);
}

// js/sections/announcement-bar.js
import { animate as animate11 } from "vendor";
var AnnouncementBarCarousel = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return {
      leaveControls: () => animate11(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-10px)"] }, { duration: 0.25, easing: [0.55, 0.055, 0.675, 0.19] }),
      enterControls: () => animate11(toSlide, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0px)"] }, { duration: 0.4, easing: [0.215, 0.61, 0.355, 1] })
    };
  }
};
if (!window.customElements.get("announcement-bar-carousel")) {
  window.customElements.define("announcement-bar-carousel", AnnouncementBarCarousel);
}

// js/sections/before-after-image.js
import { animate as animate12, inView as inView9 } from "vendor";
var _onPointerMoveListener, _touchStartTimestamp, _BeforeAfter_instances, onPointerDown_fn, onPointerMove_fn, onTouchStart_fn, onPointerUp_fn, onKeyboardNavigation_fn2, calculatePosition_fn, animateInitialPosition_fn;
var BeforeAfter = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _BeforeAfter_instances);
    __privateAdd(this, _onPointerMoveListener, __privateMethod(this, _BeforeAfter_instances, onPointerMove_fn).bind(this));
    __privateAdd(this, _touchStartTimestamp, 0);
    this.addEventListener("pointerdown", __privateMethod(this, _BeforeAfter_instances, onPointerDown_fn));
    this.addEventListener("keydown", __privateMethod(this, _BeforeAfter_instances, onKeyboardNavigation_fn2));
    this.addEventListener("touchstart", __privateMethod(this, _BeforeAfter_instances, onTouchStart_fn), { passive: false });
  }
  connectedCallback() {
    inView9(this, __privateMethod(this, _BeforeAfter_instances, animateInitialPosition_fn).bind(this));
  }
};
_onPointerMoveListener = new WeakMap();
_touchStartTimestamp = new WeakMap();
_BeforeAfter_instances = new WeakSet();
onPointerDown_fn = function(event) {
  if (event.target.tagName === "A") {
    return;
  }
  document.addEventListener("pointerup", __privateMethod(this, _BeforeAfter_instances, onPointerUp_fn).bind(this), { once: true });
  if (matchesMediaQuery("supports-hover")) {
    document.addEventListener("pointermove", __privateGet(this, _onPointerMoveListener));
    __privateMethod(this, _BeforeAfter_instances, calculatePosition_fn).call(this, event);
  }
};
onPointerMove_fn = function(event) {
  __privateMethod(this, _BeforeAfter_instances, calculatePosition_fn).call(this, event);
};
onTouchStart_fn = function(event) {
  const cursor = this.querySelector(".before-after__cursor");
  if (event.target === cursor || cursor.contains(event.target)) {
    event.preventDefault();
    document.addEventListener("pointermove", __privateGet(this, _onPointerMoveListener));
  } else {
    __privateSet(this, _touchStartTimestamp, event.timeStamp);
  }
};
onPointerUp_fn = function(event) {
  document.removeEventListener("pointermove", __privateGet(this, _onPointerMoveListener));
  if (!matchesMediaQuery("supports-hover")) {
    if (event.timeStamp - __privateGet(this, _touchStartTimestamp) <= 250) {
      __privateMethod(this, _BeforeAfter_instances, calculatePosition_fn).call(this, event);
    }
  }
};
onKeyboardNavigation_fn2 = function(event) {
  if (!event.target.classList.contains("before-after__cursor") || !this.hasAttribute("vertical") && event.code !== "ArrowLeft" && event.code !== "ArrowRight" || this.hasAttribute("vertical") && event.code !== "ArrowUp" && event.code !== "ArrowDown") {
    return;
  }
  event.preventDefault();
  let currentPosition = parseInt(this.style.getPropertyValue("--before-after-cursor-position"));
  if (Number.isNaN(currentPosition)) {
    currentPosition = parseInt(getComputedStyle(this).getPropertyValue("--before-after-initial-cursor-position"));
  }
  let newPosition;
  if (this.hasAttribute("vertical")) {
    newPosition = event.code === "ArrowUp" ? currentPosition - 1 : currentPosition + 1;
  } else {
    newPosition = event.code === "ArrowLeft" ? currentPosition - 1 : currentPosition + 1;
  }
  this.style.setProperty("--before-after-cursor-position", `${Math.min(Math.max(newPosition, 0), 100)}%`);
};
calculatePosition_fn = function(event) {
  let rectangle = this.getBoundingClientRect(), percentage;
  if (this.hasAttribute("vertical")) {
    percentage = (event.clientY - rectangle.top) / this.clientHeight * 100;
  } else {
    percentage = (event.clientX - rectangle.left) / this.clientWidth * 100;
    percentage = document.dir === "rtl" ? 100 - percentage : percentage;
  }
  this.style.setProperty("--before-after-cursor-position", `${Math.min(Math.max(percentage, 0), 100)}%`);
};
animateInitialPosition_fn = function() {
  animate12(
    (progress) => {
      this.style.setProperty("--before-after-cursor-position", `calc(var(--before-after-initial-cursor-position) * ${progress})`);
    },
    { duration: 0.6, easing: [0.85, 0, 0.15, 1] }
  );
};
if (!window.customElements.get("before-after")) {
  window.customElements.define("before-after", BeforeAfter);
}

// js/sections/blog-posts.js
import { animate as animate13, stagger as stagger2, inView as inView10 } from "vendor";
var _BlogPosts_instances, reveal_fn;
var BlogPosts = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _BlogPosts_instances);
    if (this.hasAttribute("reveal-on-scroll") && matchesMediaQuery("motion-safe")) {
      inView10(this, __privateMethod(this, _BlogPosts_instances, reveal_fn).bind(this), { margin: "-50px 0px" });
    }
  }
};
_BlogPosts_instances = new WeakSet();
reveal_fn = function() {
  this.style.opacity = "1";
  animate13(this.children, { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] }, { duration: 0.25, delay: stagger2(0.1, { easing: "ease-out" }), easing: "ease" });
};
if (!window.customElements.get("blog-posts")) {
  window.customElements.define("blog-posts", BlogPosts);
}

// js/sections/cart-drawer.js
import { animate as animate14, timeline as timeline7 } from "vendor";
// Demote hyphenated custom elements so Chrome does not upgrade a large CE tree
// during cart-drawer mount (Aw Snap / RESULT_CODE_KILLED_BAD_MESSAGE).
var __jwDemoteCustomElementsHtml = function(html) {
  if (typeof html !== "string" || !html) return html;
  return html.replace(/<\/?([a-z][\w]*-[\w-]+)\b([^>]*)>/gi, function(match, tag, rest) {
    var lower = String(tag).toLowerCase();
    if (match.charAt(1) === "/") return "</div>";
    return '<div data-jw-ce="' + lower + '"' + rest + ">";
  });
};
var __jwEscHtml = function(str) {
  return String(str == null ? "" : str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
};
/**
 * Normalize Shopify image fields to an absolute HTTPS CDN URL.
 * products.json often returns featured_image:null and images[0] as {src}, not a string —
 * String(object) becomes "[object Object]" and breaks <img src>.
 */
var __jwAbsCdnUrl = function(input, width) {
  var url = "";
  if (typeof input === "string") {
    url = input;
  } else if (input && typeof input === "object") {
    url = input.src || input.url || "";
  }
  url = String(url || "").trim();
  if (!url || url === "[object Object]") return "";
  if (url.indexOf("//") === 0) url = "https:" + url;
  else if (url.indexOf("http://") === 0) url = "https://" + url.slice(7);
  else if (url.indexOf("https://") !== 0 && url.indexOf("/") === 0) {
    // Rare relative path — leave as-is for same-origin; prefer CDN when possible.
  }
  if (width && url && url.indexOf("width=") === -1) {
    url += (url.indexOf("?") === -1 ? "?" : "&") + "width=" + width;
  }
  return url;
};
/** Storefront products.json / suggest.json prices are dollars; Liquid / *.js are cents. */
var __jwDollarsToCents = function(amount) {
  var n = Number(amount);
  if (!isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100);
};
var __jwFormatMoney = function(cents) {
  var format = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat || "${{amount}}";
  var value = (Math.round(Number(cents) || 0) / 100).toFixed(2);
  var noDecimals = String(Math.round((Number(cents) || 0) / 100));
  var withComma = value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return String(format).replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma).replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals).replace(/\{\{\s*amount\s*\}\}/g, value);
};
var __jwReadCartDrawerConfig = function(host) {
  if (!host) return { recommendations: { products: [] }, trustHtml: "", emptyHtml: "", protectionHtml: "", protectionVariantId: "", protectionAutoAdd: false };
  if (host.__jwPlainCartConfig) return host.__jwPlainCartConfig;
  var recommendations = { products: [] };
  var configNode = host.querySelector("[data-jw-cart-recommendation-config]");
  var trustTemplate = host.querySelector("template[data-jw-cart-trust-template]");
  var emptyTemplate = host.querySelector("template[data-jw-cart-empty-template]");
  var protectionTemplate = host.querySelector("template[data-jw-cart-protection-template]");
  var rawConfigJson = configNode ? String(configNode.textContent || "") : "";
  try {
    // cartCompareAt historically emitted unquoted numeric keys; repair before parse.
    var jsonText = rawConfigJson.replace(/([\{,]\s*)(\d+)\s*:/g, '$1"$2":');
    recommendations = rawConfigJson ? JSON.parse(jsonText) : recommendations;
  } catch (e) {
  }
  if (!recommendations || typeof recommendations !== "object") recommendations = { products: [] };
  if (!Array.isArray(recommendations.products)) recommendations.products = [];
  // Preserve the full catalog pool forever — render-time filters in-cart items.
  recommendations.fallbackProducts = recommendations.products.slice(0, 24);
  try {
    var recentIds = JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]");
    if (Array.isArray(recentIds) && Array.isArray(recommendations.products)) {
      recentIds = recentIds.slice(0, 12).map(String);
      var recentRank = new Map(recentIds.map(function(id, index) {
        return [id, index];
      }));
      recommendations.products = recommendations.products.map(function(product, index) {
        return { product, index };
      }).sort(function(a, b) {
        var rankA = recentRank.has(String(a.product.productId)) ? recentRank.get(String(a.product.productId)) : recentIds.length + a.index;
        var rankB = recentRank.has(String(b.product.productId)) ? recentRank.get(String(b.product.productId)) : recentIds.length + b.index;
        return rankA - rankB;
      }).map(function(entry) {
        return entry.product;
      }).slice(0, 24);
    }
  } catch (e) {
  }
  host.__jwPlainCartConfig = {
    recommendations,
    trustHtml: trustTemplate ? trustTemplate.innerHTML : "",
    emptyHtml: emptyTemplate ? emptyTemplate.innerHTML : "",
    protectionHtml: protectionTemplate ? protectionTemplate.innerHTML : "",
    protectionVariantId: host.getAttribute("data-jw-protection-variant-id") || "",
    protectionAutoAdd: host.getAttribute("data-jw-protection-auto-add") === "true",
    // Persist a parseable snapshot so later rebuilds keep promo lines even if Liquid regresses.
    configJson: JSON.stringify(recommendations)
  };
  return host.__jwPlainCartConfig;
};
var __jwBuildCartProtectionHtml = function(cart, config) {
  if (!config || !config.protectionHtml || !config.protectionVariantId) return "";
  var host = document.getElementById("cart-drawer");
  if (host && host.getAttribute("data-jw-protection-variant-id") === "") return "";
  var variantId = String(config.protectionVariantId);
  var items = cart && Array.isArray(cart.items) ? cart.items : [];
  var hasProtection = items.some(function(item) {
    return String(item && item.variant_id || "") === variantId;
  });
  var hasMerchandise = items.some(function(item) {
    return String(item && item.variant_id || "") !== variantId;
  });
  var dismissed = false;
  try {
    dismissed =
      sessionStorage.getItem("jw_shipping_protection_declined") === "1" ||
      sessionStorage.getItem("jw-protection-dismissed") === "1";
  } catch (e) {
  }
  var template = document.createElement("template");
  template.innerHTML = config.protectionHtml;
  var panel = template.content.querySelector("[data-jw-protection-panel]");
  if (!panel) return "";
  panel.setAttribute("data-has-merch", hasMerchandise ? "true" : "false");
  panel.hidden = !hasMerchandise;
  var toggle = panel.querySelector("[data-jw-protection-toggle]");
  if (toggle) toggle.checked = hasProtection || hasMerchandise && config.protectionAutoAdd && !dismissed;
  var status = panel.querySelector("[data-jw-protection-status]");
  if (status) status.textContent = hasProtection ? "Added · uncheck to remove" : dismissed ? "Removed for this session" : "";
  return template.innerHTML;
};
var __jwBuildCartRecommendationsHtml = function(cart, config) {
  if (config && config.enabled === false) return "";
  var host = document.getElementById("cart-drawer");
  if (host && host.getAttribute("data-jw-cross-sell-enable") === "false") return "";
  var products = config && Array.isArray(config.products) ? config.products : [];
  var items = cart && Array.isArray(cart.items) ? cart.items : [];
  var inCart = new Set(items.map(function(item) {
    return String(item && item.product_id || "");
  }));
  var cards = [];
  for (var i = 0; i < products.length; i++) {
    var product = products[i] || {};
    if (!product.productId || inCart.has(String(product.productId))) continue;
    var price = Number(product.price) || 0;
    var compareAt = Number(product.compareAtPrice) || 0;
    var variantId = product.variantId || "";
    var imageSrc = __jwAbsCdnUrl(product.image, 360);
    var priceRow = '<div class="jw-cart-recommendation__price-row">' + (compareAt > price ? '<s class="jw-cart-recommendation__compare">' + __jwFormatMoney(compareAt) + '</s><span class="jw-cart-recommendation__price jw-cart-recommendation__price--sale">' + __jwFormatMoney(price) + "</span>" : '<span class="jw-cart-recommendation__price">' + __jwFormatMoney(price) + "</span>") + "</div>";
    cards.push(
      '<article class="jw-cart-recommendation" data-product-id="' + __jwEscHtml(product.productId) + '" data-product-handle="' + __jwEscHtml(product.handle || "") + '">' +
      '<a class="jw-cart-recommendation__media" href="' + __jwEscHtml(product.url || "#") + '" aria-label="View ' + __jwEscHtml(product.title || "") + '">' +
      (imageSrc ? '<img class="jw-cart-recommendation__image" src="' + __jwEscHtml(imageSrc) + '" alt="' + __jwEscHtml(product.title || "") + '" width="104" height="104" loading="lazy" decoding="async">' : "") +
      '</a><div class="jw-cart-recommendation__info"><a class="jw-cart-recommendation__title" href="' + __jwEscHtml(product.url || "#") + '">' + __jwEscHtml(product.title || "") + "</a>" +
      priceRow +
      '</div><button type="button" class="jw-cart-recommendation__add" data-jw-rec-add data-variant-id="' + __jwEscHtml(variantId) + '" data-product-id="' + __jwEscHtml(product.productId) + '" data-product-handle="' + __jwEscHtml(product.handle || "") + '" data-product-title="' + __jwEscHtml(product.title || "") + '" aria-label="Quick add"><span class="sr-only">Quick add</span><svg aria-hidden="true" focusable="false" fill="none" width="12" viewBox="0 0 12 12"><path d="M6 0v12M0 6h12" stroke="currentColor" stroke-width="1"/></svg></button></article>'
    );
  }
  if (!cards.length) return "";
  var nav = cards.length > 1 ? '<div class="jw-cart-recommendations__nav"><button type="button" class="jw-cart-recommendations__arrow" data-jw-rec-dir="-1" aria-label="Previous recommendation">‹</button><button type="button" class="jw-cart-recommendations__arrow" data-jw-rec-dir="1" aria-label="Next recommendation">›</button></div>' : "";
  return '<section class="jw-cart-recommendations" aria-label="PEOPLE ALSO BOUGHT"><div class="jw-cart-recommendations__header"><p>PEOPLE ALSO BOUGHT</p>' + nav + '</div><div class="jw-cart-recommendations__viewport"><div class="jw-cart-recommendations__list">' + cards.join("") + "</div></div></section>";
};
var __jwCartRecentSource = {
  signature: "",
  controller: null,
  promise: null
};
var __jwLoadCartRecentProducts = function(drawer, cart, drawerConfig) {
  var recommendationConfig = drawerConfig && drawerConfig.recommendations;
  if (!recommendationConfig || !Array.isArray(recommendationConfig.products)) return;
  var recentIds = [];
  try {
    var parsed = JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]");
    if (Array.isArray(parsed)) {
      recentIds = Array.from(new Set(parsed.map(String).filter(function(id) {
        return /^\d+$/.test(id);
      }))).slice(0, 10);
    }
  } catch (error) {
  }
  var cartProductIds = new Set((Array.isArray(cart && cart.items) ? cart.items : []).map(function(item) {
    return String(item && item.product_id || "");
  }));
  if (!Array.isArray(recommendationConfig.fallbackProducts) || !recommendationConfig.fallbackProducts.length) {
    recommendationConfig.fallbackProducts = recommendationConfig.products.slice(0, 24);
  }
  recentIds = recentIds.filter(function(id) {
    return !cartProductIds.has(id);
  });
  var applyRecommendationMarkup = function(products) {
    recommendationConfig.products = (products || []).filter(function(product) {
      return product && product.productId && !cartProductIds.has(String(product.productId));
    }).slice(0, 12);
    var markup = __jwBuildCartRecommendationsHtml(cart, recommendationConfig);
    var current = drawer.querySelector(".jw-cart-recommendations");
    if (!markup) {
      current?.remove();
      return false;
    }
    var template = document.createElement("template");
    template.innerHTML = markup;
    var replacement = template.content.firstElementChild;
    if (current) current.replaceWith(replacement);
    else drawer.querySelector(".jw-cart-drawer__scroll")?.append(replacement);
    return true;
  };
  if (!recentIds.length) {
    if (applyRecommendationMarkup(recommendationConfig.fallbackProducts)) return;
    var rootEmpty = window.Shopify && Shopify.routes && Shopify.routes.root || "/";
    fetch(rootEmpty + "collections/bestsellers/products.json?limit=12", { headers: { Accept: "application/json" } }).then(function(response) {
      if (!response.ok) return [];
      return response.json();
    }).then(function(payload) {
      var products = Array.isArray(payload && payload.products) ? payload.products : [];
      var mapped = products.filter(function(product) {
        return product && product.available !== false && product.id && product.handle;
      }).map(function(product) {
        var variant = Array.isArray(product.variants) && product.variants[0] || {};
        // products.json: featured_image may be null; images[0] is often {src}, prices are dollars.
        var image = __jwAbsCdnUrl(product.featured_image, 360) || __jwAbsCdnUrl(product.images && product.images[0], 360);
        return {
          productId: product.id,
          handle: product.handle,
          title: product.title,
          url: rootEmpty + "products/" + encodeURIComponent(product.handle),
          image: image || "",
          price: __jwDollarsToCents(variant.price != null ? variant.price : product.price),
          compareAtPrice: __jwDollarsToCents(variant.compare_at_price),
          variantId: variant.id || ""
        };
      });
      if (mapped.length) {
        recommendationConfig.fallbackProducts = mapped.slice(0, 12);
        applyRecommendationMarkup(mapped);
      }
    }).catch(function() {
    });
    return;
  }
  var signature = recentIds.join(",");
  if (__jwCartRecentSource.signature !== signature) {
    __jwCartRecentSource.controller?.abort();
    var controller = new AbortController();
    __jwCartRecentSource = {
      signature,
      controller,
      promise: (async function() {
        try {
          var params = new URLSearchParams();
          params.set("q", recentIds.map(function(id) {
            return "id:" + id;
          }).join(" OR "));
          params.set("resources[type]", "product");
          params.set("resources[limit]", "10");
          params.set("resources[options][unavailable_products]", "hide");
          var root = window.Shopify && Shopify.routes && Shopify.routes.root || "/";
          var response = await fetch(root + "search/suggest.json?" + params.toString(), {
            signal: controller.signal,
            headers: { Accept: "application/json" }
          });
          if (!response.ok) return [];
          var payload = await response.json();
          var products = payload && payload.resources && payload.resources.results && payload.resources.results.products;
          if (!Array.isArray(products)) return [];
          return products.filter(function(product) {
            return product && product.available !== false && product.id && product.handle && product.title;
          }).map(function(product) {
            var image = __jwAbsCdnUrl(product.image, 360) || __jwAbsCdnUrl(product.featured_image, 360);
            var firstVariant = Array.isArray(product.variants) && product.variants[0] || {};
            return {
              productId: product.id,
              handle: product.handle,
              title: product.title,
              url: root + "products/" + encodeURIComponent(product.handle),
              image,
              price: __jwDollarsToCents(product.price),
              compareAtPrice: __jwDollarsToCents(product.compare_at_price_min),
              variantId: firstVariant.id || ""
            };
          }).filter(function(product) {
            return !!product.image;
          }).slice(0, 10);
        } catch (error) {
          return [];
        }
      })()
    };
  }
  var generation = (drawer.__jwRecentCartGeneration || 0) + 1;
  drawer.__jwRecentCartGeneration = generation;
  __jwCartRecentSource.promise.then(function(recentProducts) {
    if (!drawer.isConnected || drawer.__jwRecentCartGeneration !== generation) return;
    var recentById = new Map(recentProducts.map(function(product) {
      return [String(product.productId), product];
    }));
    var merged = [];
    var seen = /* @__PURE__ */ new Set();
    recentIds.forEach(function(id) {
      var product = recentById.get(id);
      if (!product || seen.has(id)) return;
      seen.add(id);
      merged.push(product);
    });
    recommendationConfig.fallbackProducts.forEach(function(product) {
      var id = String(product && product.productId || "");
      if (!id || seen.has(id) || cartProductIds.has(id)) return;
      seen.add(id);
      merged.push(product);
    });
    applyRecommendationMarkup(merged);
  });
};
var __jwVariantCompareAtCache = Object.create(null);
/**
 * cart.js has no compare_at_price. Prestige-plain rebuilds the drawer from cart JSON,
 * so we must keep cartCompareAt fresh (SSR map is only items present on page load).
 * Fetches /products/{handle}.js for missing variant IDs; never invents fake compare-at.
 */
var __jwEnsureCartCompareAt = async function(cart, drawerConfig) {
  if (!drawerConfig) return;
  if (!drawerConfig.recommendations) drawerConfig.recommendations = {};
  var map = drawerConfig.recommendations.cartCompareAt;
  if (!map || typeof map !== "object") {
    map = {};
    drawerConfig.recommendations.cartCompareAt = map;
  }
  var products = Array.isArray(drawerConfig.recommendations.products) ? drawerConfig.recommendations.products : [];
  for (var pi = 0; pi < products.length; pi++) {
    var product = products[pi] || {};
    var productVid = product.variantId != null ? String(product.variantId) : "";
    if (!productVid || Object.prototype.hasOwnProperty.call(map, productVid)) continue;
    var seeded = Number(product.compareAtPrice) || 0;
    map[productVid] = seeded;
    __jwVariantCompareAtCache[productVid] = seeded;
  }
  var items = cart && Array.isArray(cart.items) ? cart.items : [];
  var handlesToFetch = Object.create(null);
  for (var ii = 0; ii < items.length; ii++) {
    var item = items[ii] || {};
    var variantId = String(item.variant_id || "");
    if (!variantId) continue;
    if (Object.prototype.hasOwnProperty.call(map, variantId)) continue;
    if (Object.prototype.hasOwnProperty.call(__jwVariantCompareAtCache, variantId)) {
      map[variantId] = __jwVariantCompareAtCache[variantId];
      continue;
    }
    var handle = item.handle ? String(item.handle) : "";
    if (!handle) continue;
    if (!handlesToFetch[handle]) handlesToFetch[handle] = [];
    handlesToFetch[handle].push(variantId);
  }
  var handleList = Object.keys(handlesToFetch);
  if (!handleList.length) return;
  var root = window.Shopify && Shopify.routes && Shopify.routes.root || "/";
  await Promise.all(handleList.map(async function(handle) {
    var requested = handlesToFetch[handle] || [];
    try {
      var response = await fetch(root + "products/" + encodeURIComponent(handle) + ".js");
      if (!response.ok) throw new Error("product fetch failed");
      var productJson = await response.json();
      var variants = productJson && Array.isArray(productJson.variants) ? productJson.variants : [];
      for (var vi = 0; vi < variants.length; vi++) {
        var variant = variants[vi] || {};
        var id = String(variant.id || "");
        if (!id) continue;
        var compareAt = Number(variant.compare_at_price) || 0;
        __jwVariantCompareAtCache[id] = compareAt;
        map[id] = compareAt;
      }
    } catch (e) {
    }
    for (var ri = 0; ri < requested.length; ri++) {
      var missingId = requested[ri];
      if (!Object.prototype.hasOwnProperty.call(map, missingId)) {
        map[missingId] = 0;
        __jwVariantCompareAtCache[missingId] = 0;
      }
    }
  }));
};
var __jwCartItemProps = function(item) {
  return item && item.properties && typeof item.properties === "object" ? item.properties : {};
};
var __jwProtectionHandles = [
  "jwear-shipping-protection",
  "package-protection",
  "order-protection",
  "shop-protection",
  "delivery-protection",
  "shipping-protection"
];
var __jwIsProtectionCartItem = function(item, configuredVariantId) {
  if (!item) return false;
  if (configuredVariantId && String(item.variant_id || "") === String(configuredVariantId)) return true;
  var handle = String(item.handle || "").toLowerCase();
  if (handle && __jwProtectionHandles.indexOf(handle) !== -1) return true;
  var title = String(item.product_title || item.title || "").toLowerCase();
  if (
    title.indexOf("shipping protection") !== -1 ||
    title.indexOf("package protection") !== -1 ||
    title.indexOf("order protection") !== -1 ||
    title.indexOf("delivery protection") !== -1
  ) {
    return true;
  }
  return false;
};
var __jwCartItemIsFreeLine = function(item) {
  // TEMP: Shopify discount only — ignore theme _jw_bundle_role free tags
  var finalLine = Number(item && item.final_line_price);
  var originalLine = Number(item && item.original_line_price);
  return finalLine === 0 && originalLine > 0;
};
var __jwLineDiscountTitles = function(item) {
  var titles = [];
  var seen = {};
  var add = function(title) {
    title = String(title || "").trim();
    if (!title) return;
    var key = title.toLowerCase();
    if (seen[key]) return;
    seen[key] = true;
    titles.push(title);
  };
  var discounts = Array.isArray(item && item.discounts) ? item.discounts : [];
  discounts.forEach(function(entry) {
    add(entry && (entry.title || entry.name));
  });
  var allocations = [];
  if (Array.isArray(item && item.line_level_discount_allocations)) allocations = allocations.concat(item.line_level_discount_allocations);
  if (Array.isArray(item && item.discount_allocations)) allocations = allocations.concat(item.discount_allocations);
  allocations.forEach(function(allocation) {
    var application = allocation && (allocation.discount_application || allocation);
    add(application && (application.title || application.code));
  });
  return titles;
};
var __jwCartDiscountTagHtml = function(title, amountCents, removableCode) {
  var amount = Number(amountCents) || 0;
  var amountHtml = amount > 0 ? " (&minus;" + __jwFormatMoney(amount) + ")" : "";
  var removeHtml = removableCode ? '<button type="button" class="jw-cart-discount-tag__remove" data-jw-discount-remove="' + __jwEscHtml(removableCode) + '" aria-label="Remove discount ' + __jwEscHtml(title) + '">&times;</button>' : "";
  return '<span class="jw-cart-discount-tag">' + __jwEscHtml(title) + amountHtml + removeHtml + "</span>";
};
var __jwCollectCartDiscountTags = function(cart) {
  var tags = [];
  var seen = {};
  var codes = Array.isArray(cart && cart.discount_codes) ? cart.discount_codes : [];
  var applicableCodes = codes.filter(function(entry) {
    return entry && entry.applicable && entry.code;
  }).map(function(entry) {
    return String(entry.code);
  });
  var codeKey = {};
  applicableCodes.forEach(function(code) {
    codeKey[code.toLowerCase()] = code;
  });
  var add = function(title, amountCents) {
    title = String(title || "").trim();
    if (!title) return;
    var key = title.toLowerCase();
    if (seen[key]) {
      if (amountCents > 0) seen[key].amount += amountCents;
      return;
    }
    var tag = { title: title, amount: Math.max(0, Number(amountCents) || 0), code: codeKey[key] || "" };
    seen[key] = tag;
    tags.push(tag);
  };
  var items = Array.isArray(cart && cart.items) ? cart.items : [];
  items.forEach(function(item) {
    var discounts = Array.isArray(item && item.discounts) ? item.discounts : [];
    if (discounts.length) {
      discounts.forEach(function(entry) {
        add(entry && entry.title, entry && entry.amount);
      });
      return;
    }
    __jwLineDiscountTitles(item).forEach(function(title) {
      add(title, item && item.total_discount);
    });
  });
  var applications = Array.isArray(cart && cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : [];
  applications.forEach(function(application) {
    var title = application && application.title;
    if (title && seen[String(title).trim().toLowerCase()]) return;
    add(title, application && application.total_allocated_amount);
  });
  applicableCodes.forEach(function(code) {
    add(code, 0);
  });
  return tags;
};
var __jwApplyCartDiscountCode = async function(drawer, rawCode, removeCode) {
  var root = window.Shopify && Shopify.routes && Shopify.routes.root || "/";
  var wrap = drawer && drawer.querySelector("[data-jw-discount]");
  var applyButton = wrap && wrap.querySelector("[data-jw-discount-apply]");
  var errorNode = wrap && wrap.querySelector("[data-jw-discount-error]");
  if (errorNode) errorNode.hidden = true;
  if (applyButton) applyButton.setAttribute("aria-busy", "true");
  try {
    var current = await (await fetch(root + "cart.js", { headers: { Accept: "application/json" } })).json();
    var codes = (Array.isArray(current.discount_codes) ? current.discount_codes : []).filter(function(entry) {
      return entry && entry.applicable && entry.code;
    }).map(function(entry) {
      return String(entry.code);
    });
    var attempted = String(rawCode || "").trim();
    if (removeCode) {
      var removeKey = String(removeCode).toLowerCase();
      codes = codes.filter(function(code) {
        return code.toLowerCase() !== removeKey;
      });
    } else if (attempted) {
      var hxCode = String(window.jwHalloween && window.jwHalloween.code || "HALLOWEEN").toUpperCase();
      if (attempted.toUpperCase() === hxCode) {
        codes = [hxCode];
      } else if (!codes.some(function(code) { return code.toLowerCase() === attempted.toLowerCase(); })) {
        codes.push(attempted);
      }
    } else {
      return;
    }
    var postDiscount = async function(list) {
      var response = await fetch(root + "cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ discount: list.join(",") })
      });
      var json = await response.json();
      return { ok: response.ok, cart: json };
    };
    var result = await postDiscount(codes);
    if (!result.ok || !result.cart) {
      if (errorNode) errorNode.hidden = false;
      return;
    }
    if (!removeCode && attempted) {
      var match = (result.cart.discount_codes || []).find(function(entry) {
        return entry && String(entry.code || "").toLowerCase() === attempted.toLowerCase();
      });
      if (!match || match.applicable === false) {
        var kept = (result.cart.discount_codes || []).filter(function(entry) {
          return entry && entry.applicable && entry.code && String(entry.code).toLowerCase() !== attempted.toLowerCase();
        }).map(function(entry) {
          return String(entry.code);
        });
        var cleaned = await postDiscount(kept);
        window.__jwDiscountError = "Enter a valid discount code";
        window.__jwDiscountDraft = attempted;
        if (cleaned.ok && cleaned.cart) {
          document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
            bubbles: true,
            detail: { cart: cleaned.cart, baseEvent: "discount:apply" }
          }));
        } else if (errorNode) {
          errorNode.hidden = false;
        }
        return;
      }
    }
    document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
      bubbles: true,
      detail: { cart: result.cart, baseEvent: "discount:apply" }
    }));
  } catch (e) {
    if (errorNode) errorNode.hidden = false;
  } finally {
    if (applyButton && applyButton.isConnected) applyButton.removeAttribute("aria-busy");
  }
};
var __jwBuildCartOfferBadgeHtml = function(item, config) {
  // FREE is printed once on the price. Never repeat it, and never print the code title.
  if (__jwCartItemIsFreeLine(item)) return "";
  var hxProps = item && item.properties || {};
  var hxOffer = "";
  var hxOfferCode = "";
  if (Array.isArray(hxProps)) {
    for (var hxI = 0; hxI < hxProps.length; hxI++) {
      if (hxProps[hxI] && hxProps[hxI][0] === "_jw_offer") hxOffer = String(hxProps[hxI][1] || "");
      if (hxProps[hxI] && hxProps[hxI][0] === "_jw_offer_code") hxOfferCode = String(hxProps[hxI][1] || "");
    }
  } else {
    hxOffer = String(hxProps._jw_offer || hxProps["_jw_offer"] || "");
    hxOfferCode = String(hxProps._jw_offer_code || "");
  }
  if (hxOffer === "halloween") {
    var hxLabel = (hxOfferCode || (window.jwHalloween && window.jwHalloween.code) || "HALLOWEEN").replace(/[<>&"']/g, "");
    return '<div class="jw-hx-cart-offer"><span class="jw-hx-cart-offer__code">' + hxLabel + '</span></div>';
  }
  var quantity = Math.max(1, Number(item && item.quantity) || 1);
  var unitPrice = Number(item && item.original_price) || 0;
  var originalLine = Number(item && item.original_line_price) || unitPrice * quantity;
  var finalLine = Number(item && (item.final_line_price != null ? item.final_line_price : item.line_price)) || 0;
  if (finalLine === 0 && originalLine > 0) return "";
  var compareMap = config && config.recommendations && config.recommendations.cartCompareAt || {};
  var compareAt = Number(compareMap[String(item && item.variant_id)] || 0);
  var baseLine = Math.max(originalLine, compareAt * quantity, unitPrice * quantity);
  var savingAmount = baseLine - finalLine;
  if (!(savingAmount > 0) || !(baseLine > 0)) return "";
  var pct = Math.round(savingAmount * 100 / baseLine);
  if (pct < 1) return "";
  var label = "SAVE " + pct + "%";
  var accessible = label + "; " + __jwFormatMoney(savingAmount) + " total savings";
  return '<span class="jw-cart-discount-tags"><span class="jw-cart-offer-badge jw-cart-offer-badge--save" title="' + __jwEscHtml(accessible) + '" aria-label="' + __jwEscHtml(accessible) + '">' + label + "</span></span>";
};

var __jwFsOfferWindowMs = 14 * 60 * 1000;
var __jwFsOfferStorageKey = "jw-fs-offer-ends-at";
var __jwGetFsOfferEndsAt = function() {
  var now = Date.now();
  var endsAt = 0;
  try {
    endsAt = parseInt(sessionStorage.getItem(__jwFsOfferStorageKey) || "0", 10) || 0;
  } catch (e) {
  }
  if (!endsAt || endsAt <= now) {
    endsAt = now + __jwFsOfferWindowMs;
    try {
      sessionStorage.setItem(__jwFsOfferStorageKey, String(endsAt));
    } catch (e) {
    }
  }
  return endsAt;
};
var __jwFormatFsTimer = function(endsAt) {
  var remaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
  var mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  var ss = String(remaining % 60).padStart(2, "0");
  return "Your order is reserved for " + mm + ":" + ss;
};
var __jwBuildFsTimerHtml = function() {
  // Standalone strip — countdown only; FS unlocked congrats + milestones stay hidden.
  var endsAt = __jwGetFsOfferEndsAt();
  var remainMs = Math.max(0, endsAt - Date.now());
  var remainPct = Math.max(0, Math.min(100, remainMs / __jwFsOfferWindowMs * 100));
  return '<div class="jw-cart-offer-timer" aria-live="polite"><p class="jw-free-shipping-bar__reserve" data-jw-fs-timer style="--jw-timer-remain:' + remainPct.toFixed(2) + '%">' + __jwFormatFsTimer(endsAt) + '</p></div>';
};
var __jwSyncFsTimers = function(root) {
  var scope = root && root.querySelectorAll ? root : document;
  var nodes = scope.querySelectorAll("[data-jw-fs-timer]");
  if (!nodes.length) return;
  var endsAt = __jwGetFsOfferEndsAt();
  var label = __jwFormatFsTimer(endsAt);
  var remainMs = Math.max(0, endsAt - Date.now());
  var remainPct = Math.max(0, Math.min(100, remainMs / __jwFsOfferWindowMs * 100));
  var remainCss = remainPct.toFixed(2) + "%";
  nodes.forEach(function(node) {
    node.textContent = label;
    try {
      node.style.setProperty("--jw-timer-remain", remainCss);
    } catch (e) {
    }
  });
};

// Prestige-plain: build drawer HTML from cart.js JSON (no section CE remount).
var __jwBuildMinimalCartDrawerHtml = function(cart, drawerConfig) {
  cart = cart || {};
  drawerConfig = drawerConfig || { recommendations: { products: [] }, trustHtml: "", emptyHtml: "", protectionHtml: "", protectionVariantId: "", protectionAutoAdd: false };
  var items = Array.isArray(cart.items) ? cart.items : [];
  var itemCount = typeof cart.item_count === "number" ? cart.item_count : items.reduce(function(n, it) {
    return n + (it && it.quantity || 0);
  }, 0);
  var root = window.Shopify && Shopify.routes && Shopify.routes.root || "/";
  if (!itemCount || !items.length) {
    return '<div class="jw-cart-drawer__heading h4" slot="header"><span class="jw-cart-drawer__heading-label">Cart</span></div>' + (drawerConfig.emptyHtml || '<div class="jw-cart-drawer__empty"><div class="jw-cart-drawer__empty-intro"><p class="jw-cart-drawer__empty-title">YOUR CART IS EMPTY</p><p class="jw-cart-drawer__empty-copy">Discover our latest collections</p><a class="button jw-cart-drawer__empty-shop" href="' + root + 'collections/all">Shop all</a></div></div>');
  }
  var paidTarget = __jwFsPaidTarget;
  var protectionVariantId = "";
  try {
    var host = document.getElementById("cart-drawer");
    var raw = host && host.getAttribute("data-jw-fs-threshold");
    if (raw != null && raw !== "") {
      var parsedTarget = parseInt(raw, 10);
      // Legacy shipping cents were large (e.g. 10000); dual-tier uses small unit targets (4).
      if (!isNaN(parsedTarget) && parsedTarget > 0 && parsedTarget < 100) paidTarget = parsedTarget;
    }
    if (host) protectionVariantId = host.getAttribute("data-jw-protection-variant-id") || "";
  } catch (e) {
  }
  if (!protectionVariantId && drawerConfig.protectionVariantId) {
    protectionVariantId = String(drawerConfig.protectionVariantId);
  }
  var fsState = __jwFsCountCartProgress(items, protectionVariantId, paidTarget);
  // TEMP: no theme free-unit invent for display — Shopify discounts only
  var freeByKey = {}; // kept empty; Admin BXGY allocations drive FREE labels
  var progress = fsState.progress;
  var reached = fsState.reached;
  var isClose = fsState.isClose;
  var tier1Done = fsState.tier1Done;
  var tier2Done = !!fsState.tier2Done;
  var shippingUnlocked = !!fsState.shippingUnlocked;
  var fsbMsg = __jwFsBarMessageHtml(fsState);
  var fsbAria = reached ? "You've unlocked 2 free gifts!" : tier2Done ? "2 free gifts unlocked — add your free products" : tier1Done ? "1 free gift unlocked — add your free product" : "Free product progress";
  var prevFsBar = null;
  try {
    prevFsBar = document.querySelector("#cart-drawer .jw-free-shipping-bar, .cart-drawer .jw-free-shipping-bar");
  } catch (e) {
  }
  var wasReached = !!(prevFsBar && prevFsBar.classList.contains("is-reached"));
  var prevFreeTier = 0;
  try {
    prevFreeTier = parseInt((prevFsBar && prevFsBar.getAttribute("data-jw-fs-free-tier")) || "0", 10) || 0;
  } catch (e) {
  }
  var freeTier = tier2Done ? 2 : tier1Done ? 1 : 0;
  var celebrate = freeTier > prevFreeTier || (reached && !wasReached);
  var total = Number(cart.total_price) || 0;
  var shippingSubline = "";
  if (!shippingUnlocked) {
    shippingSubline = drawerConfig.recommendations && drawerConfig.recommendations.shippingSubline || "";
    if (shippingSubline) shippingSubline = __jwEscHtml(shippingSubline);
  }
  var promoLine1 = drawerConfig.recommendations && drawerConfig.recommendations.promoLine1 || "";
  var promoLine2 = "";
  var fsBarClass = "free-shipping-bar jw-free-shipping-bar" + (reached ? " is-reached" : "") + (tier1Done && !reached ? " jw-free-shipping-bar--tier1-done" : "") + (shippingUnlocked ? " jw-free-shipping-bar--shipping-unlocked" : "") + (isClose ? " jw-free-shipping-bar--close" : "");
  var html = [];
  html.push('<div class="jw-cart-drawer__heading h4" slot="header"><span class="jw-cart-drawer__heading-label">Cart</span><span class="jw-cart-drawer__count" aria-label="' + itemCount + ' items">' + itemCount + "</span></div>");
  html.push('<div class="jw-cart-drawer__scroll">');
  // TEMP: keep FS progress milestones out of the drawer; restore countdown/reserve timer only.
  celebrate = false;
  html.push(__jwBuildFsTimerHtml());
  html.push('<div class="cart-drawer__items">');
  var displayTotalAdjust = 0;
  for (var i = 0; i < items.length; i++) {
    var it = items[i] || {};
    if (__jwIsProtectionCartItem(it, drawerConfig.protectionVariantId)) continue;
    var img = "";
    if (it.image) {
      var src = String(it.image);
      if (src.indexOf("_240x240") === -1 && src.indexOf("_320x320") === -1) src = src.replace(/(\.(?:jpg|jpeg|png|webp|gif))(\?|$)/i, "_240x240$1$2");
      img = '<img class="line-item__media" src="' + __jwEscHtml(src) + '" alt="" width="108" height="108" loading="lazy">';
    }
    var title = it.product_title || it.title || "Item";
    var variant = it.variant_title && it.variant_title !== "Default Title" ? it.variant_title : "";
    var key = it.key || "";
    var qty = Math.max(1, Number(it.quantity) || 1);
    var linePrice = Number(it.final_line_price != null ? it.final_line_price : it.line_price) || 0;
    var originalLinePrice = it.original_line_price != null ? it.original_line_price : linePrice;
    var cartCompareMap = drawerConfig.recommendations && drawerConfig.recommendations.cartCompareAt || {};
    var compareAtLinePrice = (Number(cartCompareMap[String(it.variant_id)]) || 0) * qty;
    var displayOriginalLinePrice = Math.max(originalLinePrice, compareAtLinePrice);
    var designatedFreeQty = 0; // Shopify Admin discounts only — never invent free units
    var isFreeLine = __jwCartItemIsFreeLine(it);
    var isPartialFree = false;
    var displayLinePrice = linePrice;
    // Never invent $0 display; trust Shopify final_line_price / discount_allocations.
    html.push('<div class="line-item' + (isFreeLine ? " line-item--jw-free" : "") + '" data-jw-ce="line-item"' + (isFreeLine ? ' data-jw-bundle-role="free"' : "") + ">");
    html.push(img);
    html.push('<div class="line-item-info"><div class="v-stack justify-items-start gap-1">');
    if (it.url) html.push('<a class="jw-cart-line-title" href="' + __jwEscHtml(it.url) + '">' + __jwEscHtml(title) + "</a>");
    else html.push('<p class="jw-cart-line-title">' + __jwEscHtml(title) + "</p>");
    if (isFreeLine) {
      html.push('<p class="jw-cart-line-price jw-cart-line-price--free">' + (displayOriginalLinePrice > 0 ? '<s class="jw-cart-line-price__original">' + __jwFormatMoney(displayOriginalLinePrice) + "</s>" : "") + '<span class="jw-cart-line-price__current jw-cart-line-price__current--free">FREE</span></p>');
    } else {
      html.push('<p class="jw-cart-line-price">' + (displayOriginalLinePrice > displayLinePrice ? '<s class="jw-cart-line-price__original">' + __jwFormatMoney(displayOriginalLinePrice) + "</s>" : "") + '<span class="jw-cart-line-price__current">' + __jwFormatMoney(displayLinePrice) + "</span></p>");
    }
    if (variant) html.push('<p class="text-subdued text-sm jw-cart-line-variant">' + __jwEscHtml(variant) + "</p>");
    var offerHtml = __jwBuildCartOfferBadgeHtml(it, drawerConfig);
    var isHxOffer = offerHtml.indexOf("jw-hx-cart-offer") !== -1;
    if (isHxOffer) html.push(offerHtml);
    html.push("</div>");
    html.push('<div class="jw-cart-line-actions"><div class="quantity-selector quantity-selector--pill" data-jw-ce="quantity-selector"><button type="button" class="quantity-selector__button" aria-label="Decrease quantity">&minus;</button><input class="quantity-selector__input" type="number" inputmode="numeric" min="0" value="' + qty + '" data-line-key="' + __jwEscHtml(key) + '" aria-label="Quantity for ' + __jwEscHtml(title) + '"><button type="button" class="quantity-selector__button" aria-label="Increase quantity">+</button></div>' + (isHxOffer ? "" : offerHtml) + "</div>");
    html.push("</div></div>");
  }
  html.push("</div>");
  html.push(__jwBuildCartProtectionHtml(cart, drawerConfig));
  html.push(__jwBuildCartRecommendationsHtml(cart, drawerConfig.recommendations));
  html.push("</div>");
  var trustHtml = drawerConfig.trustHtml || '<div class="jw-cart-trust" role="group" aria-label="Secure checkout"><div class="jw-cart-trust__row"><span class="jw-cart-trust__label">Secure checkout</span></div></div>';
  // When protection UI is off, strip leftover protection charges from displayed totals (purge also removes them).
  // When protection UI is on, keep the charge in Total — the card is the line UI.
  if (!drawerConfig.protectionVariantId) {
    for (var pi = 0; pi < items.length; pi++) {
      var pit = items[pi] || {};
      if (__jwIsProtectionCartItem(pit, "")) {
        displayTotalAdjust += Number(pit.final_line_price) || 0;
      }
    }
  }
  var displayTotal = Math.max(0, total - displayTotalAdjust);
  var itemsSubtotal = Number(cart.items_subtotal_price != null ? cart.items_subtotal_price : total) || 0;
  var cartDiscount = Number(cart.total_discount) || 0;
  if (cartDiscount <= 0 && itemsSubtotal > total) cartDiscount = itemsSubtotal - total;
  var compareTotal = 0;
  for (var ci = 0; ci < items.length; ci++) {
    var cit = items[ci] || {};
    if (__jwIsProtectionCartItem(cit, drawerConfig.protectionVariantId)) {
      // Protection card owns the line UI; when enabled, keep face value in compare so Total savings stay accurate.
      if (drawerConfig.protectionVariantId) {
        compareTotal += Number(cit.final_line_price != null ? cit.final_line_price : cit.line_price) || 0;
      }
      continue;
    }
    var cQty = Math.max(1, Number(cit.quantity) || 1);
    var cOrig = Number(cit.original_line_price != null ? cit.original_line_price : cit.final_line_price) || 0;
    var cMap = drawerConfig.recommendations && drawerConfig.recommendations.cartCompareAt || {};
    var cCompare = (Number(cMap[String(cit.variant_id)]) || 0) * cQty;
    var cFinal = Number(cit.final_line_price != null ? cit.final_line_price : cit.line_price) || 0;
    compareTotal += Math.max(cOrig, cCompare, cFinal);
  }
  if (compareTotal < itemsSubtotal - displayTotalAdjust) compareTotal = itemsSubtotal - displayTotalAdjust;
  if (compareTotal < displayTotal) compareTotal = displayTotal;
  var savings = Math.max(cartDiscount + displayTotalAdjust, compareTotal - displayTotal);
  var summaryHtml = '<div class="jw-cart-order-summary" aria-label="Order summary"><div class="jw-cart-order-summary__row jw-cart-order-summary__row--total"><span class="jw-cart-order-summary__label">Total</span><span class="jw-cart-order-summary__totals">' + (compareTotal > displayTotal ? '<s class="jw-cart-order-summary__compare">' + __jwFormatMoney(compareTotal) + "</s>" : "") + '<span class="jw-cart-order-summary__value">' + __jwFormatMoney(displayTotal) + "</span>" + (savings > 0 ? '<span class="jw-cart-order-summary__save">&minus;' + __jwFormatMoney(savings) + "</span>" : "") + "</span></div></div>";
  html.push('<div slot="footer"><form action="' + root + 'cart" method="POST" class="cart-drawer__footer" aria-label="Checkout">' + summaryHtml + '<div class="button-group jw-cart-drawer__actions"><button type="submit" name="checkout" class="button" style="width:100%">SECURE CHECKOUT</button></div>' + trustHtml + "</form></div>");
  return html.join("");
};

if (!window.__jwFsTimerInterval) {
  window.__jwFsTimerInterval = window.setInterval(function() {
    __jwSyncFsTimers(document);
  }, 1000);
  document.addEventListener("cart:change", function() {
    __jwSyncFsTimers(document);
  });
}

var __jwPlainCartMutationQueue = Promise.resolve();
var __jwPlainCartMutationSequence = 0;
// TEMP: disable theme free-gift allocation / orphan purge — Shopify discounts only
var __jwThemeFreeProductLogicEnabled = false;
var __jwBundleSyncBusy = false;
var __jwCollectOrphanFreeLineKeys = function(cart, protectionVariantId) {
  if (!__jwThemeFreeProductLogicEnabled) return [];
  var items = Array.isArray(cart && cart.items) ? cart.items : [];
  var paidQty = 0;
  var freeEntries = [];
  var groups = {};
  for (var i = 0; i < items.length; i++) {
    var it = items[i] || {};
    if (!it.key) continue;
    if (__jwIsProtectionCartItem(it, protectionVariantId) || __jwFsIsExcludedItem(it, protectionVariantId)) continue;
    var props = __jwCartItemProps(it);
    var qty = Math.max(0, Number(it.quantity) || 0);
    if (!qty) continue;
    var role = String(props._jw_bundle_role || "").toLowerCase();
    var isFree = role === "free" || __jwCartItemIsFreeLine(it);
    var bundleId = props._jw_bundle_id ? String(props._jw_bundle_id) : "";
    if (isFree) {
      freeEntries.push({ key: it.key, index: i, bundleId: bundleId });
      if (bundleId) {
        if (!groups[bundleId]) groups[bundleId] = { paidQty: 0, freeKeys: [] };
        groups[bundleId].freeKeys.push(it.key);
      }
    } else {
      paidQty += qty;
      if (bundleId) {
        if (!groups[bundleId]) groups[bundleId] = { paidQty: 0, freeKeys: [] };
        groups[bundleId].paidQty += qty;
      }
    }
  }
  // Cart-wide: never allow more free than remaining paid unlocks (0 paid → purge all free).
  var maxFree = __jwFsAllowedFreeForPaid(paidQty);
  var orphan = {};
  if (maxFree <= 0) {
    for (var a = 0; a < freeEntries.length; a++) orphan[freeEntries[a].key] = true;
  } else if (freeEntries.length > maxFree) {
    // Keep the earliest free lines that still fit; purge the rest.
    for (var b = maxFree; b < freeEntries.length; b++) orphan[freeEntries[b].key] = true;
  }
  // Per-bundle: free rows whose pack paid count no longer qualifies must go even if
  // other cart paid units would otherwise "sponsor" them cart-wide.
  Object.keys(groups).forEach(function(bundleId) {
    var group = groups[bundleId];
    var bundleMax = __jwFsAllowedFreeForPaid(group.paidQty);
    if (group.freeKeys.length > bundleMax) {
      for (var fi = bundleMax; fi < group.freeKeys.length; fi++) orphan[group.freeKeys[fi]] = true;
    }
  });
  return Object.keys(orphan);
};
var __jwSyncJwBundleIntegrity = async function(cart) {
  if (__jwBundleSyncBusy) return cart;
  var protectionVariantId = "";
  try {
    var host = document.getElementById("cart-drawer");
    if (host) protectionVariantId = host.getAttribute("data-jw-protection-variant-id") || "";
  } catch (e) {
  }
  var keys = __jwCollectOrphanFreeLineKeys(cart, protectionVariantId);
  if (!keys.length) return cart;
  __jwBundleSyncBusy = true;
  try {
    var updates = {};
    for (var i = 0; i < keys.length; i++) updates[keys[i]] = 0;
    var root = window.Shopify && Shopify.routes && Shopify.routes.root || "/";
    var response = await fetch(root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({ updates })
    });
    if (!response.ok) return cart;
    return await response.json();
  } catch (e) {
    return cart;
  } finally {
    __jwBundleSyncBusy = false;
  }
};
if (!window.__jwBundleIntegrityBound) {
  window.__jwBundleIntegrityBound = true;
  var __jwRunBundleIntegrity = function(cart, baseEvent) {
    if (__jwBundleSyncBusy) return;
    if (baseEvent === "jw-bundle:sync") return;
    if (!cart || !__jwCollectOrphanFreeLineKeys(cart, (function() {
      try {
        var host = document.getElementById("cart-drawer");
        return host && host.getAttribute("data-jw-protection-variant-id") || "";
      } catch (e) {
        return "";
      }
    })()).length) return;
    __jwSyncJwBundleIntegrity(cart).then(function(nextCart) {
      if (!nextCart || nextCart === cart) return;
      if ((Number(nextCart.item_count) || 0) === (Number(cart.item_count) || 0)) return;
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: { cart: nextCart, baseEvent: "jw-bundle:sync" }
      }));
    });
  };
  document.addEventListener("cart:change", function(event) {
    __jwRunBundleIntegrity(event.detail && event.detail.cart, event.detail && event.detail.baseEvent);
  });
  try {
    var bundleRoot = window.Shopify && Shopify.routes && Shopify.routes.root || "/";
    fetch(bundleRoot + "cart.js", { headers: { Accept: "application/json" } })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(cart) { if (cart) __jwRunBundleIntegrity(cart, "jw-bundle:boot"); })
      .catch(function() {});
  } catch (e) {
  }
}

// Auto-tag cheapest full lines as FREE when B2G1/B4G2 qualifies without bundle picker.
// Bundle-picker ATC lines (_jw_bundle_id without _jw_auto_free) are never rewritten so
// B4G2 packs (4 paid + 2 free-tagged) and auto-allocate cannot double-conflict.
var __jwAutoFreeSyncBusy = false;
var __jwCloneLineProperties = function(props) {
  var out = {};
  if (!props || typeof props !== "object") return out;
  Object.keys(props).forEach(function(key) {
    if (props[key] == null || props[key] === "") return;
    out[key] = String(props[key]);
  });
  return out;
};
var __jwCollectAutoFreePropertyChanges = function(cart, protectionVariantId) {
  if (!__jwThemeFreeProductLogicEnabled) return [];
  var items = Array.isArray(cart && cart.items) ? cart.items : [];
  var alloc = __jwFsAllocateFreeUnits(items, protectionVariantId);
  var changes = [];
  for (var i = 0; i < items.length; i++) {
    var it = items[i] || {};
    if (!it.key || __jwFsIsExcludedItem(it, protectionVariantId)) continue;
    var qty = Math.max(0, Number(it.quantity) || 0);
    if (!qty) continue;
    var props = __jwCartItemProps(it);
    var role = String(props._jw_bundle_role || "").toLowerCase();
    var isAuto = String(props._jw_auto_free || "") === "1";
    var bundleId = props._jw_bundle_id;
    // Never rewrite bundle-picker lines (they already carry paid/free roles).
    if (bundleId && !isAuto) continue;
    var freeQty = Math.max(0, Number(alloc.byKey[String(it.key)]) || 0);
    var nextProps = __jwCloneLineProperties(props);
    if (freeQty >= qty && role !== "free") {
      nextProps._jw_bundle_role = "free";
      nextProps._jw_auto_free = "1";
      changes.push({ key: it.key, quantity: qty, properties: nextProps });
    } else if (freeQty === 0 && role === "free" && isAuto) {
      delete nextProps._jw_bundle_role;
      delete nextProps._jw_auto_free;
      changes.push({ key: it.key, quantity: qty, properties: nextProps });
    }
    // Partial free stays display-only (splitting lines via Cart API is brittle).
  }
  return changes;
};
var __jwSyncAutoFreeDesignation = async function(cart) {
  if (__jwAutoFreeSyncBusy) return cart;
  var protectionVariantId = "";
  try {
    var host = document.getElementById("cart-drawer");
    if (host) protectionVariantId = host.getAttribute("data-jw-protection-variant-id") || "";
  } catch (e) {
  }
  var changes = __jwCollectAutoFreePropertyChanges(cart, protectionVariantId);
  if (!changes.length) return cart;
  __jwAutoFreeSyncBusy = true;
  try {
    var root = window.Shopify && Shopify.routes && Shopify.routes.root || "/";
    var nextCart = cart;
    for (var i = 0; i < changes.length; i++) {
      var change = changes[i];
      var response = await fetch(root + "cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          id: change.key,
          quantity: change.quantity,
          properties: change.properties
        })
      });
      if (!response.ok) return nextCart;
      nextCart = await response.json();
    }
    return nextCart;
  } catch (e) {
    return cart;
  } finally {
    __jwAutoFreeSyncBusy = false;
  }
};
if (!window.__jwAutoFreeSyncBound) {
  window.__jwAutoFreeSyncBound = true;
  var __jwRunAutoFreeSync = function(cart, baseEvent) {
    if (!cart || __jwAutoFreeSyncBusy) return;
    if (baseEvent === "jw-auto-free:sync" || baseEvent === "jw-bundle:sync") return;
    var protectionVariantId = "";
    try {
      var host = document.getElementById("cart-drawer");
      if (host) protectionVariantId = host.getAttribute("data-jw-protection-variant-id") || "";
    } catch (e) {
    }
    if (!__jwCollectAutoFreePropertyChanges(cart, protectionVariantId).length) return;
    __jwSyncAutoFreeDesignation(cart).then(function(nextCart) {
      if (!nextCart || nextCart === cart) return;
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: { cart: nextCart, baseEvent: "jw-auto-free:sync" }
      }));
    });
  };
  document.addEventListener("cart:change", function(event) {
    __jwRunAutoFreeSync(event.detail && event.detail.cart, event.detail && event.detail.baseEvent);
  });
  try {
    var autoFreeRoot = window.Shopify && Shopify.routes && Shopify.routes.root || "/";
    fetch(autoFreeRoot + "cart.js", { headers: { Accept: "application/json" } })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(cart) { if (cart) __jwRunAutoFreeSync(cart, "jw-auto-free:boot"); })
      .catch(function() {});
  } catch (e) {
  }
}

var __jwProtectionPurgeBusy = false;
var __jwCollectProtectionLineKeys = function(cart, configuredVariantId) {
  var items = Array.isArray(cart && cart.items) ? cart.items : [];
  var keys = [];
  for (var i = 0; i < items.length; i++) {
    var it = items[i] || {};
    if (__jwIsProtectionCartItem(it, configuredVariantId) && it.key) keys.push(it.key);
  }
  return keys;
};
var __jwPurgeShippingProtection = async function(cart) {
  if (__jwProtectionPurgeBusy) return cart;
  var host = document.getElementById("cart-drawer");
  // Only purge when theme protection UI is disabled (empty variant id attribute).
  if (host && host.getAttribute("data-jw-protection-variant-id")) return cart;
  var keys = __jwCollectProtectionLineKeys(cart, "");
  if (!keys.length) return cart;
  __jwProtectionPurgeBusy = true;
  try {
    var updates = {};
    for (var i = 0; i < keys.length; i++) updates[keys[i]] = 0;
    var root = window.Shopify && Shopify.routes && Shopify.routes.root || "/";
    var response = await fetch(root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({ updates })
    });
    if (!response.ok) return cart;
    return await response.json();
  } catch (e) {
    return cart;
  } finally {
    __jwProtectionPurgeBusy = false;
  }
};
if (!window.__jwProtectionPurgeBound) {
  window.__jwProtectionPurgeBound = true;
  var __jwRunProtectionPurge = function(cart) {
    if (!cart || __jwProtectionPurgeBusy) return;
    if (!__jwCollectProtectionLineKeys(cart, "").length) return;
    __jwPurgeShippingProtection(cart).then(function(nextCart) {
      if (!nextCart || nextCart === cart) return;
      if ((Number(nextCart.item_count) || 0) === (Number(cart.item_count) || 0)) return;
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: { cart: nextCart, baseEvent: "jw-protection:purge" }
      }));
    });
  };
  document.addEventListener("cart:change", function(event) {
    if (event.detail && event.detail.baseEvent === "jw-protection:purge") return;
    __jwRunProtectionPurge(event.detail && event.detail.cart);
  });
  // Purge leftovers already in the bag on first paint.
  try {
    var root = window.Shopify && Shopify.routes && Shopify.routes.root || "/";
    fetch(root + "cart.js", { headers: { Accept: "application/json" } })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(cart) { if (cart) __jwRunProtectionPurge(cart); })
      .catch(function() {});
  } catch (e) {}
}
var __jwQuickAddState = {
  open: false,
  busy: false,
  lastFocus: null,
  productCache: /* @__PURE__ */ new Map()
};
var __jwCloseQuickAddModal = function() {
  var modal = document.getElementById("jw-quick-add-modal");
  if (!modal) return;
  modal.hidden = true;
  modal.setAttribute("aria-hidden", "true");
  document.documentElement.classList.remove("jw-quick-add-open");
  __jwQuickAddState.open = false;
  __jwQuickAddState.busy = false;
  if (__jwQuickAddState.lastFocus && typeof __jwQuickAddState.lastFocus.focus === "function") {
    try {
      __jwQuickAddState.lastFocus.focus();
    } catch (e) {
    }
  }
  __jwQuickAddState.lastFocus = null;
};
var __jwEnsureQuickAddModal = function() {
  var modal = document.getElementById("jw-quick-add-modal");
  if (modal) return modal;
  modal = document.createElement("div");
  modal.id = "jw-quick-add-modal";
  modal.className = "jw-quick-add-modal";
  modal.hidden = true;
  modal.setAttribute("aria-hidden", "true");
  modal.innerHTML = '<div class="jw-quick-add-modal__backdrop" data-jw-quick-add-close></div><div class="jw-quick-add-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="jw-quick-add-title" tabindex="-1"><button type="button" class="jw-quick-add-modal__close" data-jw-quick-add-close aria-label="Close">&times;</button><div class="jw-quick-add-modal__body" data-jw-quick-add-body></div></div>';
  document.body.appendChild(modal);
  if (!window.__jwQuickAddBound) {
    window.__jwQuickAddBound = true;
    document.addEventListener("keydown", function(event) {
      if (event.key === "Escape" && __jwQuickAddState.open) {
        event.preventDefault();
        __jwCloseQuickAddModal();
      }
    });
    document.addEventListener("click", function(event) {
      if (event.target && event.target.closest && event.target.closest("[data-jw-quick-add-close]")) {
        __jwCloseQuickAddModal();
      }
    });
  }
  return modal;
};
var __jwFindVariantFromOptions = function(product, selected) {
  var variants = product && Array.isArray(product.variants) ? product.variants : [];
  for (var i = 0; i < variants.length; i++) {
    var variant = variants[i];
    var ok = true;
    for (var oi = 0; oi < (product.options || []).length; oi++) {
      if (String(variant["option" + (oi + 1)] || "") !== String(selected[oi] || "")) {
        ok = false;
        break;
      }
    }
    if (ok) return variant;
  }
  return variants.find(function(v) {
    return v && v.available;
  }) || variants[0] || null;
};
var __jwRenderQuickAddBody = function(meta, product) {
  var options = Array.isArray(product.options) ? product.options : [];
  var variants = Array.isArray(product.variants) ? product.variants : [];
  var firstAvailable = variants.find(function(v) {
    return v && v.available;
  }) || variants[0];
  var selected = options.map(function(_, index) {
    return firstAvailable && firstAvailable["option" + (index + 1)] || "";
  });
  var price = Number(firstAvailable && firstAvailable.price != null ? firstAvailable.price : meta.price) || 0;
  var compare = Number(firstAvailable && firstAvailable.compare_at_price != null ? firstAvailable.compare_at_price : meta.compare) || 0;
  var optionHtml = options.map(function(optionName, optionIndex) {
    var values = [];
    variants.forEach(function(variant) {
      var value = variant && variant["option" + (optionIndex + 1)];
      if (value && values.indexOf(value) === -1) values.push(value);
    });
    if (values.length <= 1 && String(values[0] || "").toLowerCase() === "default title") return "";
    return '<fieldset class="jw-quick-add-modal__option"><legend>' + __jwEscHtml(optionName) + '</legend><div class="jw-quick-add-modal__values">' + values.map(function(value) {
      var checked = selected[optionIndex] === value ? " checked" : "";
      return '<label class="jw-quick-add-modal__value"><input type="radio" name="jw-qa-opt-' + optionIndex + '" value="' + __jwEscHtml(value) + '"' + checked + '><span>' + __jwEscHtml(value) + "</span></label>";
    }).join("") + "</div></fieldset>";
  }).join("");
  return '<div class="jw-quick-add-modal__product">' + (meta.image ? '<img class="jw-quick-add-modal__image" src="' + __jwEscHtml(meta.image) + '" alt="">' : "") + '<div class="jw-quick-add-modal__meta"><h2 id="jw-quick-add-title" class="jw-quick-add-modal__title">' + __jwEscHtml(meta.title || product.title || "") + '</h2><p class="jw-quick-add-modal__price" data-jw-qa-price>' + __jwFormatMoney(price) + (compare > price ? ' <s>' + __jwFormatMoney(compare) + "</s>" : "") + "</p></div></div>" + '<form class="jw-quick-add-modal__form" data-jw-qa-form>' + optionHtml + '<p class="jw-quick-add-modal__status" data-jw-qa-status role="status" aria-live="polite" hidden></p><button type="submit" class="button jw-quick-add-modal__submit" data-jw-qa-submit style="width:100%">Add to cart</button></form>';
};
var __jwOpenQuickAddModal = async function(button) {
  if (!button || __jwQuickAddState.busy) return;
  var handle = button.getAttribute("data-product-handle") || "";
  var root = window.Shopify && Shopify.routes && Shopify.routes.root || "/";
  var modal = __jwEnsureQuickAddModal();
  var body = modal.querySelector("[data-jw-quick-add-body]");
  if (!body) return;
  __jwQuickAddState.lastFocus = button;
  __jwQuickAddState.open = true;
  __jwQuickAddState.busy = true;
  body.innerHTML = '<p class="jw-quick-add-modal__loading">Loading options…</p>';
  modal.hidden = false;
  modal.setAttribute("aria-hidden", "false");
  document.documentElement.classList.add("jw-quick-add-open");
  modal.querySelector(".jw-quick-add-modal__dialog")?.focus();
  var meta = {
    title: button.getAttribute("data-product-title") || "",
    image: button.getAttribute("data-product-image") || "",
    price: Number(button.getAttribute("data-product-price")) || 0,
    compare: Number(button.getAttribute("data-product-compare")) || 0,
    url: button.getAttribute("data-product-url") || ""
  };
  try {
    var product = __jwQuickAddState.productCache.get(handle);
    if (!product) {
      if (!handle) throw new Error("Missing product handle");
      var response = await fetch(root + "products/" + encodeURIComponent(handle) + ".js", {
        headers: { Accept: "application/json" }
      });
      if (!response.ok) throw new Error("Unable to load product");
      product = await response.json();
      __jwQuickAddState.productCache.set(handle, product);
    }
    body.innerHTML = __jwRenderQuickAddBody(meta, product);
    var form = body.querySelector("[data-jw-qa-form]");
    var status = body.querySelector("[data-jw-qa-status]");
    var submit = body.querySelector("[data-jw-qa-submit]");
    var priceEl = body.querySelector("[data-jw-qa-price]");
    var syncVariant = function() {
      var selected = Array.from(body.querySelectorAll("fieldset.jw-quick-add-modal__option")).map(function(fieldset) {
        var checked = fieldset.querySelector("input:checked");
        return checked ? checked.value : "";
      });
      var variant = __jwFindVariantFromOptions(product, selected);
      if (!variant) return null;
      var variantPrice = Number(variant.price) || 0;
      var variantCompare = Number(variant.compare_at_price) || 0;
      if (priceEl) {
        priceEl.innerHTML = __jwFormatMoney(variantPrice) + (variantCompare > variantPrice ? " <s>" + __jwFormatMoney(variantCompare) + "</s>" : "");
      }
      if (submit) {
        submit.disabled = !variant.available;
        submit.textContent = variant.available ? "Add to cart" : "Sold out";
      }
      return variant;
    };
    syncVariant();
    form?.addEventListener("change", function() {
      syncVariant();
    });
    form?.addEventListener("submit", function(event) {
      event.preventDefault();
      if (__jwQuickAddState.busy && submit && submit.getAttribute("aria-busy") === "true") return;
      var variant = syncVariant();
      if (!variant || !variant.available || !variant.id) return;
      if (submit) {
        submit.setAttribute("aria-busy", "true");
        submit.disabled = true;
      }
      if (status) {
        status.hidden = false;
        status.textContent = "Adding…";
      }
      var drawer = document.getElementById("cart-drawer");
      __jwCommitPlainCartMutation(drawer || document.body, root + "cart/add.js", {
        items: [{ id: variant.id, quantity: 1 }]
      }, "plain-cart:quick-add").then(function(cart) {
        if (status) status.textContent = cart ? "Added" : "Could not add";
        if (cart) {
          setTimeout(__jwCloseQuickAddModal, 250);
        } else if (submit) {
          submit.removeAttribute("aria-busy");
          submit.disabled = false;
        }
      }).catch(function() {
        if (status) status.textContent = "Could not add";
        if (submit) {
          submit.removeAttribute("aria-busy");
          submit.disabled = false;
        }
      });
    });
  } catch (error) {
    body.innerHTML = '<p class="jw-quick-add-modal__error">Unable to load options. <a href="' + __jwEscHtml(meta.url || "#") + '">View product</a></p>';
  } finally {
    __jwQuickAddState.busy = false;
  }
};
var __jwCommitPlainCartMutation = function(drawer, url, payload, source) {
  var sequence = ++__jwPlainCartMutationSequence;
  var runMutation = async function() {
    var response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      throw new Error("Cart request failed: " + response.status);
    }
    var cart = url.indexOf("/change") !== -1 ? await response.json() : await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    if (sequence !== __jwPlainCartMutationSequence) return cart;
    cart = await __jwSyncJwBundleIntegrity(cart) || cart;
    if (sequence !== __jwPlainCartMutationSequence) return cart;
    document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
      bubbles: true,
      detail: { cart, baseEvent: source || "plain-cart:update" }
    }));
    return cart;
  };
  var queuedMutation = __jwPlainCartMutationQueue.then(runMutation).catch(function(error) {
    if (sequence !== __jwPlainCartMutationSequence) return null;
    drawer.dispatchEvent(new CustomEvent("cart:error", {
      bubbles: true,
      detail: { error: error.message || String(error) }
    }));
    document.dispatchEvent(new CustomEvent("cart:refresh"));
    return null;
  });
  __jwPlainCartMutationQueue = queuedMutation;
  return queuedMutation;
};
/** Open Prestige <quick-buy-modal> (same as homepage product-card +) outside the cart drawer tree. */
var __jwOpenCartRecQuickBuy = function(button) {
  if (!button) return;
  var handle = button.getAttribute("data-product-handle") || "";
  if (!handle) return;
  var variantId = button.getAttribute("data-variant-id") || "";
  var title = button.getAttribute("data-product-title") || "";
  var modalId = "jw-cart-rec-quick-buy";
  var modal = document.getElementById(modalId);
  if (!modal) {
    modal = document.createElement("quick-buy-modal");
    modal.id = modalId;
    modal.className = "quick-buy-modal modal";
    document.body.appendChild(modal);
  }
  modal.setAttribute("handle", handle + (variantId ? "?variant=" + variantId : ""));
  modal.setAttribute("aria-label", title ? "Choose options for " + title : "Choose options");
  if (modal.open && typeof modal.hide === "function") {
    modal.hide().then(function() {
      if (typeof modal.show === "function") modal.show();
    }).catch(function() {
      if (typeof modal.show === "function") modal.show();
    });
    return;
  }
  if (typeof modal.show === "function") modal.show();
};
var __jwOnPlainCartClick = function(event) {
  var discountApply = event.target.closest("[data-jw-discount-apply]");
  if (discountApply && this.contains(discountApply)) {
    event.preventDefault();
    event.stopPropagation();
    var discountWrap = discountApply.closest("[data-jw-discount]");
    var discountInput = discountWrap && discountWrap.querySelector("[data-jw-discount-input]");
    __jwApplyCartDiscountCode(this, discountInput && discountInput.value, "");
    return;
  }
  var discountRemove = event.target.closest("[data-jw-discount-remove]");
  if (discountRemove && this.contains(discountRemove)) {
    event.preventDefault();
    event.stopPropagation();
    __jwApplyCartDiscountCode(this, "", discountRemove.getAttribute("data-jw-discount-remove"));
    return;
  }
  var recAddButton = event.target.closest("[data-jw-rec-add]");
  if (recAddButton && this.contains(recAddButton)) {
    event.preventDefault();
    event.stopPropagation();
    if (recAddButton.disabled) return;
    __jwOpenCartRecQuickBuy(recAddButton);
    return;
  }
  var recArrow = event.target.closest("[data-jw-rec-dir]");
  if (recArrow && this.contains(recArrow)) {
    event.preventDefault();
    var arrowRecs = recArrow.closest(".jw-cart-recommendations");
    var arrowList = arrowRecs && arrowRecs.querySelector(".jw-cart-recommendations__list");
    if (!arrowList) return;
    var dir = parseInt(recArrow.getAttribute("data-jw-rec-dir"), 10) || 1;
    arrowList.scrollBy({ left: dir * Math.max(160, arrowList.clientWidth * 0.85), behavior: "smooth" });
    return;
  }
  var recommendationDot = event.target.closest("[data-jw-rec-dot]");
  if (recommendationDot && this.contains(recommendationDot)) {
    event.preventDefault();
    var recommendations = recommendationDot.closest(".jw-cart-recommendations");
    var list = recommendations && recommendations.querySelector(".jw-cart-recommendations__list");
    var cards = list ? list.querySelectorAll(".jw-cart-recommendation") : [];
    var index = Math.max(0, parseInt(recommendationDot.dataset.jwRecDot, 10) || 0);
    if (cards[index]) cards[index].scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
    recommendations.querySelectorAll("[data-jw-rec-dot]").forEach(function(dot, dotIndex) {
      var active = dotIndex === index;
      dot.classList.toggle("is-active", active);
      dot.setAttribute("aria-current", active ? "true" : "false");
    });
    return;
  }
  var removeButton = event.target.closest("[data-jw-remove-line]");
  if (removeButton && this.contains(removeButton)) {
    event.preventDefault();
    if (removeButton.getAttribute("aria-busy") === "true") return;
    removeButton.setAttribute("aria-busy", "true");
    __jwCommitPlainCartMutation(this, `${Shopify.routes.root}cart/change.js`, {
      id: removeButton.dataset.jwRemoveLine,
      quantity: 0
    }, "plain-cart:remove").finally(function() {
      if (removeButton.isConnected) removeButton.removeAttribute("aria-busy");
    });
    return;
  }
  var quantityButton = event.target.closest(".quantity-selector__button");
  if (quantityButton && this.contains(quantityButton)) {
    event.preventDefault();
    var selector = quantityButton.closest(".quantity-selector");
    var input = selector && selector.querySelector("[data-line-key]");
    if (!input) return;
    var delta = quantityButton.textContent.trim() === "+" ? 1 : -1;
    var quantity = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
    input.value = String(quantity);
    __jwCommitPlainCartMutation(this, `${Shopify.routes.root}cart/change.js`, {
      id: input.dataset.lineKey,
      quantity
    }, "plain-cart:quantity");
    return;
  }
};
var __jwOnPlainCartScroll = function(event) {
  var list = event.target.closest && event.target.closest(".jw-cart-recommendations__list");
  if (!list || !this.contains(list)) return;
  var cards = Array.from(list.querySelectorAll(".jw-cart-recommendation"));
  if (!cards.length) return;
  var index = Math.max(0, Math.min(cards.length - 1, Math.round(list.scrollLeft / Math.max(1, list.clientWidth))));
  var recommendations = list.closest(".jw-cart-recommendations");
  recommendations.querySelectorAll("[data-jw-rec-dot]").forEach(function(dot, dotIndex) {
    var active = dotIndex === index;
    dot.classList.toggle("is-active", active);
    dot.setAttribute("aria-current", active ? "true" : "false");
  });
};
var __jwOnPlainCartChange = function(event) {
  var input = event.target.closest(".quantity-selector__input[data-line-key]");
  if (!input || !this.contains(input)) return;
  var quantity = Math.max(0, parseInt(input.value, 10) || 0);
  __jwCommitPlainCartMutation(this, `${Shopify.routes.root}cart/change.js`, {
    id: input.dataset.lineKey,
    quantity
  }, "plain-cart:quantity");
};
var _sectionId, _cartGeneration, _cartRefreshAbort, _CartDrawer_instances, onBundleSection_fn, onCartChange_fn, onBeforeShow_fn, onPageShow_fn, refreshCart_fn, replaceContent_fn;
var CartDrawer = class extends Drawer {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CartDrawer_instances);
    __privateAdd(this, _sectionId);
    __privateAdd(this, _cartGeneration, 0);
    __privateAdd(this, _cartRefreshAbort);
  }
  connectedCallback() {
    super.connectedCallback();
    __privateGet(this, _sectionId) ?? __privateSet(this, _sectionId, extractSectionId(this));
    document.addEventListener("cart:prepare-bundled-sections", __privateMethod(this, _CartDrawer_instances, onBundleSection_fn).bind(this), { signal: this.abortController.signal });
    document.addEventListener("cart:change", __privateMethod(this, _CartDrawer_instances, onCartChange_fn).bind(this), { signal: this.abortController.signal });
    document.addEventListener("cart:refresh", __privateMethod(this, _CartDrawer_instances, refreshCart_fn).bind(this), { signal: this.abortController.signal });
    window.addEventListener("pageshow", __privateMethod(this, _CartDrawer_instances, onPageShow_fn).bind(this), { signal: this.abortController.signal });
    this.addEventListener("dialog:before-show", __privateMethod(this, _CartDrawer_instances, onBeforeShow_fn));
    this.addEventListener("click", __jwOnPlainCartClick, { signal: this.abortController.signal });
    this.addEventListener("keydown", function(event) {
      var discountInput = event.target && event.target.closest && event.target.closest("[data-jw-discount-input]");
      if (!discountInput || !this.contains(discountInput) || event.key !== "Enter") return;
      event.preventDefault();
      event.stopPropagation();
      __jwApplyCartDiscountCode(this, discountInput.value, "");
    }.bind(this), { signal: this.abortController.signal });
    this.addEventListener("change", __jwOnPlainCartChange, { signal: this.abortController.signal });
    this.addEventListener("scroll", __jwOnPlainCartScroll, { signal: this.abortController.signal, capture: true });
    // Hydrate from cart.js so any leftover CE SSR is replaced with prestige-plain HTML.
    __privateMethod(this, _CartDrawer_instances, refreshCart_fn).call(this);
  }
  disconnectedCallback() {
    __privateGet(this, _cartRefreshAbort)?.abort();
    super.disconnectedCallback();
  }
};
_sectionId = new WeakMap();
_cartGeneration = new WeakMap();
_cartRefreshAbort = new WeakMap();
_CartDrawer_instances = new WeakSet();
/**
 * Prestige-plain: do NOT request cart-drawer section HTML on ATC.
 * Section remount upgrades a large CE tree and Aw Snaps Chromium on CDN/preview.
 */
onBundleSection_fn = function(event) {
  // intentionally empty — cart JSON + __jwBuildMinimalCartDrawerHtml instead
};
onCartChange_fn = async function(event) {
  const cart = event.detail && event.detail.cart;
  if (cart && typeof cart === "object" && Array.isArray(cart.items)) {
    const generation = __privateGet(this, _cartGeneration) + 1;
    __privateSet(this, _cartGeneration, generation);
    __privateGet(this, _cartRefreshAbort)?.abort();
    await __privateMethod(this, _CartDrawer_instances, replaceContent_fn).call(this, cart, generation);
    } else {
    await __privateMethod(this, _CartDrawer_instances, refreshCart_fn).call(this);
  }
  if (event.detail && event.detail.baseEvent === "variant:add") {
    // Always open the side cart on ATC unless cart type is message-only
    // (unless a form explicitly forces the drawer via on_success).
    if (window.themeVariables.settings.cartType !== "message" || event.detail["onSuccessDo"] === "force_open_drawer") {
      this.show();
    }
  }
};
onBeforeShow_fn = async function() {
  const drawerFooter = this.shadowRoot && this.shadowRoot.querySelector('[part="footer"]');
  if (!drawerFooter) {
    return;
  }
  drawerFooter.style.opacity = "0";
  await waitForEvent(this, "dialog:after-show");
  animate14(drawerFooter, { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] }, { duration: 0.25, easing: [0.25, 0.46, 0.45, 0.94] });
};
onPageShow_fn = async function(event) {
  if (!event.persisted) {
    return;
  }
  __privateMethod(this, _CartDrawer_instances, refreshCart_fn).call(this);
};
refreshCart_fn = async function() {
  const generation = __privateGet(this, _cartGeneration) + 1;
  __privateSet(this, _cartGeneration, generation);
  __privateGet(this, _cartRefreshAbort)?.abort();
  const controller = new AbortController();
  __privateSet(this, _cartRefreshAbort, controller);
  try {
    const response = await fetch(`${Shopify.routes.root}cart.js`, { signal: controller.signal });
    const cart = await response.json();
    if (generation !== __privateGet(this, _cartGeneration) || controller.signal.aborted) return;
    await __privateMethod(this, _CartDrawer_instances, replaceContent_fn).call(this, cart, generation);
      } catch (e) {
  }
};
replaceContent_fn = async function(cartOrHtml, generation) {
  let cart = cartOrHtml;
  if (typeof cartOrHtml === "string" || !cartOrHtml || !Array.isArray(cartOrHtml.items)) {
    __privateMethod(this, _CartDrawer_instances, refreshCart_fn).call(this);
    return;
  }
  if (generation != null && generation !== __privateGet(this, _cartGeneration)) return;
  const itemCount = typeof cart.item_count === "number" ? cart.item_count : 0;
  const drawerConfig = __jwReadCartDrawerConfig(this);
  await __jwEnsureCartCompareAt(cart, drawerConfig);
  if (generation != null && generation !== __privateGet(this, _cartGeneration)) return;
  // Demote any accidental hyphenated tags before assign (defense in depth).
  const inner = __jwDemoteCustomElementsHtml(__jwBuildMinimalCartDrawerHtml(cart, drawerConfig));
  while (this.firstChild) {
    this.removeChild(this.firstChild);
  }
  // Re-seed config + templates so a later cache miss can recover the recs pool.
  const persist = [];
  if (drawerConfig.configJson) {
    persist.push('<script type="application/json" data-jw-cart-recommendation-config>' + drawerConfig.configJson + "</script>");
  }
  if (drawerConfig.trustHtml) persist.push('<template data-jw-cart-trust-template>' + drawerConfig.trustHtml + "</template>");
  if (drawerConfig.emptyHtml) persist.push('<template data-jw-cart-empty-template>' + drawerConfig.emptyHtml + "</template>");
  if (drawerConfig.protectionHtml) persist.push('<template data-jw-cart-protection-template>' + drawerConfig.protectionHtml + "</template>");
  this.innerHTML = persist.join("") + inner;
  if (window.__jwDiscountError) {
    var discountError = this.querySelector("[data-jw-discount-error]");
    var discountDraft = this.querySelector("[data-jw-discount-input]");
    if (discountError) discountError.hidden = false;
    if (discountDraft && window.__jwDiscountDraft) discountDraft.value = window.__jwDiscountDraft;
    window.__jwDiscountError = "";
    window.__jwDiscountDraft = "";
  }
    this.classList.toggle("drawer--center-body", itemCount === 0);
  __jwSyncFsTimers(this);
  __jwLoadCartRecentProducts(this, cart, drawerConfig);
  const drawer = this;
  requestAnimationFrame(() => {
    drawer.dispatchEvent(new CustomEvent("cart-drawer:refreshed", { bubbles: true }));
  });
};
var CartNoteDialog = class extends DialogElement {
  createEnterAnimationControls() {
    return animate14(this, { transform: ["translateY(100%)", "translateY(0)"] }, { duration: 0.2, easing: "ease-in" });
  }
  createLeaveAnimationControls() {
    return animate14(this, { transform: ["translateY(0)", "translateY(100%)"] }, { duration: 0.2, easing: "ease-in" });
  }
};
if (!window.customElements.get("cart-drawer")) {
  window.customElements.define("cart-drawer", CartDrawer);
}
if (!window.customElements.get("cart-note-dialog")) {
  window.customElements.define("cart-note-dialog", CartNoteDialog);
}
// Header bag uses href=/cart + aria-controls=cart-drawer. If CartDrawer binds via Delegate,
// clicks open the drawer. This capture handler is a safety net so navigation never wins when
// the upgraded drawer is present (modified-clicks still allow opening /cart in a new tab).
if (!window.__jwCartDrawerOpenBound) {
  window.__jwCartDrawerOpenBound = true;
  document.addEventListener("click", function(event) {
    if (event.defaultPrevented) return;
    if (event.button != null && event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var target = event.target;
    if (!target || typeof target.closest !== "function") return;
    var control = target.closest('[aria-controls="cart-drawer"]');
    if (!control) return;
    var drawer = document.getElementById("cart-drawer");
    if (!drawer || typeof drawer.show !== "function" || typeof drawer.hide !== "function") return;
    event.preventDefault();
    event.stopPropagation();
    if (drawer.open) drawer.hide();
    else drawer.show();
  }, true);
}

// js/sections/collection.js
import { timeline as timeline8, inView as inView11, Delegate as Delegate7 } from "vendor";
var _CollectionBanner_instances, reveal_fn2;
var CollectionBanner = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CollectionBanner_instances);
  }
  connectedCallback() {
    if (this.hasAttribute("reveal-on-scroll") && matchesMediaQuery("motion-safe")) {
      inView11(this, __privateMethod(this, _CollectionBanner_instances, reveal_fn2).bind(this));
    }
  }
};
_CollectionBanner_instances = new WeakSet();
reveal_fn2 = async function() {
  const image = this.querySelector(".content-over-media > picture img, .content-over-media > image-parallax img"), hasParallax = this.querySelector(".content-over-media image-parallax") !== null, content = this.querySelector(".content-over-media > .prose");
  await imageLoaded(image);
  const transformEffect = 0.15 * 100 / 1.3;
  const imageTransform = hasParallax ? [`scale(1.5) translateY(-${transformEffect}%)`, `scale(1.3) translateY(-${transformEffect}%)`] : ["scale(1.2)", "scale(1)"];
  return timeline8([
    [this, { opacity: 1 }, { duration: 0, easing: [0.25, 0.46, 0.45, 0.94] }],
    [image, { opacity: [0, 1], transform: imageTransform }, { duration: 0.8, delay: 0.25, at: "<", easing: [0.25, 0.46, 0.45, 0.94] }],
    [content, { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] }, { duration: 0.6, at: "-0.4", easing: [0.215, 0.61, 0.355, 1] }]
  ]);
};
var _delegate6, _CollectionLayoutSwitch_instances, onLayoutSwitch_fn, setCartAttribute_fn;
var CollectionLayoutSwitch = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CollectionLayoutSwitch_instances);
    __privateAdd(this, _delegate6, new Delegate7(this));
  }
  connectedCallback() {
    __privateGet(this, _delegate6).on("click", 'button[type="button"]', __privateMethod(this, _CollectionLayoutSwitch_instances, onLayoutSwitch_fn).bind(this));
  }
  get controlledList() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
};
_delegate6 = new WeakMap();
_CollectionLayoutSwitch_instances = new WeakSet();
onLayoutSwitch_fn = function(event, target) {
  if (target.classList.contains("is-active")) {
    return;
  }
  this.controlledList.setAttribute(`collection-${this.getAttribute("device")}-layout`, target.value);
  Array.from(this.querySelectorAll("button")).forEach((item) => item.classList.toggle("is-active", item === target));
  this.controlledList.reveal();
  __privateMethod(this, _CollectionLayoutSwitch_instances, setCartAttribute_fn).call(this, target.value);
};
setCartAttribute_fn = function(newLayout) {
  const attributeProperty = `products_${this.getAttribute("device")}_grid_mode`;
  fetch(`${Shopify.routes.root}cart/update.js`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      attributes: {
        [attributeProperty]: newLayout
      }
    }),
    keepalive: true
    // Allows to make sure the request is fired even when submitting the form
  });
};
if (!window.customElements.get("collection-banner")) {
  window.customElements.define("collection-banner", CollectionBanner);
}
if (!window.customElements.get("collection-layout-switch")) {
  window.customElements.define("collection-layout-switch", CollectionLayoutSwitch);
}

// js/sections/countdown-timer.js
import { animate as animate15, inView as inView12 } from "vendor";
var _flips, _expirationDate, _interval, _isVisible, _CountdownTimer_instances, recalculateFlips_fn;
var CountdownTimer = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CountdownTimer_instances);
    __privateAdd(this, _flips);
    __privateAdd(this, _expirationDate);
    __privateAdd(this, _interval);
    __privateAdd(this, _isVisible);
  }
  connectedCallback() {
    __privateSet(this, _flips, Array.from(this.querySelectorAll("countdown-timer-flip")));
    const expiresAt = this.getAttribute("expires-at");
    if (expiresAt !== "") {
      __privateSet(this, _expirationDate, new Date(expiresAt));
      __privateSet(this, _interval, setInterval(__privateMethod(this, _CountdownTimer_instances, recalculateFlips_fn).bind(this), 1e3));
      __privateMethod(this, _CountdownTimer_instances, recalculateFlips_fn).call(this);
    }
    inView12(
      this,
      () => {
        __privateSet(this, _isVisible, true);
        return () => __privateSet(this, _isVisible, false);
      },
      { margin: "500px" }
    );
  }
  disconnectedCallback() {
    clearInterval(__privateGet(this, _interval));
  }
  get daysFlip() {
    return __privateGet(this, _flips).find((flip) => flip.getAttribute("type") === "days");
  }
  get hoursFlip() {
    return __privateGet(this, _flips).find((flip) => flip.getAttribute("type") === "hours");
  }
  get minutesFlip() {
    return __privateGet(this, _flips).find((flip) => flip.getAttribute("type") === "minutes");
  }
  get secondsFlip() {
    return __privateGet(this, _flips).find((flip) => flip.getAttribute("type") === "seconds");
  }
};
_flips = new WeakMap();
_expirationDate = new WeakMap();
_interval = new WeakMap();
_isVisible = new WeakMap();
_CountdownTimer_instances = new WeakSet();
recalculateFlips_fn = function() {
  const dateNow = /* @__PURE__ */ new Date();
  if (__privateGet(this, _expirationDate) < dateNow) {
    if (this.getAttribute("expiration-behavior") === "hide") {
      this.closest(".shopify-section").remove();
    } else {
      return clearInterval(__privateGet(this, _interval));
    }
  }
  if (!__privateGet(this, _isVisible)) {
    return;
  }
  let delta = Math.abs(__privateGet(this, _expirationDate) - dateNow) / 1e3;
  const days = Math.floor(delta / 86400);
  delta -= days * 86400;
  const hours = Math.floor(delta / 3600) % 24;
  delta -= hours * 3600;
  const minutes = Math.floor(delta / 60) % 60;
  delta -= minutes * 60;
  const seconds = Math.floor(delta % 60);
  this.daysFlip?.updateValue(days);
  this.hoursFlip?.updateValue(hours);
  this.minutesFlip?.updateValue(minutes);
  this.secondsFlip?.updateValue(seconds);
};
var CountdownTimerFlip = class extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    let flipHtml = [...this.textContent].map(
      () => `<countdown-timer-flip-digit part="digit" ${this.hasAttribute("animate") ? "animate" : ""} style="display: inline-block">0</countdown-timer-flip-digit>`
    );
    this.shadowRoot.appendChild(document.createRange().createContextualFragment(flipHtml.join("")));
  }
  updateValue(value) {
    const newValue = Math.min(99, value).toString().padStart(2, "0");
    [...newValue].forEach((digit, index) => {
      this.shadowRoot.children[index].setAttribute("number", digit);
    });
  }
};
var CountdownTimerFlipDigit = class extends HTMLElement {
  static observedAttributes = ["number"];
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment("<div><slot></slot></div>"));
  }
  async attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || !this.hasAttribute("animate")) {
      return this.textContent = newValue;
    }
    await animate15(this.shadowRoot.firstElementChild, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-8px)"] }, { duration: 0.3, easing: [0.64, 0, 0.78, 0] }).finished;
    this.textContent = newValue;
    animate15(this.shadowRoot.firstElementChild, { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0px)"] }, { duration: 0.3, easing: [0.22, 1, 0.36, 1] });
  }
};
if (!window.customElements.get("countdown-timer")) {
  window.customElements.define("countdown-timer", CountdownTimer);
}
if (!window.customElements.get("countdown-timer-flip")) {
  window.customElements.define("countdown-timer-flip", CountdownTimerFlip);
}
if (!window.customElements.get("countdown-timer-flip-digit")) {
  window.customElements.define("countdown-timer-flip-digit", CountdownTimerFlipDigit);
}

// js/sections/dynamic-grid.js
import { animate as animate16, scroll as scroll2 } from "vendor";
var _DynamicGrid_instances, setupEmulateScrollTimeline_fn;
var DynamicGrid = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _DynamicGrid_instances);
    if (window.Shopify.designMode) {
      this.closest(".shopify-section")?.addEventListener("shopify:section:select", () => this.classList.add("editor-is-selected"));
      this.closest(".shopify-section")?.addEventListener("shopify:section:deselect", () => this.classList.remove("editor-is-selected"));
      this.addEventListener("shopify:block:select", (event) => event.target.classList.add("editor-is-selected"));
      this.addEventListener("shopify:block:deselect", (event) => event.target.classList.remove("editor-is-selected"));
    }
  }
  connectedCallback() {
    if (!("ViewTimeline" in window)) {
      __privateMethod(this, _DynamicGrid_instances, setupEmulateScrollTimeline_fn).call(this);
    }
  }
};
_DynamicGrid_instances = new WeakSet();
/**
 * On older browsers, we fallback to a more standard "scroll listener" based approach, by using MotionJS.
 */
setupEmulateScrollTimeline_fn = function() {
  Array.from(this.querySelectorAll(".dynamic-grid__cell")).forEach((element) => {
    const parallaxSpeed = parseInt(element.style.getPropertyValue("--parallax-speed"));
    if (parallaxSpeed === 0) {
      return;
    }
    const offsetAmount = `${parallaxSpeed * 10 / 2}vmin`;
    scroll2(
      animate16(element, {
        transform: element.getAttribute("data-parallax-direction") === "horizontal" ? [`translateX(calc(-1 * ${offsetAmount}))`, `translateX(${offsetAmount})`] : [`translateY(calc(-1 * ${offsetAmount}))`, `translateY(${offsetAmount})`]
      }),
      {
        offset: ["start end", "end start"],
        target: this.closest(".shopify-section")
      }
    );
  });
};
if (!window.customElements.get("dynamic-grid")) {
  window.customElements.define("dynamic-grid", DynamicGrid);
}

// js/sections/customer.js
import { animate as animate17 } from "vendor";
var _AccountLogin_instances, loginForm_get, recoverForm_get, switchForm_fn;
var AccountLogin = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _AccountLogin_instances);
    window.addEventListener("hashchange", __privateMethod(this, _AccountLogin_instances, switchForm_fn).bind(this));
    if (window.location.hash === "#recover") {
      __privateGet(this, _AccountLogin_instances, loginForm_get).hidden = true;
      __privateGet(this, _AccountLogin_instances, recoverForm_get).hidden = false;
    }
  }
};
_AccountLogin_instances = new WeakSet();
loginForm_get = function() {
  return this.querySelector("#login");
};
recoverForm_get = function() {
  return this.querySelector("#recover");
};
switchForm_fn = async function() {
  const fromForm = window.location.hash === "#recover" ? __privateGet(this, _AccountLogin_instances, loginForm_get) : __privateGet(this, _AccountLogin_instances, recoverForm_get), toForm = window.location.hash === "#recover" ? __privateGet(this, _AccountLogin_instances, recoverForm_get) : __privateGet(this, _AccountLogin_instances, loginForm_get);
  await animate17(fromForm, { transform: ["translateY(0)", "translateY(30px)"], opacity: [1, 0] }, { duration: 0.6, easing: "ease" }).finished;
  fromForm.hidden = true;
  toForm.hidden = false;
  await animate17(toForm, { transform: ["translateY(30px)", "translateY(0)"], opacity: [0, 1] }, { duration: 0.6, easing: "ease" });
};
if (!window.customElements.get("account-login")) {
  window.customElements.define("account-login", AccountLogin);
}

// js/sections/faq.js
var _observer, _FaqToc_instances, onObserve_fn;
var FaqToc = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _FaqToc_instances);
    __privateAdd(this, _observer, new IntersectionObserver(__privateMethod(this, _FaqToc_instances, onObserve_fn).bind(this), { rootMargin: "0px 0px -70% 0px" }));
  }
  connectedCallback() {
    this.anchoredElements.forEach((anchoredElement) => __privateGet(this, _observer).observe(anchoredElement));
  }
  disconnectedCallback() {
    __privateGet(this, _observer).disconnect();
  }
  get anchorLinks() {
    return Array.from(this.querySelectorAll('a[href^="#"]'));
  }
  get anchoredElements() {
    return this.anchorLinks.map((anchor) => document.querySelector(anchor.getAttribute("href")));
  }
};
_observer = new WeakMap();
_FaqToc_instances = new WeakSet();
onObserve_fn = function(entries) {
  for (const entry of entries) {
    const anchorLink = this.anchorLinks.find((anchor) => anchor.getAttribute("href") === `#${entry.target.id}`);
    if (!entry.isIntersecting && anchorLink.classList.contains("is-active")) {
      continue;
    }
    if (entry.isIntersecting) {
      this.anchorLinks.forEach((link) => link.classList.toggle("is-active", link === anchorLink));
    }
  }
};
if (!window.customElements.get("faq-toc")) {
  window.customElements.define("faq-toc", FaqToc);
}

// js/sections/featured-collections.js
import { animate as animate18 } from "vendor";
var FeaturedCollectionsCarousel = class extends EffectCarousel {
  connectedCallback() {
    super.connectedCallback();
    this.#hydrateDeferredTab(this.selectedIndex);
    this.#syncHeaderShopNow(this.selectedIndex);
    this.addEventListener("carousel:change", (event) => {
      this.#hydrateDeferredTab(event.detail.index);
      this.#syncHeaderShopNow(event.detail.index);
    });
    this.addEventListener("carousel:settle", () => {
      this.cleanUpAnimations();
      this.#syncHeaderShopNow(this.selectedIndex);
    });
  }
  #hydrateDeferredTab(index) {
    const slide = this.cells?.[index];
    if (!slide) return;
    const tpl = slide.querySelector("template.jw-deferred-tab");
    if (!tpl || !tpl.content) return;
    slide.appendChild(tpl.content);
    tpl.remove();
    slide.querySelector("product-list")?.revealPending?.(true);
  }
  /**
   * Header SHOP NOW is outside animated slides. Only sync href/label on tab
   * change — never move the button into the product grid.
   */
  #syncHeaderShopNow(index) {
    const root = this.closest(".shopify-section");
    const shopNow = root?.querySelector("[data-featured-collections-shop-now]");
    const slide = this.cells?.[index];
    if (!shopNow || !slide) {
      return;
    }
    if (slide.dataset.shopUrl) {
      shopNow.setAttribute("href", slide.dataset.shopUrl);
    }
    if (slide.dataset.shopLabel) {
      shopNow.textContent = slide.dataset.shopLabel;
    }
  }
  createOnChangeAnimationControls(fromSlide, toSlide) {
    // Opacity only — transform on a slide creates a containing block for any
    // absolutely positioned descendants (legacy CTA bug after tab clicks).
    return {
      leaveControls: () => animate18(fromSlide, { opacity: [1, 0] }, { duration: 0.25, easing: "ease-in" }),
      enterControls: () => animate18(toSlide, { opacity: [0, 1] }, { duration: 0.25, easing: "ease-out" })
    };
  }
};
if (!window.customElements.get("featured-collections-carousel")) {
  window.customElements.define("featured-collections-carousel", FeaturedCollectionsCarousel);
}

// js/sections/header.js
import { animate as animate19, timeline as timeline9, stagger as stagger3, Delegate as Delegate8 } from "vendor";
var _headerTrackerIntersectionObserver, _abortController11, _scrollYTrackingPosition, _isVisible2, _Header_instances, onHeaderTrackerIntersection_fn, detectMousePosition_fn, detectScrollDirection_fn, setVisibility_fn;
var Header = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _Header_instances);
    __privateAdd(this, _headerTrackerIntersectionObserver, new IntersectionObserver(__privateMethod(this, _Header_instances, onHeaderTrackerIntersection_fn).bind(this)));
    __privateAdd(this, _abortController11);
    __privateAdd(this, _scrollYTrackingPosition, 0);
    __privateAdd(this, _isVisible2, true);
  }
  connectedCallback() {
    __privateSet(this, _abortController11, new AbortController());
    const headerTracker = document.getElementById("header-scroll-tracker");
    if (headerTracker) {
      __privateGet(this, _headerTrackerIntersectionObserver).observe(headerTracker);
    }
    if (this.hasAttribute("hide-on-scroll")) {
      window.addEventListener("scroll", __privateMethod(this, _Header_instances, detectScrollDirection_fn).bind(this), { passive: true, signal: __privateGet(this, _abortController11).signal });
      window.addEventListener("pointermove", __privateMethod(this, _Header_instances, detectMousePosition_fn).bind(this), { passive: true, signal: __privateGet(this, _abortController11).signal });
    }
  }
  disconnectedCallback() {
    __privateGet(this, _abortController11).abort();
  }
};
_headerTrackerIntersectionObserver = new WeakMap();
_abortController11 = new WeakMap();
_scrollYTrackingPosition = new WeakMap();
_isVisible2 = new WeakMap();
_Header_instances = new WeakSet();
onHeaderTrackerIntersection_fn = function(entries) {
  this.classList.toggle("is-solid", !entries[0].isIntersecting);
};
detectMousePosition_fn = function(event) {
  if (event.clientY < 100 && window.matchMedia("screen and (pointer: fine)").matches) {
    __privateMethod(this, _Header_instances, setVisibility_fn).call(this, true);
    __privateSet(this, _scrollYTrackingPosition, 0);
  }
};
detectScrollDirection_fn = function() {
  let isVisible;
  const scrollY = Math.max(0, window.scrollY);
  if (scrollY > __privateGet(this, _scrollYTrackingPosition) && scrollY - __privateGet(this, _scrollYTrackingPosition) > 100) {
    isVisible = false;
    __privateSet(this, _scrollYTrackingPosition, scrollY);
  } else if (window.scrollY < __privateGet(this, _scrollYTrackingPosition)) {
    __privateSet(this, _scrollYTrackingPosition, scrollY);
    isVisible = true;
  }
  if (isVisible !== void 0) {
    __privateMethod(this, _Header_instances, setVisibility_fn).call(this, isVisible);
  }
};
setVisibility_fn = function(isVisible) {
  if (isVisible !== __privateGet(this, _isVisible2)) {
    if (!isVisible && this.querySelectorAll(":where(.header__primary-nav, .header__secondary-nav) [open]").length > 0) {
      return;
    }
    __privateSet(this, _isVisible2, isVisible);
    document.documentElement.style.setProperty("--header-is-visible", isVisible ? "1" : "0");
    this.classList.toggle("is-hidden", !isVisible);
  }
};
var DropdownMenuDisclosure = class extends MenuDisclosure {
  createShowAnimationControls() {
    let menuItemsSequence = [];
    if (window.themeVariables.settings.staggerMenuApparition) {
      menuItemsSequence = [
        this.contentElement.querySelectorAll(":scope > li"),
        { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0)"] },
        { duration: 0.15, at: "-0.15", delay: stagger3(0.1) }
      ];
    }
    return timeline9([[this.contentElement, { opacity: [0, 1] }, { duration: 0.25 }], menuItemsSequence]);
  }
  createHideAnimationControls() {
    return timeline9([[this.contentElement, { opacity: [1, 0] }, { duration: 0.4 }]]);
  }
};
var MegaMenuDisclosure = class extends MenuDisclosure {
  createShowAnimationControls() {
    const linklists = Array.from(this.contentElement.querySelectorAll(".mega-menu__linklist > li"));
    const promo = this.contentElement.querySelector(".mega-menu__promo");
    const ease = [0.22, 1, 0.36, 1];
    let menuItemsSequence = [];
    if (window.themeVariables.settings.staggerMenuApparition) {
      menuItemsSequence = [
        { name: "content", at: "-0.45" },
        [linklists, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.32, at: "content", delay: stagger3(0.05), easing: ease }],
        [promo, { opacity: [0, 1], transform: ["translateY(12px)", "translateY(0)"] }, { duration: 0.34, at: "-0.18", easing: ease }]
      ];
    }
    return timeline9([
      [this.contentElement, { opacity: [0, 1], transform: ["translateY(-10px)", "translateY(0)"] }, { duration: 0.3, easing: ease }],
      ...menuItemsSequence
    ]);
  }
  createHideAnimationControls() {
    return timeline9([
      [this.contentElement, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-8px)"] }, { duration: 0.22, easing: [0.4, 0, 1, 1] }]
    ]);
  }
};
var _HeaderSearch_instances, calculateMaxHeight_fn;
var HeaderSearch = class extends DialogElement {
  constructor() {
    super();
    __privateAdd(this, _HeaderSearch_instances);
    this.addEventListener("dialog:before-show", __privateMethod(this, _HeaderSearch_instances, calculateMaxHeight_fn).bind(this));
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("dialog:after-show", () => {
      requestAnimationFrame(() => {
        this.querySelector(".header-search__input")?.focus({ preventScroll: true });
      });
    }, { signal: this.abortController.signal });
  }
  get shadowDomTemplate() {
    return "header-search-default-template";
  }
  get shouldLock() {
    return true;
  }
  createEnterAnimationControls() {
    return timeline9([
      [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.2, easing: [0.645, 0.045, 0.355, 1] }],
      [
        this.getShadowPartByName("content"),
        { opacity: [0, 1], transform: ["translateY(calc(-1 * var(--header-height)))", "translateY(0)"] },
        { duration: 0.2, at: "<", easing: [0.645, 0.045, 0.355, 1] }
      ]
    ]);
  }
  createLeaveAnimationControls() {
    return timeline9([
      [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.2, easing: [0.645, 0.045, 0.355, 1] }],
      [
        this.getShadowPartByName("content"),
        { opacity: [1, 0], transform: ["translateY(0)", "translateY(calc(-1 * var(--header-height)))"] },
        { duration: 0.2, at: "<", easing: [0.645, 0.045, 0.355, 1] }
      ]
    ]);
  }
};
_HeaderSearch_instances = new WeakSet();
calculateMaxHeight_fn = function() {
  const boundingRect = this.getBoundingClientRect(), maxHeight = window.innerHeight - boundingRect.top;
  this.style.setProperty("--header-search-max-height", `${maxHeight}px`);
};
var _collapsiblePanel, _buttonElements, _HeaderSidebar_instances, openCollapsiblePanel_fn, onSidebarBeforeShow_fn, onSidebarAfterShow_fn, onSidebarBeforeHide_fn, onSidebarAfterHide_fn;
var HeaderSidebar = class extends Drawer {
  constructor() {
    super();
    __privateAdd(this, _HeaderSidebar_instances);
    __privateAdd(this, _collapsiblePanel);
    __privateAdd(this, _buttonElements);
    this.addEventListener("dialog:before-show", __privateMethod(this, _HeaderSidebar_instances, onSidebarBeforeShow_fn));
    this.addEventListener("dialog:after-show", __privateMethod(this, _HeaderSidebar_instances, onSidebarAfterShow_fn));
    this.addEventListener("dialog:before-hide", __privateMethod(this, _HeaderSidebar_instances, onSidebarBeforeHide_fn));
    this.addEventListener("dialog:after-hide", __privateMethod(this, _HeaderSidebar_instances, onSidebarAfterHide_fn));
  }
  connectedCallback() {
    super.connectedCallback();
    __privateSet(this, _collapsiblePanel, this.querySelector('[slot="collapsible-panel"]'));
    __privateSet(this, _buttonElements, Array.from(this.querySelectorAll(".header-sidebar__main-panel .header-sidebar__linklist [aria-controls]")));
    __privateGet(this, _buttonElements).forEach((button) => button.addEventListener("click", __privateMethod(this, _HeaderSidebar_instances, openCollapsiblePanel_fn).bind(this), { signal: this.abortController.signal }));
  }
  createEnterAnimationControls() {
    const overlay = this.getShadowPartByName("overlay");
    const content = this.getShadowPartByName("content");
    if (content) content.style.marginInlineStart = "0";
    if (!overlay || !content) {
      return super.createEnterAnimationControls();
    }
    const ease = [0.22, 1, 0.36, 1];
    return timeline9([
      [overlay, { opacity: [0, 1] }, { duration: 0.28, easing: ease }],
      [content, { transform: ["translateX(-100%)", "translateX(0)"] }, { duration: 0.34, at: "<", easing: ease }]
    ]);
  }
  createLeaveAnimationControls() {
    const overlay = this.getShadowPartByName("overlay");
    const content = this.getShadowPartByName("content");
    if (!overlay || !content) {
      return super.createLeaveAnimationControls();
    }
    const ease = [0.4, 0, 1, 1];
    return timeline9([
      [overlay, { opacity: [1, 0] }, { duration: 0.22, easing: ease }],
      [content, { transform: ["translateX(0)", "translateX(-100%)"] }, { duration: 0.28, at: "<", easing: ease }]
    ]);
  }
  revealItems(withDelay = false) {
    if (matchesMediaQuery("md-max")) {
      const panel = this.querySelector(".header-sidebar__main-panel");
      const footer = this.querySelector(".header-sidebar__footer");
      const items = this.querySelectorAll(".header-sidebar__main-panel .header-sidebar__linklist li");
      if (panel) {
        panel.style.opacity = "1";
        panel.style.transform = "none";
      }
      items.forEach((el) => {
        el.style.opacity = "1";
        el.style.transform = "none";
      });
      if (footer) {
        footer.style.opacity = "1";
        footer.style.transform = "none";
      }
      return timeline9([[this, {}, { duration: 0 }]]);
    }
    return timeline9([
      [this.querySelector(".header-sidebar__main-panel"), { opacity: 1, transform: "translateX(0)" }, { duration: 0, delay: withDelay ? 0.5 : 0 }],
      [
        this.querySelectorAll(".header-sidebar__main-panel .header-sidebar__linklist li"),
        { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0)"] },
        { duration: 0.15, at: "-0.15", delay: window.themeVariables.settings.staggerMenuApparition ? stagger3(0.1) : 0 }
      ],
      [this.querySelector(".header-sidebar__footer"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.3 }]
    ]);
  }
};
_collapsiblePanel = new WeakMap();
_buttonElements = new WeakMap();
_HeaderSidebar_instances = new WeakSet();
openCollapsiblePanel_fn = function(event) {
  __privateGet(this, _buttonElements).forEach((button) => button.setAttribute("aria-expanded", button === event.currentTarget ? "true" : "false"));
  __privateGet(this, _collapsiblePanel)?.setAttribute("aria-activedescendant", event.currentTarget.getAttribute("aria-controls"));
  if (matchesMediaQuery("md")) {
    animate19(this.querySelector(".header-sidebar__main-panel"), { opacity: [1, 0], transform: ["translateX(0)", "translateX(-10px)"] }, { duration: 0.25 });
  }
};
onSidebarBeforeShow_fn = function() {
  if (matchesMediaQuery("md-max")) {
    const panel = this.querySelector(".header-sidebar__main-panel");
    const footer = this.querySelector(".header-sidebar__footer");
    if (panel) {
      panel.style.opacity = "1";
      panel.style.transform = "none";
    }
    this.querySelectorAll(".header-sidebar__main-panel .header-sidebar__linklist li").forEach((el) => {
      el.style.opacity = "1";
      el.style.transform = "none";
    });
    if (footer) {
      footer.style.opacity = "1";
      footer.style.transform = "none";
    }
    return;
  }
  animate19(this.querySelector(".header-sidebar__main-panel"), { opacity: 0, transform: "translateX(0)" }, { duration: 0 });
};
onSidebarAfterShow_fn = function() {
  this.revealItems();
};
onSidebarBeforeHide_fn = function() {
  if (matchesMediaQuery("md")) {
    __privateGet(this, _collapsiblePanel)?.removeAttribute("aria-activedescendant");
    __privateGet(this, _buttonElements).forEach((button) => button.setAttribute("aria-expanded", "false"));
  }
};
onSidebarAfterHide_fn = function() {
  if (matchesMediaQuery("md-max")) {
    __privateGet(this, _collapsiblePanel)?.removeAttribute("aria-activedescendant");
    __privateGet(this, _buttonElements).forEach((button) => button.setAttribute("aria-expanded", "false"));
  }
  Array.from(this.querySelectorAll("details")).forEach((detail) => detail.open = false);
};
var _sidebarDelegate, _HeaderSidebarCollapsiblePanel_instances, closePanel_fn, switchPanel_fn;
var HeaderSidebarCollapsiblePanel = class extends DialogElement {
  constructor() {
    super();
    __privateAdd(this, _HeaderSidebarCollapsiblePanel_instances);
    __privateAdd(this, _sidebarDelegate, new Delegate8(this));
    __privateGet(this, _sidebarDelegate).on("click", '[data-action="close-panel"]', __privateMethod(this, _HeaderSidebarCollapsiblePanel_instances, closePanel_fn).bind(this));
  }
  static get observedAttributes() {
    return [...super.observedAttributes, "aria-activedescendant"];
  }
  hideForOutsideClickTarget(target) {
    return false;
  }
  allowOutsideClickForTarget(target) {
    return target.closest(".header-sidebar") !== void 0;
  }
  createEnterAnimationControls() {
    if (matchesMediaQuery("md-max")) {
      return timeline9([[this, { opacity: [1, 1], transform: ["translateX(100%)", "translateX(0)"] }, { duration: 0.3, easing: [0.22, 1, 0.36, 1] }]]);
    } else {
      return timeline9([[this, { opacity: [0, 1], transform: ["translateX(0)", "translateX(calc(var(--transform-logical-flip) * 100%)"] }, { duration: 0.3 }]]);
    }
  }
  createLeaveAnimationControls() {
    if (matchesMediaQuery("md-max")) {
      return timeline9([[this, { opacity: [1, 1], transform: ["translateX(0)", "translateX(100%)"] }, { duration: 0.28, easing: [0.4, 0, 1, 1] }]]);
    } else {
      return timeline9([[this, { opacity: [1, 0], transform: ["translateX(calc(var(--transform-logical-flip) * 100%))", "translateX(0)"] }, { duration: 0.3 }]]);
    }
  }
  async attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === "aria-activedescendant") {
      if (oldValue === newValue) {
        return;
      }
      if (newValue !== null) {
        __privateMethod(this, _HeaderSidebarCollapsiblePanel_instances, switchPanel_fn).call(this, this.querySelector(`#${oldValue}`), this.querySelector(`#${newValue}`));
      } else {
        await this.hide();
        Array.from(this.querySelectorAll(".header-sidebar__sub-panel")).forEach((subPanel) => subPanel.hidden = true);
      }
    }
  }
};
_sidebarDelegate = new WeakMap();
_HeaderSidebarCollapsiblePanel_instances = new WeakSet();
closePanel_fn = function() {
  this.removeAttribute("aria-activedescendant");
  this.closest("header-sidebar").revealItems(true);
};
switchPanel_fn = async function(fromPanel, toPanel) {
  if (!this.open) {
    await this.show();
  }
  if (fromPanel) {
    await animate19(fromPanel, { opacity: [1, 0] }, { duration: 0.15 }).finished;
    fromPanel.hidden = true;
  }
  toPanel.hidden = false;
  if (matchesMediaQuery("md-max")) {
    toPanel.style.opacity = "1";
    toPanel.querySelectorAll(".header-sidebar__back-button, .header-sidebar__linklist li, .header-sidebar__promo").forEach((el) => {
      el.style.opacity = "1";
      el.style.transform = "none";
    });
    return;
  }
  const listSelector = ".header-sidebar__linklist li";
  timeline9([
    [toPanel, { opacity: 1 }, { duration: 0 }],
    [
      toPanel.querySelectorAll(listSelector),
      { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0)"] },
      { duration: 0.15, at: "-0.15", delay: window.themeVariables.settings.staggerMenuApparition ? stagger3(0.1) : 0 }
    ],
    [toPanel.querySelector(".header-sidebar__promo"), { opacity: [0, 1] }, { duration: 0.45 }]
  ]);
};
if (!window.customElements.get("x-header")) {
  window.customElements.define("x-header", Header);
}
if (!window.customElements.get("dropdown-menu-disclosure")) {
  window.customElements.define("dropdown-menu-disclosure", DropdownMenuDisclosure);
}
if (!window.customElements.get("mega-menu-disclosure")) {
  window.customElements.define("mega-menu-disclosure", MegaMenuDisclosure);
}
if (!window.customElements.get("header-search")) {
  window.customElements.define("header-search", HeaderSearch);
}
if (!window.customElements.get("header-sidebar")) {
  window.customElements.define("header-sidebar", HeaderSidebar);
}
if (!window.customElements.get("header-sidebar-collapsible-panel")) {
  window.customElements.define("header-sidebar-collapsible-panel", HeaderSidebarCollapsiblePanel);
}

// js/sections/image-with-text.js
import { animate as animate20, inView as inView13 } from "vendor";
var _ImageWithText_instances, onBecameVisible_fn;
var ImageWithText = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ImageWithText_instances);
  }
  connectedCallback() {
    if (matchesMediaQuery("motion-safe")) {
      inView13(this.querySelector('[reveal-on-scroll="true"]'), ({ target }) => __privateMethod(this, _ImageWithText_instances, onBecameVisible_fn).call(this, target), { margin: "-200px 0px 0px 0px" });
    }
  }
};
_ImageWithText_instances = new WeakSet();
onBecameVisible_fn = async function(target) {
  await imageLoaded(target);
  const fromValue = (window.direction === "rtl" ? -1 : 1) * (matchesMediaQuery("md-max") ? 0.6 : 1) * (this.classList.contains("image-with-text--reverse") ? 25 : -25);
  animate20(target, { opacity: 1, transform: [`translateX(${fromValue}px)`, "translateX(0)"] }, { easing: [0.215, 0.61, 0.355, 1] }, { duration: 0.8 });
};
if (!window.customElements.get("image-with-text")) {
  window.customElements.define("image-with-text", ImageWithText);
}

// js/sections/image-with-text-overlay.js
import { timeline as timeline10, inView as inView14 } from "vendor";
var _preventInitialTransition2, _ImageWithTextOverlay_instances, onBecameVisible_fn2;
var ImageWithTextOverlay = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _ImageWithTextOverlay_instances);
    __privateAdd(this, _preventInitialTransition2, false);
    if (Shopify.designMode) {
      this.closest(".shopify-section").addEventListener("shopify:section:select", (event) => __privateSet(this, _preventInitialTransition2, event.detail.load));
    }
  }
  connectedCallback() {
    if (matchesMediaQuery("motion-safe") && this.getAttribute("reveal-on-scroll") === "true") {
      inView14(this, ({ target }) => __privateMethod(this, _ImageWithTextOverlay_instances, onBecameVisible_fn2).call(this, target), { amount: 0.05 });
    }
  }
};
_preventInitialTransition2 = new WeakMap();
_ImageWithTextOverlay_instances = new WeakSet();
onBecameVisible_fn2 = async function(target) {
  const media = target.querySelector(".content-over-media > picture img, .content-over-media > svg"), content = target.querySelector(".content-over-media > :not(picture, svg)");
  await imageLoaded(media);
  const animationControls = timeline10([
    [target, { opacity: 1 }],
    [media, { opacity: [0, 1], scale: [1.1, 1], easing: [0.215, 0.61, 0.355, 1], duration: 0.8 }],
    [content, { opacity: [0, 1], duration: 0.8 }]
  ]);
  if (__privateGet(this, _preventInitialTransition2)) {
    animationControls.finish();
  }
};
if (!window.customElements.get("image-with-text-overlay")) {
  window.customElements.define("image-with-text-overlay", ImageWithTextOverlay);
}

// js/sections/images-with-text-scroll.js
import { timeline as timeline11, animate as animate21, inView as inView15, scroll as scroll3, ScrollOffset } from "vendor";
var _itemElements, _imageElements, _textElements, _visibleImageElement, _ImagesWithTextScroll_instances, setupScrollObservers_fn, onBreakpointChanged_fn;
var ImagesWithTextScroll = class extends EffectCarousel {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ImagesWithTextScroll_instances);
    __privateAdd(this, _itemElements);
    __privateAdd(this, _imageElements);
    __privateAdd(this, _textElements);
    __privateAdd(this, _visibleImageElement);
  }
  // Reference to the currently visible image elements
  connectedCallback() {
    super.connectedCallback();
    __privateSet(this, _itemElements, Array.from(this.querySelectorAll(".images-with-text-scroll__item")));
    __privateSet(this, _imageElements, Array.from(this.querySelectorAll(".images-with-text-scroll__image")));
    __privateSet(this, _textElements, Array.from(this.querySelectorAll(".images-with-text-scroll__text")));
    __privateSet(this, _visibleImageElement, __privateGet(this, _imageElements)[0]);
    inView15(this, () => {
      __privateGet(this, _imageElements).forEach((imageElement) => imageElement.removeAttribute("loading"));
    });
    if (matchesMediaQuery("md")) {
      __privateMethod(this, _ImagesWithTextScroll_instances, setupScrollObservers_fn).call(this);
    }
    mediaQueryListener("md", __privateMethod(this, _ImagesWithTextScroll_instances, onBreakpointChanged_fn).bind(this));
  }
  /**
   * Override the "cellSelector". In this component, what makes a "slide" (on mobile) is the piece of text
   */
  get cellSelector() {
    return ".images-with-text-scroll__item";
  }
  /**
   * Swipe should only be available on mobile and tablet, otherwise it is a scroll-based experience
   */
  get allowSwipe() {
    return matchesMediaQuery("md-max");
  }
  /**
   * Perform the mobile animation
   */
  createOnChangeAnimationControls(fromSlide, toSlide, { direction } = {}) {
    let imageAnimationSequence = [], toSlideImage = toSlide.querySelector(".images-with-text-scroll__image");
    if (toSlideImage && toSlideImage !== __privateGet(this, _visibleImageElement)) {
      imageAnimationSequence.push(
        [__privateGet(this, _visibleImageElement), { opacity: [1, 0] }, { duration: 0.8, delay: 0.4 }],
        [toSlideImage, { opacity: [0, 1] }, { duration: 0.8, at: "<", delay: 0.4 }]
      );
      __privateSet(this, _visibleImageElement, toSlideImage);
    }
    return timeline11([
      ...imageAnimationSequence,
      [
        fromSlide.querySelector(".images-with-text-scroll__text"),
        { opacity: [1, 0], transform: ["translateY(0)", "translateY(-15px)"] },
        { duration: 0.4, at: "<", easing: [0.55, 0.055, 0.675, 0.19] }
      ],
      [
        toSlide.querySelector(".images-with-text-scroll__text"),
        { opacity: [0, 1], transform: ["translateY(15px)", "translateY(0)"] },
        { duration: 0.4, at: "+0.4", easing: [0.25, 0.46, 0.45, 0.94] }
      ]
    ]);
  }
};
_itemElements = new WeakMap();
_imageElements = new WeakMap();
_textElements = new WeakMap();
_visibleImageElement = new WeakMap();
_ImagesWithTextScroll_instances = new WeakSet();
/**
 * Setup the different observers for the desktop experience
 */
setupScrollObservers_fn = function() {
  __privateGet(this, _textElements).forEach((textElement) => {
    scroll3(animate21(textElement, { opacity: [0, 0.25, 1, 0.25, 0] }), { target: textElement, offset: ScrollOffset.Any });
  });
  scroll3(
    (info) => {
      const index = Math.min(Math.floor(info.y.progress / (1 / __privateGet(this, _itemElements).length)), __privateGet(this, _itemElements).length - 1), toImage = __privateGet(this, _itemElements)[index].querySelector(".images-with-text-scroll__image");
      if (toImage && toImage !== __privateGet(this, _visibleImageElement)) {
        timeline11([
          [__privateGet(this, _visibleImageElement), { opacity: [1, 0] }, { duration: 0.25 }],
          [toImage, { opacity: [0, 1] }, { duration: 0.25, at: "<" }]
        ]);
        __privateSet(this, _visibleImageElement, toImage);
      }
    },
    { target: this, offset: ["start center", "end center"] }
  );
};
/**
 * Due to how different the experience is on mobile and desktop, we use an observer to toggle between one mode and other
 */
onBreakpointChanged_fn = function(event) {
  if (event.matches) {
    __privateGet(this, _imageElements).forEach((image) => image.style = null);
    __privateGet(this, _textElements).forEach((text) => text.style = null);
    __privateMethod(this, _ImagesWithTextScroll_instances, setupScrollObservers_fn).call(this);
  } else {
    this.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
  }
};
if (!window.customElements.get("images-with-text-scroll")) {
  window.customElements.define("images-with-text-scroll", ImagesWithTextScroll);
}

// js/sections/main-article.js
import { scroll as scroll4 } from "vendor";
var ArticleToolbar = class extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      scroll4(
        (info) => {
          this.classList.toggle("is-visible", info.y.progress > 0 && info.y.progress < 1);
        },
        {
          target: this.closest(".shopify-section"),
          offset: ["100px start", "end start"]
        }
      );
    }
  }
};
if (!window.customElements.get("article-toolbar")) {
  window.customElements.define("article-toolbar", ArticleToolbar);
}

// js/sections/media-grid.js
import { animate as animate22, inView as inView16 } from "vendor";
var _MediaGrid_instances, onReveal_fn;
var MediaGrid = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _MediaGrid_instances);
  }
  connectedCallback() {
    if (matchesMediaQuery("motion-safe")) {
      inView16(this.querySelectorAll('[reveal-on-scroll="true"]'), __privateMethod(this, _MediaGrid_instances, onReveal_fn).bind(this), { margin: "-200px 0px 0px 0px" });
    }
  }
};
_MediaGrid_instances = new WeakSet();
onReveal_fn = async function(entry) {
  await imageLoaded(entry.target.querySelector(":scope > img"));
  animate22(entry.target, { opacity: [0, 1] }, { duration: 0.35, easing: "ease" });
};
if (!window.customElements.get("media-grid")) {
  window.customElements.define("media-grid", MediaGrid);
}

// js/sections/multi-column.js
var MultiColumn = class extends HTMLElement {
  constructor() {
    super();
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => {
        event.target.scrollIntoView({ inline: "center", block: "nearest", behavior: event.detail["load"] ? "auto" : "smooth" });
      });
    }
  }
};
if (!window.customElements.get("multi-column")) {
  window.customElements.define("multi-column", MultiColumn);
}

// js/sections/multiple-media-with-text.js
import { timeline as timeline12, inView as inView17 } from "vendor";
var _preventInitialTransition3, _MultipleMediaWithText_instances, onBecameVisible_fn3;
var MultipleMediaWithText = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _MultipleMediaWithText_instances);
    __privateAdd(this, _preventInitialTransition3, false);
    if (Shopify.designMode) {
      this.closest(".shopify-section").addEventListener("shopify:section:select", (event) => __privateSet(this, _preventInitialTransition3, event.detail.load));
    }
  }
  connectedCallback() {
    if (matchesMediaQuery("motion-safe") && this.hasAttribute("reveal-on-scroll")) {
      inView17(this, __privateMethod(this, _MultipleMediaWithText_instances, onBecameVisible_fn3).bind(this), { margin: "-10% 0px" });
    }
  }
};
_preventInitialTransition3 = new WeakMap();
_MultipleMediaWithText_instances = new WeakSet();
onBecameVisible_fn3 = function() {
  // Ensure reveal always lands visible. Partial Motion timelines previously left
  // media/content at opacity:0, which painted a blank spacer above homepage videos.
  const mediaElements = Array.from(this.querySelectorAll(".multiple-media-with-text__media-wrapper > *"));
  const content = this.querySelector(".multiple-media-with-text__content-wrapper");
  const finalizeReveal = () => {
    this.classList.add("is-visible");
    this.style.opacity = "1";
    mediaElements.forEach((media) => {
      media.style.opacity = "1";
      media.style.visibility = "visible";
    });
    if (content) {
      content.style.opacity = "1";
      content.style.visibility = "visible";
      content.style.transform = "none";
    }
  };
  const timelineSequence = timeline12([
    [this, { opacity: 1 }, { duration: 0 }],
    "media",
    ...mediaElements.map((media) => {
      const rotateTo = media.style.getPropertyValue("--media-rotate") || "0deg";
      return [media, { opacity: [0, 1], transform: ["rotate(0deg)", `rotate(${rotateTo})`] }, { duration: 0.5, at: "media" }];
    }),
    [content, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.5 }]
  ]);
  if (timelineSequence && typeof timelineSequence.finished?.then === "function") {
    timelineSequence.finished.then(finalizeReveal).catch(finalizeReveal);
  } else {
    finalizeReveal();
  }
  if (__privateGet(this, _preventInitialTransition3)) {
    timelineSequence.finish();
    finalizeReveal();
  }
};
if (!window.customElements.get("multiple-media-with-text")) {
  window.customElements.define("multiple-media-with-text", MultipleMediaWithText);
}

// js/sections/newsletter-popup.js
var NewsletterPopup = class extends PopIn {
  connectedCallback() {
    super.connectedCallback();
    if (this.shouldAppearAutomatically) {
      setTimeout(() => this.show(), this.apparitionDelay);
    }
  }
  get apparitionDelay() {
    return parseInt(this.getAttribute("apparition-delay") || 0) * 1e3;
  }
  get shouldAppearAutomatically() {
    return !(localStorage.getItem("theme:popup-filled") === "true" || // Never open if popup has been filled successfully
    this.hasAttribute("only-once") && localStorage.getItem("theme:popup-appeared") === "true");
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === "open" && this.open) {
      localStorage.setItem("theme:popup-appeared", "true");
    }
  }
};
if (!window.customElements.get("newsletter-popup")) {
  window.customElements.define("newsletter-popup", NewsletterPopup);
}

// js/sections/privacy-banner.js
import { Delegate as Delegate9 } from "vendor";
var _delegate7, _PrivacyBanner_instances, onConsentLibraryLoaded_fn, acceptPolicy_fn, declinePolicy_fn;
var PrivacyBanner = class extends PopIn {
  constructor() {
    super();
    __privateAdd(this, _PrivacyBanner_instances);
    __privateAdd(this, _delegate7, new Delegate9(this));
    window.Shopify.loadFeatures([
      {
        name: "consent-tracking-api",
        version: "0.1",
        onLoad: __privateMethod(this, _PrivacyBanner_instances, onConsentLibraryLoaded_fn).bind(this)
      }
    ]);
  }
  connectedCallback() {
    super.connectedCallback();
    __privateGet(this, _delegate7).on("click", '[data-action="accept"]', __privateMethod(this, _PrivacyBanner_instances, acceptPolicy_fn).bind(this));
    __privateGet(this, _delegate7).on("click", '[data-action="decline"]', __privateMethod(this, _PrivacyBanner_instances, declinePolicy_fn).bind(this));
  }
  disconnectedCallback() {
    __privateGet(this, _delegate7).off();
  }
};
_delegate7 = new WeakMap();
_PrivacyBanner_instances = new WeakSet();
onConsentLibraryLoaded_fn = function() {
  if (window.Shopify.customerPrivacy?.shouldShowBanner()) {
    this.show();
  }
};
acceptPolicy_fn = function() {
  window.Shopify.customerPrivacy?.setTrackingConsent(true, this.hide.bind(this));
};
declinePolicy_fn = function() {
  window.Shopify.customerPrivacy?.setTrackingConsent(false, this.hide.bind(this));
};
if (!window.customElements.get("privacy-banner")) {
  window.customElements.define("privacy-banner", PrivacyBanner);
}

// js/sections/product.js
var _intersectionObserver3, _formElement, _footerElement, _latestFooterCondition, _latestFormCondition, _ProductStickyBar_instances, onFormVisibilityChange_fn;
var ProductStickyBar = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductStickyBar_instances);
    __privateAdd(this, _intersectionObserver3, new IntersectionObserver(__privateMethod(this, _ProductStickyBar_instances, onFormVisibilityChange_fn).bind(this)));
    __privateAdd(this, _formElement);
    __privateAdd(this, _footerElement);
    __privateAdd(this, _latestFooterCondition, false);
    __privateAdd(this, _latestFormCondition, false);
  }
  connectedCallback() {
    __privateSet(this, _formElement, document.forms[this.getAttribute("form")]);
    __privateSet(this, _footerElement, document.querySelector(".shopify-section--footer"));
    if (__privateGet(this, _formElement)) {
      __privateGet(this, _intersectionObserver3).observe(__privateGet(this, _formElement));
      __privateGet(this, _intersectionObserver3).observe(__privateGet(this, _footerElement));
    }
  }
  disconnectedCallback() {
    __privateGet(this, _intersectionObserver3).disconnect();
  }
};
_intersectionObserver3 = new WeakMap();
_formElement = new WeakMap();
_footerElement = new WeakMap();
_latestFooterCondition = new WeakMap();
_latestFormCondition = new WeakMap();
_ProductStickyBar_instances = new WeakSet();
onFormVisibilityChange_fn = function(entries) {
  const [formEntry, footerEntry] = [entries.find((entry) => entry.target === __privateGet(this, _formElement)), entries.find((entry) => entry.target === __privateGet(this, _footerElement))];
  if (formEntry) {
    __privateSet(this, _latestFormCondition, !formEntry.isIntersecting && formEntry.boundingClientRect.bottom < 0);
  }
  if (footerEntry) {
    __privateSet(this, _latestFooterCondition, !footerEntry.isIntersecting);
  }
  this.classList.toggle("is-visible", __privateGet(this, _latestFooterCondition) && __privateGet(this, _latestFormCondition));
};
if (!window.customElements.get("product-sticky-bar")) {
  window.customElements.define("product-sticky-bar", ProductStickyBar);
}

// js/sections/product-recommendations.js
var _isLoaded, _ProductRecommendations_instances, loadRecommendations_fn;
var ProductRecommendations = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductRecommendations_instances);
    __privateAdd(this, _isLoaded, false);
  }
  connectedCallback() {
    // Defer Section Rendering fetch until near viewport (below-fold PDP/home widgets).
    // Guards with _isLoaded prevent double-fetch if observer + designMode both fire.
    if (!("IntersectionObserver" in window) || Shopify.designMode) {
      __privateMethod(this, _ProductRecommendations_instances, loadRecommendations_fn).call(this);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        __privateMethod(this, _ProductRecommendations_instances, loadRecommendations_fn).call(this);
      },
      { rootMargin: "480px 0px" }
    );
    observer.observe(this);
  }
};
_isLoaded = new WeakMap();
_ProductRecommendations_instances = new WeakSet();
loadRecommendations_fn = async function() {
  if (__privateGet(this, _isLoaded)) {
    return;
  }
  __privateSet(this, _isLoaded, true);
  const section = this.closest(".shopify-section"), intent = this.getAttribute("intent") || "related", url = `${Shopify.routes.root}recommendations/products?product_id=${this.getAttribute("product")}&limit=${this.getAttribute("limit") || 4}&section_id=${extractSectionId(section)}&intent=${intent}`, response = await fetch(url, { priority: intent === "related" ? "low" : "auto" });
  const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), productRecommendationsElement = tempDiv.querySelector("product-recommendations");
  if (productRecommendationsElement.childElementCount > 0) {
    this.replaceChildren(...document.importNode(productRecommendationsElement, true).childNodes);
    this.hidden = false;
  } else {
    this.remove();
  }
};
if (!window.customElements.get("product-recommendations")) {
  window.customElements.define("product-recommendations", ProductRecommendations);
}

// js/sections/quick-order-list.js
var _abortController12, _updateSequence, _QuickOrderList_instances, onQuantityChange_fn, onUpdate_fn, onQuantityUpdated_fn;
var QuickOrderList = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _QuickOrderList_instances);
    __privateAdd(this, _abortController12);
    __privateAdd(this, _updateSequence, 0);
    this.addEventListener("quick-order-list:update", __privateMethod(this, _QuickOrderList_instances, onUpdate_fn).bind(this));
    this.addEventListener("change", __privateMethod(this, _QuickOrderList_instances, onQuantityChange_fn).bind(this));
  }
};
_abortController12 = new WeakMap();
_updateSequence = new WeakMap();
_QuickOrderList_instances = new WeakSet();
onQuantityChange_fn = async function() {
  __privateGet(this, _abortController12)?.abort();
};
onUpdate_fn = async function(event) {
  const sequence = __privateGet(this, _updateSequence) + 1;
  __privateSet(this, _updateSequence, sequence);
  let sectionsToBundle = [extractSectionId(this)];
  document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
  __privateSet(this, _abortController12, new AbortController());
  const inputWrapper = event.target.closest(".quick-order-list__quantity-actions")?.querySelector(".quantity-selector__input-wrapper");
  inputWrapper?.setAttribute("aria-busy", "true");
  try {
    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: "POST",
      signal: __privateGet(this, _abortController12).signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        updates: event.detail.updates,
        sections: sectionsToBundle.join(",")
      })
    });
    if (sequence !== __privateGet(this, _updateSequence)) return;
    inputWrapper?.removeAttribute("aria-busy");
    if (response.ok) {
      setTimeout(() => {
        if (sequence !== __privateGet(this, _updateSequence)) return;
        __privateMethod(this, _QuickOrderList_instances, onQuantityUpdated_fn).call(this, response, sequence);
      }, 250);
    } else {
      const { message } = await response.json();
      event.target.dispatchEvent(new CustomEvent("cart:error", { bubbles: true, detail: { message } }));
    }
  } catch (error) {
    if (sequence === __privateGet(this, _updateSequence)) {
      inputWrapper?.removeAttribute("aria-busy");
    }
  }
};
onQuantityUpdated_fn = async function(response, sequence) {
  if (sequence !== __privateGet(this, _updateSequence)) return;
  const cartContent = await response.json(), sectionId = extractSectionId(this);
  if (sequence !== __privateGet(this, _updateSequence)) return;
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:change", {
      bubbles: true,
      detail: {
        baseEvent: "quick-order-list:add",
        cart: cartContent
      }
    })
  );
  this.closest(".shopify-section").outerHTML = cartContent["sections"][sectionId];
};
var _QuickOrderListQuantitySelector_instances, onQuantityChange_fn2, onCartError_fn2;
var QuickOrderListQuantitySelector = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _QuickOrderListQuantitySelector_instances);
    this.addEventListener("change", debounce(__privateMethod(this, _QuickOrderListQuantitySelector_instances, onQuantityChange_fn2).bind(this), 300));
    this.addEventListener("cart:error", __privateMethod(this, _QuickOrderListQuantitySelector_instances, onCartError_fn2).bind(this));
  }
};
_QuickOrderListQuantitySelector_instances = new WeakSet();
onQuantityChange_fn2 = function(event) {
  this.dispatchEvent(
    new CustomEvent("quick-order-list:update", {
      bubbles: true,
      detail: {
        updates: { [this.getAttribute("variant-id")]: parseInt(event.target.value) }
      }
    })
  );
};
onCartError_fn2 = function(event) {
  const errorSvg = `<svg width="13" height="13" fill="none" viewBox="0 0 13 13">
        <circle cx="6.5" cy="6.5" r="6.5" fill="#BF1515"/>
        <path fill="#fff" d="M6.75 7.97a.387.387 0 0 1-.3-.12.606.606 0 0 1-.12-.34l-.3-3.82c-.02-.247.033-.443.16-.59.127-.153.313-.23.56-.23.24 0 .42.077.54.23.127.147.18.343.16.59l-.3 3.82a.522.522 0 0 1-.12.34.344.344 0 0 1-.28.12Zm0 2.08a.744.744 0 0 1-.55-.21.751.751 0 0 1-.2-.54c0-.213.067-.387.2-.52.14-.14.323-.21.55-.21.233 0 .413.07.54.21.133.133.2.307.2.52 0 .22-.067.4-.2.54-.127.14-.307.21-.54.21Z"/>
      </svg>`;
  this.insertAdjacentHTML("afterend", `<p class="h-stack gap-2 justify-center text-xs" role="alert">${errorSvg} ${event.detail.message}</p>`);
  this.querySelector("quantity-selector")?.restoreDefaultValue();
};
var _QuickOrderListRemoveVariant_instances, onClick_fn;
var QuickOrderListRemoveVariant = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _QuickOrderListRemoveVariant_instances);
    this.addEventListener("click", __privateMethod(this, _QuickOrderListRemoveVariant_instances, onClick_fn));
  }
};
_QuickOrderListRemoveVariant_instances = new WeakSet();
onClick_fn = function() {
  this.dispatchEvent(
    new CustomEvent("quick-order-list:update", {
      bubbles: true,
      detail: {
        updates: { [this.getAttribute("variant-id")]: 0 }
      }
    })
  );
};
var _QuickOrderListRemoveAll_instances, onClick_fn2;
var QuickOrderListRemoveAll = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _QuickOrderListRemoveAll_instances);
    this.addEventListener("click", __privateMethod(this, _QuickOrderListRemoveAll_instances, onClick_fn2));
  }
};
_QuickOrderListRemoveAll_instances = new WeakSet();
onClick_fn2 = function() {
  const updates = JSON.parse(this.getAttribute("variant-ids")).reduce((acc, variantId) => {
    acc[variantId] = 0;
    return acc;
  }, {});
  this.dispatchEvent(
    new CustomEvent("quick-order-list:update", {
      bubbles: true,
      detail: {
        updates
      }
    })
  );
};
if (!window.customElements.get("quick-order-list")) {
  window.customElements.define("quick-order-list", QuickOrderList);
}
if (!window.customElements.get("quick-order-list-quantity-selector")) {
  window.customElements.define("quick-order-list-quantity-selector", QuickOrderListQuantitySelector);
}
if (!window.customElements.get("quick-order-list-remove-variant")) {
  window.customElements.define("quick-order-list-remove-variant", QuickOrderListRemoveVariant);
}
if (!window.customElements.get("quick-order-list-remove-all")) {
  window.customElements.define("quick-order-list-remove-all", QuickOrderListRemoveAll);
}

// js/sections/recently-viewed-products.js
var _isLoaded2, _RecentlyViewedProducts_instances, searchQueryString_get, loadProducts_fn;
var RecentlyViewedProducts = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _RecentlyViewedProducts_instances);
    __privateAdd(this, _isLoaded2, false);
  }
  connectedCallback() {
    // Idle-path secondary widget: fetch via Section Rendering API only when near viewport.
    if (!("IntersectionObserver" in window) || Shopify.designMode) {
      __privateMethod(this, _RecentlyViewedProducts_instances, loadProducts_fn).call(this);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        __privateMethod(this, _RecentlyViewedProducts_instances, loadProducts_fn).call(this);
      },
      { rootMargin: "480px 0px" }
    );
    observer.observe(this);
  }
};
_isLoaded2 = new WeakMap();
_RecentlyViewedProducts_instances = new WeakSet();
searchQueryString_get = function() {
  let storedItems = [];
  try {
    const parsedItems = JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]");
    if (Array.isArray(parsedItems)) storedItems = parsedItems.slice(0, 12);
  } catch (error) {
  }
  const items = new Set(storedItems);
  if (this.hasAttribute("exclude-id")) {
    items.delete(parseInt(this.getAttribute("exclude-id")));
  }
  return Array.from(items.values(), (item) => `id:${item}`).slice(0, parseInt(this.getAttribute("products-count"))).join(" OR ");
};
loadProducts_fn = async function() {
  if (__privateGet(this, _isLoaded2)) {
    return;
  }
  __privateSet(this, _isLoaded2, true);
  const section = this.closest(".shopify-section"), url = `${Shopify.routes.root}search?type=product&q=${__privateGet(this, _RecentlyViewedProducts_instances, searchQueryString_get)}&section_id=${extractSectionId(section)}`, response = await fetch(url, { priority: "low" });
  const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), recentlyViewedProductsElement = tempDiv.querySelector("recently-viewed-products");
  if (recentlyViewedProductsElement.childElementCount > 0) {
    this.replaceChildren(...document.importNode(recentlyViewedProductsElement, true).childNodes);
  } else {
    section.remove();
  }
};
if (!window.customElements.get("recently-viewed-products")) {
  window.customElements.define("recently-viewed-products", RecentlyViewedProducts);
}

// js/sections/shop-the-look.js
import { animate as animate23, timeline as timeline13 } from "vendor";
var _controlledPopover, _selectedHotSpot, _ShopTheLookMobileCarousel_instances, setInitialPosition_fn, onSpotSelected_fn, onUpdateHotSpotPosition_fn, onLookChanged_fn, changeLookFocalPoint_fn, restorePosition_fn;
var ShopTheLookMobileCarousel = class extends ScrollCarousel {
  constructor() {
    super();
    __privateAdd(this, _ShopTheLookMobileCarousel_instances);
    __privateAdd(this, _controlledPopover);
    __privateAdd(this, _selectedHotSpot);
    this.addEventListener("carousel:change", __privateMethod(this, _ShopTheLookMobileCarousel_instances, onLookChanged_fn));
    Array.from(this.querySelectorAll(".shop-the-look__hot-spot-list")).forEach((list) => {
      list.carousel.addEventListener("carousel:select", __privateMethod(this, _ShopTheLookMobileCarousel_instances, onSpotSelected_fn).bind(this));
      list.carousel.addEventListener("carousel:change", () => __privateMethod(this, _ShopTheLookMobileCarousel_instances, onUpdateHotSpotPosition_fn).call(this, list));
    });
    Array.from(this.querySelectorAll(".shop-the-look__popover")).forEach((popover) => {
      popover.addEventListener("dialog:before-show", __privateMethod(this, _ShopTheLookMobileCarousel_instances, changeLookFocalPoint_fn).bind(this));
      popover.addEventListener("dialog:before-hide", __privateMethod(this, _ShopTheLookMobileCarousel_instances, restorePosition_fn).bind(this));
    });
  }
  connectedCallback() {
    super.connectedCallback();
    __privateMethod(this, _ShopTheLookMobileCarousel_instances, setInitialPosition_fn).call(this);
  }
  get isExpanded() {
    return this.classList.contains("is-expanded");
  }
};
_controlledPopover = new WeakMap();
_selectedHotSpot = new WeakMap();
_ShopTheLookMobileCarousel_instances = new WeakSet();
setInitialPosition_fn = function() {
  __privateSet(this, _selectedHotSpot, this.selectedCell.querySelector('.shop-the-look__hot-spot[aria-current="true"]'));
  __privateMethod(this, _ShopTheLookMobileCarousel_instances, onLookChanged_fn).call(this);
};
onSpotSelected_fn = function() {
  if (!this.isExpanded) {
    document.getElementById(this.selectedCell.getAttribute("data-popover-id")).show();
  }
};
onUpdateHotSpotPosition_fn = function(list) {
  __privateSet(this, _selectedHotSpot, list.querySelector('.shop-the-look__hot-spot[aria-current="true"]'));
  if (this.isExpanded) {
    __privateMethod(this, _ShopTheLookMobileCarousel_instances, changeLookFocalPoint_fn).call(this);
  }
};
onLookChanged_fn = function() {
  const popoverId = this.selectedCell.getAttribute("data-popover-id");
  __privateSet(this, _controlledPopover, document.getElementById(popoverId));
  this.nextElementSibling.setAttribute("aria-controls", popoverId);
};
changeLookFocalPoint_fn = function() {
  const scale = window.innerWidth / this.selectedCell.clientWidth, remainingSpace = window.innerHeight - __privateGet(this, _controlledPopover).shadowRoot.querySelector('[part="base"]').clientHeight, imageHeightAfterScale = Math.round(this.selectedCell.querySelector(".shop-the-look__image-wrapper").clientHeight * scale), outsideViewportImageHeight = Math.max(imageHeightAfterScale - remainingSpace, 0), insideViewportImageHeight = imageHeightAfterScale - outsideViewportImageHeight, hotSpotFocalPoint = Math.round((__privateGet(this, _selectedHotSpot).offsetTop + __privateGet(this, _selectedHotSpot).clientHeight / 2) * scale), offsetToMove = Math.round(hotSpotFocalPoint - insideViewportImageHeight / 2), minTranslateY = Math.round(-(this.parentElement.getBoundingClientRect().top - (imageHeightAfterScale - this.selectedCell.offsetHeight) / 2)), maxTranslateY = Math.round(minTranslateY - outsideViewportImageHeight), translateY = Math.min(Math.max(minTranslateY - offsetToMove, maxTranslateY), minTranslateY);
  if (!this.isExpanded) {
    animate23(this, { transform: ["translateY(0) scale(1)", `translateY(${translateY}px) scale(${scale})`] }, { duration: 0.4, easing: [0.645, 0.045, 0.355, 1] });
    document.documentElement.style.setProperty("--hide-header-group", "1");
  } else {
    animate23(this, { transform: `translateY(${translateY}px) scale(${scale})` }, { duration: 0.4, easing: "ease-in-out" });
  }
  this.classList.add("is-expanded");
};
restorePosition_fn = function() {
  animate23(this, { transform: "translateY(0) scale(1)" }, { duration: 0.4, easing: [0.645, 0.045, 0.355, 1] }).finished.then(() => {
    this.style.transform = null;
  });
  this.classList.remove("is-expanded");
  document.documentElement.style.removeProperty("--hide-header-group");
};
var _ShopTheLookProductListCarousel_instances, updateButtonLink_fn;
var ShopTheLookProductListCarousel = class extends EffectCarousel {
  constructor() {
    super();
    __privateAdd(this, _ShopTheLookProductListCarousel_instances);
    this.addEventListener("carousel:change", __privateMethod(this, _ShopTheLookProductListCarousel_instances, updateButtonLink_fn).bind(this));
  }
};
_ShopTheLookProductListCarousel_instances = new WeakSet();
updateButtonLink_fn = function(event) {
  const productCard = event.detail.cell.querySelector(".product-card");
  if (productCard.hasAttribute("handle")) {
    this.nextElementSibling.href = `${Shopify.routes.root}products/${productCard.getAttribute("handle")}`;
  }
};
var ShopTheLookDesktopCarousel = class extends EffectCarousel {
  createOnBecameVisibleAnimationControls(toSlide) {
    return animate23(toSlide.querySelectorAll(".shop-the-look__item-content"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.5 });
  }
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return timeline13([
      [fromSlide.querySelectorAll(".shop-the-look__item-content"), { opacity: [1, 0] }, { duration: 0.3 }],
      [
        fromSlide.querySelectorAll(".shop-the-look__image-wrapper > *"),
        { opacity: [1, 0], transform: ["translateX(0)", "translateX(-15px)"] },
        { duration: 0.5, at: "<", easing: [0.645, 0.045, 0.355, 1] }
      ],
      [toSlide.querySelectorAll(".shop-the-look__image-wrapper > *"), { opacity: [0, 1], transform: ["translateX(-15px)", "translateX(0)"] }, { duration: 0.5, at: "<" }],
      [toSlide.querySelectorAll(".shop-the-look__item-content"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.5, at: "-0.1" }]
    ]);
  }
};
var ShopTheLookPopover = class extends Popover {
  hideForOutsideClickTarget(target) {
    return false;
  }
  allowOutsideClickForTarget(target) {
    return target.classList.contains("shop-the-look__hot-spot");
  }
};
if (!window.customElements.get("shop-the-look-mobile-carousel")) {
  window.customElements.define("shop-the-look-mobile-carousel", ShopTheLookMobileCarousel);
}
if (!window.customElements.get("shop-the-look-product-list-carousel")) {
  window.customElements.define("shop-the-look-product-list-carousel", ShopTheLookProductListCarousel);
}
if (!window.customElements.get("shop-the-look-desktop-carousel")) {
  window.customElements.define("shop-the-look-desktop-carousel", ShopTheLookDesktopCarousel);
}
if (!window.customElements.get("shop-the-look-popover")) {
  window.customElements.define("shop-the-look-popover", ShopTheLookPopover);
}

// js/sections/slideshow.js
import { timeline as timeline14, Delegate as Delegate10 } from "vendor";
var _delegate8, _onVideoEndedListener, _SlideshowCarousel_instances, autoplayPauseOnVideo_get, getSlideEnteringSequence_fn, getSlideLeavingSequence_fn, muteVideo_fn, unmuteVideo_fn, onVolumeChange_fn, onSlideSettle_fn, onVideoEnded_fn, onNextButtonClicked_fn, handleAutoplayProgress_fn;
var SlideshowCarousel = class extends EffectCarousel {
  constructor() {
    super();
    __privateAdd(this, _SlideshowCarousel_instances);
    __privateAdd(this, _delegate8, new Delegate10(this));
    __privateAdd(this, _onVideoEndedListener, __privateMethod(this, _SlideshowCarousel_instances, onVideoEnded_fn).bind(this));
    __privateGet(this, _delegate8).on("click", '[data-action="navigate-next"]', __privateMethod(this, _SlideshowCarousel_instances, onNextButtonClicked_fn).bind(this));
    __privateGet(this, _delegate8).on("click", '[data-action="unmute"]', __privateMethod(this, _SlideshowCarousel_instances, unmuteVideo_fn).bind(this));
    __privateGet(this, _delegate8).on("click", '[data-action="mute"]', __privateMethod(this, _SlideshowCarousel_instances, muteVideo_fn).bind(this));
    this.addEventListener("volumechange", __privateMethod(this, _SlideshowCarousel_instances, onVolumeChange_fn), { capture: true });
    this.addEventListener("carousel:settle", __privateMethod(this, _SlideshowCarousel_instances, onSlideSettle_fn));
    if (this.classList.contains("jw-hero-fade") && window.matchMedia("(prefers-reduced-motion: reduce)").matches && this.player) {
      this.player.stop();
    }
    if (this.hasAttribute("autoplay") && this.player) {
      this.player.addEventListener("player:start", __privateMethod(this, _SlideshowCarousel_instances, handleAutoplayProgress_fn).bind(this));
      this.player.addEventListener("player:stop", __privateMethod(this, _SlideshowCarousel_instances, handleAutoplayProgress_fn).bind(this));
      this.player.addEventListener("player:visibility-pause", __privateMethod(this, _SlideshowCarousel_instances, handleAutoplayProgress_fn).bind(this));
      this.player.addEventListener("player:visibility-resume", __privateMethod(this, _SlideshowCarousel_instances, handleAutoplayProgress_fn).bind(this));
    }
  }
  disconnectedCallback() {
    __privateGet(this, _delegate8).destroy();
  }
  /**
   * Check when the media is fully loaded so that we can initiate the switch
   */
  async createOnBecameVisibleAnimationControls(toSlide) {
    if (toSlide.getAttribute("media-type") === "image") {
      await imageLoaded(toSlide.querySelectorAll("img"));
    } else {
      await videoLoaded(toSlide.querySelectorAll("video"));
    }
    if (toSlide.hasAttribute("reveal-on-scroll")) {
      return timeline14([...__privateMethod(this, _SlideshowCarousel_instances, getSlideEnteringSequence_fn).call(this, toSlide)]);
    }
    return { finished: Promise.resolve() };
  }
  /**
   * Create the animation when it changes from one slide to another, by making sure to properly handling the videos
   */
  createOnChangeAnimationControls(fromSlide, toSlide, { direction } = {}) {
    if (this.classList.contains("jw-hero-fade")) {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const duration = reduced ? 0 : 1;
      toSlide.classList.add("is-selected");
      fromSlide.style.zIndex = "1";
      fromSlide.style.opacity = "1";
      toSlide.style.zIndex = "2";
      toSlide.style.opacity = "0";
      const controls = timeline14([
        [toSlide, { opacity: [0, 1] }, { duration, easing: "linear" }]
      ]);
      controls.finished.then(() => {
        fromSlide.style.opacity = "";
        fromSlide.style.zIndex = "";
        toSlide.style.zIndex = "";
      });
      return controls;
    }
    const fromVideo = Array.from(fromSlide.querySelectorAll("video")), toVideo = Array.from(toSlide.querySelectorAll("video")).filter((video) => video.offsetParent);
    fromSlide.removeEventListener("ended", __privateGet(this, _onVideoEndedListener), { capture: true });
    fromVideo.forEach((video) => {
      video.muted = true;
      video.pause();
    });
    toVideo.forEach((video) => {
      video.muted = true;
      video.currentTime = 0;
      video.play();
    });
    return {
      leaveControls: () => timeline14(__privateMethod(this, _SlideshowCarousel_instances, getSlideLeavingSequence_fn).call(this, fromSlide)),
      enterControls: () => timeline14(__privateMethod(this, _SlideshowCarousel_instances, getSlideEnteringSequence_fn).call(this, toSlide))
    };
  }
};
_delegate8 = new WeakMap();
_onVideoEndedListener = new WeakMap();
_SlideshowCarousel_instances = new WeakSet();
autoplayPauseOnVideo_get = function() {
  return this.hasAttribute("autoplay-pause-on-video");
};
/**
 * Generate the part of the sequence when the given slide is entering
 */
getSlideEnteringSequence_fn = function(slide) {
  const slideContent = slide.querySelector(".slideshow__slide-content");
  if (slideContent.classList.contains("slideshow__slide-content--boxed")) {
    return [
      [slide, { opacity: [0, 1] }, { duration: 0.8, easing: [0.25, 0.46, 0.45, 0.94] }],
      [
        slide.querySelectorAll(".content-over-media > :is(video-media, svg), .content-over-media > picture img"),
        { opacity: [0, 1], transform: ["scale(1.2)", "scale(1)"] },
        { duration: 0.8, at: "<", easing: [0.25, 0.46, 0.45, 0.94] }
      ],
      [slideContent, { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] }, { duration: 0.6, at: "-0.4", easing: [0.215, 0.61, 0.355, 1] }]
    ];
  } else {
    return [
      [slide, { opacity: [0, 1] }, { duration: 0.8, easing: [0.25, 0.46, 0.45, 0.94] }],
      [
        slide.querySelectorAll(".content-over-media > :is(video-media, svg), .content-over-media > picture img"),
        { opacity: [0, 1], transform: ["scale(1.2)", "scale(1)"] },
        { duration: 0.8, at: "<", easing: [0.25, 0.46, 0.45, 0.94] }
      ],
      [
        slideContent.querySelector(".prose"),
        { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] },
        { duration: 0.6, at: "-0.4", easing: [0.215, 0.61, 0.355, 1] }
      ],
      [
        slideContent.querySelector(".button-group"),
        { opacity: [0, 1], transform: ["translateY(20px)", "translateY(0)"] },
        { duration: 0.6, at: "-0.4", easing: [0.215, 0.61, 0.355, 1] }
      ]
    ];
  }
};
/**
 * Generate the part of the sequence when the given slide is leaving
 */
getSlideLeavingSequence_fn = function(slide) {
  const slideContent = slide.querySelector(".slideshow__slide-content");
  if (slideContent.classList.contains("slideshow__slide-content--boxed")) {
    return [
      [slideContent, { opacity: [1, 0], transform: ["translateY(0)", "translateY(20px)"] }, { duration: 0.25, at: "leaving", easing: [0.55, 0.055, 0.675, 0.19] }],
      [
        slide.querySelectorAll(".content-over-media > :is(video-media, svg), .content-over-media > picture img"),
        { opacity: [1, 0] },
        { duration: 0.2, at: "-0.1", easing: [0.55, 0.055, 0.675, 0.19] }
      ]
    ];
  } else {
    return [
      [
        slideContent.querySelector(".prose"),
        { opacity: [1, 0], transform: ["translateY(0)", "translateY(10px)"] },
        { duration: 0.25, at: "leaving", easing: [0.55, 0.055, 0.675, 0.19] }
      ],
      [
        slideContent.querySelector(".button-group"),
        { opacity: [1, 0], transform: ["translateY(0)", "translateY(20px)"] },
        { duration: 0.25, at: "<", easing: [0.55, 0.055, 0.675, 0.19] }
      ],
      [
        slide.querySelectorAll(".content-over-media > :is(video-media, svg), .content-over-media > picture img"),
        { opacity: [1, 0] },
        { duration: 0.2, at: "-0.1", easing: [0.55, 0.055, 0.675, 0.19] }
      ]
    ];
  }
};
/**
 * Mute the video for the active slide, by ignoring the video that may not be visible
 */
muteVideo_fn = function(event) {
  event.preventDefault();
  Array.from(this.selectedCell.querySelectorAll("video")).filter((video) => video.offsetParent).forEach((video) => video.muted = true);
};
/**
 * Unmute the video for the active slide, by ignoring the video that may not be visible
 */
unmuteVideo_fn = function(event) {
  event.preventDefault();
  Array.from(this.selectedCell.querySelectorAll("video")).filter((video) => video.offsetParent).forEach((video) => video.muted = false);
};
/**
 * Update the volume controls button (if any) based on whether the video is mute or not
 */
onVolumeChange_fn = function(event) {
  const volumeControl = event.target.closest(".slideshow__slide").querySelector(".slideshow__volume-control");
  if (volumeControl) {
    volumeControl.querySelector('[data-action="unmute"]').hidden = !event.target.muted;
    volumeControl.querySelector('[data-action="mute"]').hidden = event.target.muted;
  }
};
/**
 * Do action when the slide settles
 */
onSlideSettle_fn = function(event) {
  const videoList = Array.from(event.detail.cell.querySelectorAll("video"));
  if (__privateGet(this, _SlideshowCarousel_instances, autoplayPauseOnVideo_get) && this.cells.length > 1 && videoList.length > 0) {
    this.player?.pause();
    event.detail.cell.addEventListener("ended", __privateGet(this, _onVideoEndedListener), { capture: true, once: true });
  }
};
/**
 * If the merchant decide to autorotate but to stop autoplay when video is playing, we register a listener that
 * will move to the next slide (if any)
 */
onVideoEnded_fn = function() {
  this.next();
};
/**
 * Scroll to the next section when the button is clicked, by leveraging JS native scroll
 */
onNextButtonClicked_fn = function() {
  this.closest(".shopify-section").nextElementSibling?.scrollIntoView({ block: "start", behavior: "smooth" });
};
handleAutoplayProgress_fn = async function(event) {
  switch (event.type) {
    case "player:start":
      let autoplayDuration = this.getAttribute("autoplay");
      if (__privateGet(this, _SlideshowCarousel_instances, autoplayPauseOnVideo_get) && this.selectedCell.getAttribute("media-type") === "video") {
        const video = Array.from(this.selectedCell.querySelectorAll("video")).filter((video2) => video2.offsetParent).pop();
        if (isNaN(video.duration)) {
          await new Promise((resolve) => {
            video.onloadedmetadata = () => resolve();
          });
        }
        autoplayDuration = video.duration;
      }
      this.style.setProperty("--slideshow-progress-duration", `${autoplayDuration}s`);
      this.style.setProperty("--slideshow-progress-play-state", "running");
      break;
    case "player:stop":
      this.style.setProperty("--slideshow-progress-duration", `0s`);
      this.style.setProperty("--slideshow-progress-play-state", "paused");
      break;
    case "player:visibility-pause":
      this.style.setProperty("--slideshow-progress-play-state", "paused");
      break;
    case "player:visibility-resume":
      this.style.setProperty("--slideshow-progress-play-state", "running");
      break;
  }
};
if (!window.customElements.get("slideshow-carousel")) {
  window.customElements.define("slideshow-carousel", SlideshowCarousel);
}

// js/sections/testimonials.js
import { animate as animate24 } from "vendor";
var TestimonialCarousel = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide, { direction }) {
    return {
      leaveControls: () => animate24(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-15px)"] }, { duration: 0.4, easing: [0.55, 0.055, 0.675, 0.19] }),
      enterControls: () => animate24(toSlide, { opacity: [0, 1], transform: ["translateY(15px)", "translateY(0)"] }, { duration: 0.4, delay: 0, easing: [0.25, 0.46, 0.45, 0.94] })
    };
  }
};
if (!window.customElements.get("testimonial-carousel")) {
  window.customElements.define("testimonial-carousel", TestimonialCarousel);
}

// js/sections/text-with-icons.js
import { animate as animate25 } from "vendor";
var TextWithIconsCarousel = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return {
      leaveControls: () => animate25(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-10px)"] }, { duration: 0.3, easing: "ease-in" }),
      enterControls: () => animate25(toSlide, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0px)"] }, { duration: 0.3, delay: 0.2, easing: "ease-out" })
    };
  }
};
if (!window.customElements.get("text-with-icons-carousel")) {
  window.customElements.define("text-with-icons-carousel", TextWithIconsCarousel);
}

// js/sections/timeline.js
import { animate as animate26, timeline as timeline15 } from "vendor";
var TimelineCarousel = class extends EffectCarousel {
  createOnBecameVisibleAnimationControls(toSlide) {
    return animate26(toSlide.querySelectorAll(".timeline__item-content"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.5 });
  }
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return timeline15([
      [fromSlide.querySelectorAll(".timeline__item-content"), { opacity: [1, 0] }, { duration: 0.3 }],
      [
        fromSlide.querySelector(".timeline__item-image-wrapper :is(img, svg)"),
        { opacity: [1, 0], transform: ["translateX(0)", "translateX(-15px)"] },
        { duration: 0.5, at: "<", easing: [0.645, 0.045, 0.355, 1] }
      ],
      [toSlide.querySelector(".timeline__item-image-wrapper :is(img, svg)"), { opacity: [0, 1], transform: ["translateX(-15px)", "translateX(0)"] }, { duration: 0.5, at: "<" }],
      [toSlide.querySelectorAll(".timeline__item-content"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.5, at: "-0.1" }]
    ]);
  }
};
if (!window.customElements.get("timeline-carousel")) {
  window.customElements.define("timeline-carousel", TimelineCarousel);
}

// js/theme.js
import { Delegate as Delegate11 } from "vendor";
(() => {
  const delegateDocument = new Delegate11(document.documentElement);
  delegateDocument.on("click", 'a[href*="#"]', (event, target) => {
    if (event.defaultPrevented || target.matches("[allow-hash-change]") || target.pathname !== window.location.pathname || target.search !== window.location.search) {
      return;
    }
    const url = new URL(target.href);
    if (url.hash === "") {
      return;
    }
    const anchorElement = document.querySelector(url.hash);
    if (anchorElement) {
      event.preventDefault();
      anchorElement.scrollIntoView({ block: "start", behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto" });
      document.documentElement.dispatchEvent(new CustomEvent("hashchange:simulate", { bubbles: true, detail: { hash: url.hash } }));
    }
  });
  if (navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)) {
    document.head.querySelector('meta[name="viewport"]').content = "width=device-width, initial-scale=1.0, height=device-height, minimum-scale=1.0, maximum-scale=1.0";
  }
  Array.from(document.querySelectorAll(".prose table")).forEach((table) => {
    table.outerHTML = '<div class="table-scroller">' + table.outerHTML + "</div>";
  });
})();
export {
  AccordionDisclosure,
  AccountLogin,
  AnnouncementBarCarousel,
  ArticleToolbar,
  BeforeAfter,
  BlogPosts,
  BuyButtons,
  CarouselNavigation,
  CarouselNextButton,
  CarouselPrevButton,
  CartCount,
  CartDiscountBanner,
  CartDiscountField,
  CartDiscountRemoveButton,
  CartDot,
  CartDrawer,
  CartNote,
  CollectionBanner,
  CollectionLayoutSwitch,
  ConfirmButton,
  CopyButton,
  CountdownTimer,
  CountdownTimerFlip,
  CountdownTimerFlipDigit,
  CountrySelector,
  CustomDetails,
  DialogCloseButton,
  DialogElement,
  Drawer,
  DynamicGrid,
  EffectCarousel,
  FacetLink,
  FacetsDrawer,
  FacetsForm,
  FacetsSortPopover,
  FaqToc,
  FeaturedCollectionsCarousel,
  FreeShippingBar,
  GestureArea,
  GiftCardRecipient,
  Header,
  HeightObserver,
  ImageParallax,
  ImageWithText,
  ImageWithTextOverlay,
  ImagesWithTextScroll,
  LineItemQuantity,
  Listbox,
  LoadingBar,
  MarqueeText,
  MediaGrid,
  MenuDisclosure,
  Modal,
  ModelMedia,
  MultiColumn,
  MultipleMediaWithText,
  NewsletterPopup,
  OpenLightBoxButton,
  Player,
  PopIn,
  Popover,
  PredictiveSearch,
  PriceRange,
  PrivacyBanner,
  ProductCard,
  ProductForm,
  ProductGallery,
  ProductGalleryNavigation,
  ProductList,
  ProductLoader,
  ProductRecommendations,
  ProductRerender,
  ProductStickyBar,
  ProgressBar,
  QrCode,
  QuantityInput,
  QuantitySelector,
  QuickBuyModal,
  QuickOrderList,
  RecentlyViewedProducts,
  SafeSticky,
  ScrollCarousel,
  ShareButton,
  ShippingEstimator,
  ShopTheLookDesktopCarousel,
  ShopTheLookMobileCarousel,
  ShopTheLookPopover,
  ShopTheLookProductListCarousel,
  SlideshowCarousel,
  Tabs,
  TestimonialCarousel,
  TextWithIconsCarousel,
  TimelineCarousel,
  VariantPicker,
  VideoMedia,
  announceError,
  announceStatus,
  cachedFetch,
  createMediaImg,
  debounce,
  deepQuerySelector,
  extractSectionId,
  fetchCart,
  generateSrcset,
  imageLoaded,
  matchesMediaQuery,
  mediaQueryListener,
  throttle,
  videoLoaded,
  waitForEvent
};

/* JW: defensive Clarity / WebView teardown guards (theme-owned listeners only). */
(function __jwThemeClarityGuard() {
  try {
    if (window.__jwThemeClarityGuardInstalled) return;
    window.__jwThemeClarityGuardInstalled = true;
    var origPostMessage = window.postMessage;
    if (typeof origPostMessage === 'function') {
      window.postMessage = function () {
        try { return origPostMessage.apply(this, arguments); }
        catch (e) { /* ignore postMessage after WebView/iframe teardown */ }
      };
    }
    if (typeof window.addEventListener === 'function') {
      window.addEventListener('message', function () {}, { passive: true });
    }
    var idb = window.indexedDB;
    if (idb && typeof idb.open === 'function' && !idb.__jwPatched) {
      var origOpen = idb.open.bind(idb);
      idb.open = function () {
        try {
          var req = origOpen.apply(idb, arguments);
          if (req && typeof req.addEventListener === 'function') {
            req.addEventListener('error', function () {}, true);
            req.addEventListener('success', function () {
              try {
                var db = req.result;
                if (!db) return;
                var origTx = db.transaction.bind(db);
                db.transaction = function (storeNames) {
                  try {
                    return origTx.apply(db, arguments);
                  } catch (err) {
                    var msg = String(err && err.message || err || '');
                    if (/object store|not found|InvalidState/i.test(msg)) {
                      return {
                        objectStore: function () {
                          return {
                            get: function () { return { onsuccess: null, onerror: null, result: undefined }; },
                            put: function () { return { onsuccess: null, onerror: null }; },
                            delete: function () { return { onsuccess: null, onerror: null }; }
                          };
                        },
                        abort: function () {},
                        commit: function () {},
                        oncomplete: null,
                        onerror: null,
                        onabort: null
                      };
                    }
                    throw err;
                  }
                };
              } catch (_) {}
            });
          }
          return req;
        } catch (e) {
          return { result: null, error: e, onsuccess: null, onerror: null, addEventListener: function () {} };
        }
      };
      idb.__jwPatched = true;
    }
  } catch (_) {}
})();
